// frontend/src/pages/SeoEnginePage.js
// Admin Dashboard for FabVogueStudio SEO Opportunity & Merchandising Engine
// Features:
// 1. Phase 1: Keyword Opportunity Board (GSC & Google Trends A-Z Sweeps)
// 2. Phase 2: Micro-Batch (3-5 Products) Review & 1-Click Sync
// 3. Phase 3: Live Closed-Loop Ranking & Performance Tracker (GSC Progression & 1-Click Rollback)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import './SeoEnginePage.css';

function getRecommendedPageLabel(query) {
  if (query.routingStatus === 'HELD') return 'Hold for clarity';
  if (query.ownerType === 'BLOG') return 'Update existing blog';
  if (query.ownerType === 'PRODUCT') return 'Optimize existing product';
  if (query.ownerType === 'COLLECTION') return 'Optimize existing collection';
  if (query.ownerActionable === false) return 'Resolve page ownership';
  if (query.intent === 'informational') return 'Blog guide';
  if (['category', 'commercial_category'].includes(query.intent)) return 'Collection';
  return 'Product';
}

function renderWeeklyGscTrend(history = []) {
  const measured = history.filter((point) => (
    point.postChangeWindow === true &&
    point.coverageComplete === true &&
    point.measurementStatus === 'MEASURED' &&
    Number(point.position) > 0
  ));

  if (measured.length < 2) {
    const label = history.length === 0
      ? 'First weekly snapshot pending'
      : measured.length === 1
        ? 'One post-change week recorded'
        : history.every((point) => point.postChangeWindow === false)
          ? 'Waiting for a full post-change week'
          : history.some((point) => point.measurementStatus === 'COVERAGE_INCOMPLETE')
            ? 'GSC window incomplete; rechecking next week'
            : 'No exact GSC row yet';
    return <span style={{ color: '#64748b', fontSize: 11 }}>{label}</span>;
  }

  const recent = measured.slice(-8);
  const values = recent.map((point) => Number(point.position));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const points = values.map((value, index) => {
    const x = recent.length === 1 ? 35 : 3 + (index * 64) / (recent.length - 1);
    const y = max === min ? 14 : 3 + ((value - min) / (max - min)) * 22;
    return `${x},${y}`;
  }).join(' ');
  const firstPosition = values[0];
  const lastPosition = values[values.length - 1];
  const gain = Number((firstPosition - lastPosition).toFixed(1));
  const color = gain >= 2 ? '#15803d' : gain <= -2 ? '#b91c1c' : '#475569';
  const latest = recent[recent.length - 1];

  return (
    <div title={`Weekly average position moved from ${firstPosition.toFixed(1)} to ${lastPosition.toFixed(1)} across ${recent.length} post-change weeks.`}>
      <svg width="72" height="30" viewBox="0 0 70 30" role="img" aria-label={`Weekly average position trend from ${firstPosition.toFixed(1)} to ${lastPosition.toFixed(1)}`}>
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div style={{ fontSize: 10, color, whiteSpace: 'nowrap' }}>
        {firstPosition.toFixed(1)} → {lastPosition.toFixed(1)} · {gain > 0 ? '+' : ''}{gain} pos
      </div>
      <div style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>
        {latest.impressions} impr · week of {latest.windowStartDate}
      </div>
    </div>
  );
}

export default function SeoEnginePage() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('action_center'); // 'action_center' | 'opportunities' | 'collections' | ...

  // Opportunity state
  const [queries, setQueries] = useState([]);
  const [queriesLastUpdated, setQueriesLastUpdated] = useState(null);
  const [trendFilter, setTrendFilter] = useState('ACTION_NEEDED'); // 'ACTION_NEEDED' | 'OPTIMIZED' | 'ALL'
  const [opportunities, setOpportunities] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [loadingOpps, setLoadingOpps] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [runningDailyScan, setRunningDailyScan] = useState(false);
  const [runningQueryText, setRunningQueryText] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [expandedTraceId, setExpandedTraceId] = useState(null);

  const actionNeededCount = useMemo(() => {
    return (queries || []).filter((q) => !q.isActionTaken && q.actionStatus !== 'OPTIMIZED' && q.status !== 'SYNCED').length;
  }, [queries]);

  const optimizedCount = useMemo(() => {
    return (queries || []).filter((q) => q.isActionTaken || q.actionStatus === 'OPTIMIZED' || q.status === 'SYNCED').length;
  }, [queries]);

  const displayedQueries = useMemo(() => {
    if (trendFilter === 'ALL') return queries;
    if (trendFilter === 'OPTIMIZED') {
      return (queries || []).filter((q) => q.isActionTaken || q.actionStatus === 'OPTIMIZED' || q.status === 'SYNCED');
    }
    // Default 'ACTION_NEEDED' (Untapped)
    return (queries || []).filter((q) => !q.isActionTaken && q.actionStatus !== 'OPTIMIZED' && q.status !== 'SYNCED');
  }, [queries, trendFilter]);

  // Performance Tracker & 90-Day Lifecycle state
  const [trackerData, setTrackerData] = useState(null);
  const [loadingTracker, setLoadingTracker] = useState(false);
  const [evaluatingLifecycle, setEvaluatingLifecycle] = useState(false);

  // Collection Optimizer state
  const [collectionsData, setCollectionsData] = useState(null);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [runningCollectionId, setRunningCollectionId] = useState(null);
  const [expandedCollectionIds, setExpandedCollectionIds] = useState(new Set());
  const [rawHtmlViewMap, setRawHtmlViewMap] = useState({});
  const [rawProductHtmlMap, setRawProductHtmlMap] = useState({});

  // Smart Collection Creator Modal state
  const [showCreateSmartModal, setShowCreateSmartModal] = useState(false);
  const [smartQuery, setSmartQuery] = useState('');
  const [smartTitle, setSmartTitle] = useState('');
  const [smartFabric, setSmartFabric] = useState('');
  const [smartPattern, setSmartPattern] = useState('');
  const [smartColor, setSmartColor] = useState('');
  const [smartGarment, setSmartGarment] = useState('');
  const [smartPreviewDraft, setSmartPreviewDraft] = useState(null);
  const [loadingSmartPreview, setLoadingSmartPreview] = useState(false);
  const [creatingSmartCollection, setCreatingSmartCollection] = useState(false);
  const [smartError, setSmartError] = useState(null);
  const [smartSuccessMessage, setSmartSuccessMessage] = useState(null);
  const [showRawModalHtml, setShowRawModalHtml] = useState(false);

  // Blog Creator Modal state
  const [showCreateBlogModal, setShowCreateBlogModal] = useState(false);
  const [blogQuery, setBlogQuery] = useState('');
  const [blogUpdateHandle, setBlogUpdateHandle] = useState(null);
  const [blogGscMetrics, setBlogGscMetrics] = useState(null);
  const [blogTitle, setBlogTitle] = useState('');
  const [blogCategory, setBlogCategory] = useState('design_trends');
  const [blogProductCategories, setBlogProductCategories] = useState(['digital_printed', 'plain_dyed', 'fabric_set']);
  const [blogKeywords, setBlogKeywords] = useState('');
  const [blogAuthor, setBlogAuthor] = useState(null);
  const [blogAuthorsList, setBlogAuthorsList] = useState([]);
  const [blogCategoriesList, setBlogCategoriesList] = useState([]);
  const [blogPreviewDraft, setBlogPreviewDraft] = useState(null);
  const [loadingBlogPreview, setLoadingBlogPreview] = useState(false);
  const [publishingBlog, setPublishingBlog] = useState(false);
  const [blogError, setBlogError] = useState(null);
  const [blogSuccessMessage, setBlogSuccessMessage] = useState(null);
  const [blogActiveView, setBlogActiveView] = useState('preview'); // 'preview' | 'raw'

  // Day-1 New Arrivals state
  const [day1Candidates, setDay1Candidates] = useState([]);
  const [loadingDay1Candidates, setLoadingDay1Candidates] = useState(false);
  const [generatingDay1, setGeneratingDay1] = useState(false);

  // Autonomous Pipeline & Watchdog state
  const [autopilotStatus, setAutopilotStatus] = useState(null);
  const [autopilotStatusCheckedAt, setAutopilotStatusCheckedAt] = useState(null);
  const [autopilotStatusError, setAutopilotStatusError] = useState(null);
  const [runningAutopilot, setRunningAutopilot] = useState(false);

  // Monday Release Queue state
  const [queueSummary, setQueueSummary] = useState(null);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [executingRelease, setExecutingRelease] = useState(false);
  const [oppFilterStatus, setOppFilterStatus] = useState('ALL'); // 'ALL' | 'READY_FOR_REVIEW' | 'QUEUED_FOR_RELEASE'

  // Google Ads Intelligence Bridge state
  const [adRecommendations, setAdRecommendations] = useState([]);
  const [loadingAdRecs, setLoadingAdRecs] = useState(false);
  const [adCampaignTypeTab, setAdCampaignTypeTab] = useState('pmax'); // 'pmax' | 'search' | 'shopping' | 'demand_gen'
  const [modalCampaignTab, setModalCampaignTab] = useState('pmax');
  const [selectedAdRecForCopy, setSelectedAdRecForCopy] = useState(null);
  const [generatedAdCopyMap, setGeneratedAdCopyMap] = useState({});
  const [loadingAdCopy, setLoadingAdCopy] = useState(false);
  const [downloadingAdCsv, setDownloadingAdCsv] = useState(false);
  const [downloadingClusterEndUse, setDownloadingClusterEndUse] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  const [updatingLiveStatusEndUse, setUpdatingLiveStatusEndUse] = useState(null);
  const [updatingSearchLiveEndUse, setUpdatingSearchLiveEndUse] = useState(null);
  const [addingKeywordEndUse, setAddingKeywordEndUse] = useState(null);
  const [customSearchKeywordInputs, setCustomSearchKeywordInputs] = useState({});
  const [scenePhotosMap, setScenePhotosMap] = useState({}); // endUse -> { products, error }
  const [loadingScenePhotosEndUse, setLoadingScenePhotosEndUse] = useState(null);

  // Negative & Keyword Exclusion state
  const [exclusionsData, setExclusionsData] = useState(null);
  const [loadingExclusions, setLoadingExclusions] = useState(false);
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [selectedQueryForExclude, setSelectedQueryForExclude] = useState(null);
  const [pickedExclusionStem, setPickedExclusionStem] = useState('');
  const [customExclusionInput, setCustomExclusionInput] = useState('');
  const [excludeActionType, setExcludeActionType] = useState('PERMANENT'); // 'PERMANENT' | 'DISMISS_ONLY'
  const [submittingExclusion, setSubmittingExclusion] = useState(false);
  const [showManageExclusionsModal, setShowManageExclusionsModal] = useState(false);
  const [newManualExclusion, setNewManualExclusion] = useState('');

  // Competitor Keyword Gap Hunter state
  const [competitorsList, setCompetitorsList] = useState([]);
  const [competitorGapsData, setCompetitorGapsData] = useState(null);
  const [competitorGapsError, setCompetitorGapsError] = useState(null);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [loadingCompetitorGaps, setLoadingCompetitorGaps] = useState(false);
  const [executingConquestQuery, setExecutingConquestQuery] = useState(null);

  // Collection AI Re-generation modal state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [selectedCollectionForRegenerate, setSelectedCollectionForRegenerate] = useState(null);
  const [regenerateReasonChips, setRegenerateReasonChips] = useState([]);
  const [customRegenerateInstruction, setCustomRegenerateInstruction] = useState('');
  const [submittingRegenerate, setSubmittingRegenerate] = useState(false);

  // Homepage SEO Studio state
  const [homepageData, setHomepageData] = useState(null);
  const [loadingHomepage, setLoadingHomepage] = useState(false);
  const [generatingHomepage, setGeneratingHomepage] = useState(false);
  const [syncingHomepage, setSyncingHomepage] = useState(false);
  const [showHomepageRegenerateModal, setShowHomepageRegenerateModal] = useState(false);
  const [homepageReasonChips, setHomepageReasonChips] = useState([]);
  const [customHomepageInstruction, setCustomHomepageInstruction] = useState('');
  const [rawBrandStoryView, setRawBrandStoryView] = useState(false);
  const [showHomepageSchemaView, setShowHomepageSchemaView] = useState(false);
  const [copiedSchema, setCopiedSchema] = useState(false);
  const [showHomepageThemeSpec, setShowHomepageThemeSpec] = useState(false);

  // Authenticated fetch helper matching app standard
  const authedFetch = useCallback(async (url, options = {}) => {
    let token = '';
    if (currentUser && typeof currentUser.getIdToken === 'function') {
      token = await currentUser.getIdToken();
    }
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = base && url.startsWith('/') ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const jsonFetch = useCallback(async (url, options = {}) => {
    const res = await authedFetch(url, options);
    let data;
    try {
      data = await res.json();
    } catch (parseErr) {
      throw new Error(`Server returned status ${res.status}`);
    }
    if (!res.ok || !data?.success) {
      throw new Error(data?.error || data?.message || `Request failed with status ${res.status}`);
    }
    return data;
  }, [authedFetch]);

  // Video Creator: load real product + lookbook photos for the selected asset group (once per end-use)
  const scenePhotosEndUse = modalCampaignTab === 'video_creator' ? selectedAdRecForCopy?.endUse : null;
  useEffect(() => {
    if (!scenePhotosEndUse || scenePhotosMap[scenePhotosEndUse]) return;
    let cancelled = false;
    setLoadingScenePhotosEndUse(scenePhotosEndUse);
    jsonFetch(`/api/v1/seo-engine/ad-intelligence/scene-photos?endUse=${encodeURIComponent(scenePhotosEndUse)}`)
      .then((data) => {
        if (!cancelled) setScenePhotosMap((prev) => ({ ...prev, [scenePhotosEndUse]: { products: data.products || [] } }));
      })
      .catch((err) => {
        if (!cancelled) setScenePhotosMap((prev) => ({ ...prev, [scenePhotosEndUse]: { products: [], error: err.message } }));
      })
      .finally(() => {
        if (!cancelled) setLoadingScenePhotosEndUse(null);
      });
    return () => { cancelled = true; };
  }, [scenePhotosEndUse, scenePhotosMap, jsonFetch]);

  // Helper to format last updated timestamp
  const formatLastUpdatedTime = (isoString) => {
    if (!isoString) return 'Not scanned yet';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return 'Not scanned yet';
      const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      
      const diffMs = Date.now() - d.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      let relativeStr = '';
      if (diffMins < 1) relativeStr = 'just now';
      else if (diffMins < 60) relativeStr = `${diffMins}m ago`;
      else {
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) relativeStr = `${diffHours}h ago`;
        else {
          const diffDays = Math.floor(diffHours / 24);
          relativeStr = `${diffDays}d ago`;
        }
      }
      return `${dateStr}, ${timeStr} (${relativeStr})`;
    } catch {
      return 'Not scanned yet';
    }
  };

  // 1. Fetch Discovered Queries (Phase 1)
  const fetchQueries = useCallback(async () => {
    setLoadingQueries(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/queries?limit=50');
      setQueries(data.queries || []);
      setQueriesLastUpdated(data.lastUpdated || data.updatedAt || null);
    } catch (err) {
      console.error('Failed to load queries:', err);
    } finally {
      setLoadingQueries(false);
    }
  }, [jsonFetch]);

  // 2. Fetch Pending & Queued Product Opportunities (Phase 2)
  const fetchOpportunities = useCallback(async () => {
    setLoadingOpps(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/opportunities?status=READY_FOR_REVIEW_OR_QUEUED');
      setOpportunities(data.opportunities || []);
    } catch (err) {
      console.error('Failed to load opportunities:', err);
    } finally {
      setLoadingOpps(false);
    }
  }, [jsonFetch]);

  // 2b. Fetch Day-1 New Arrival Candidates
  const fetchDay1Candidates = useCallback(async () => {
    setLoadingDay1Candidates(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/day1/candidates?limit=30');
      setDay1Candidates(data.candidates || []);
    } catch (err) {
      console.warn('Failed to load Day-1 candidates:', err);
    } finally {
      setLoadingDay1Candidates(false);
    }
  }, [jsonFetch]);

  const handleGenerateDay1Batch = async (limit = 5) => {
    setGeneratingDay1(true);
    setStatusMessage(null);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/day1/generate', {
        method: 'POST',
        body: JSON.stringify({ limit }),
      });
      setStatusMessage(`✨ Generated Day-1 SEO drafts for ${data.count} new arrival fabrics! Ready for review below.`);
      fetchOpportunities();
      fetchDay1Candidates();
    } catch (err) {
      alert(`Error generating Day-1 SEO: ${err.message}`);
    } finally {
      setGeneratingDay1(false);
    }
  };

  // 3. Fetch Shopify Collections
  const fetchCollections = useCallback(async () => {
    setLoadingCollections(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/collections');
      setCollectionsData(data);
    } catch (err) {
      console.error('Failed to load collections:', err);
    } finally {
      setLoadingCollections(false);
    }
  }, [jsonFetch]);

  // 4. Fetch Live Performance & Ranking Data (Phase 3)
  const fetchTrackerData = useCallback(async () => {
    setLoadingTracker(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/performance-tracker');
      setTrackerData(data);
    } catch (err) {
      console.error('Failed to load performance tracker:', err);
    } finally {
      setLoadingTracker(false);
    }
  }, [jsonFetch]);

  const handleRunLifecycleEvaluation = async () => {
    setEvaluatingLifecycle(true);
    setStatusMessage(null);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/lifecycle/evaluate', {
        method: 'POST',
        body: JSON.stringify({ dryRun: false }),
      });
      setStatusMessage(`🛡️ 90-Day Lifecycle Evaluation Complete: ${data.winnersProtected} winners protected, ${data.strikingDistanceBoosted} striking-distance items, ${data.growthProbation} in probation, ${data.refreshRecommended || 0} refresh recommendations, ${data.inconclusive || 0} inconclusive.`);
      fetchTrackerData();
      fetchOpportunities();
    } catch (err) {
      alert(`Error running lifecycle evaluation: ${err.message}`);
    } finally {
      setEvaluatingLifecycle(false);
    }
  };

  // 5. Fetch Blog Authors & Categories Meta
  const fetchBlogMeta = useCallback(async () => {
    try {
      const data = await jsonFetch('/api/v1/seo-engine/blogs/meta');
      if (Array.isArray(data.authors)) {
        setBlogAuthorsList(data.authors);
        setBlogAuthor((prev) => prev || data.authors[0]);
      }
      if (Array.isArray(data.categories)) {
        setBlogCategoriesList(data.categories);
      }
    } catch (err) {
      console.warn('Failed to load blog meta:', err.message);
    }
  }, [jsonFetch]);

  // 6. Fetch Monday Release Queue Summary
  const fetchQueueSummary = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/queue/summary');
      setQueueSummary(data);
    } catch (err) {
      console.warn('Failed to load release queue summary:', err.message);
    } finally {
      setLoadingQueue(false);
    }
  }, [jsonFetch]);

  // 7. Fetch Google Ads Expansion Recommendations
  const fetchAdRecommendations = useCallback(async () => {
    setLoadingAdRecs(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/ad-intelligence/recommendations');
      const recs = data.recommendations || [];
      setAdRecommendations(recs);
      setSelectedAdRecForCopy((prev) => {
        if (!prev) return prev;
        const fresh = recs.find((r) => r.endUse.toLowerCase() === prev.endUse.toLowerCase());
        return fresh ? { ...prev, ...fresh } : prev;
      });
      const prefilled = {};
      recs.forEach((r) => {
        if (r.savedCopy && typeof r.savedCopy === 'object') {
          prefilled[r.endUse] = r.savedCopy;
        }
      });
      if (Object.keys(prefilled).length > 0) {
        setGeneratedAdCopyMap((prev) => ({ ...prefilled, ...prev }));
      }
    } catch (err) {
      console.warn('Failed to load ad recommendations:', err.message);
    } finally {
      setLoadingAdRecs(false);
    }
  }, [jsonFetch]);

  // 8. Fetch Exclusions and Keyword Rules
  const fetchExclusions = useCallback(async () => {
    setLoadingExclusions(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/settings/keyword-rules');
      setExclusionsData(data);
    } catch (err) {
      console.warn('Failed to load keyword exclusions:', err.message);
    } finally {
      setLoadingExclusions(false);
    }
  }, [jsonFetch]);

  // 9. Fetch Homepage SEO Audit & AI Proposal
  const fetchHomepageData = useCallback(async () => {
    setLoadingHomepage(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/homepage');
      setHomepageData(data);
    } catch (err) {
      console.warn('Failed to load homepage SEO data:', err.message);
    } finally {
      setLoadingHomepage(false);
    }
  }, [jsonFetch]);

  // 10. Fetch Autopilot & Watchdog Status
  const fetchAutopilotStatus = useCallback(async () => {
    try {
      const data = await jsonFetch('/api/v1/seo-engine/autopilot/status');
      setAutopilotStatus(data);
      setAutopilotStatusCheckedAt(Date.now());
      setAutopilotStatusError(null);
    } catch (err) {
      setAutopilotStatusError(String(err?.message || 'Status refresh failed.').slice(0, 160));
      console.warn('Failed to load autopilot status:', err.message);
    }
  }, [jsonFetch]);

  // 11. Fetch Competitors List & Keyword Gaps
  const fetchCompetitors = useCallback(async () => {
    setLoadingCompetitors(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/competitor-intelligence/competitors');
      setCompetitorsList(data.competitors || []);
    } catch (err) {
      console.warn('Failed to load competitors list:', err.message);
    } finally {
      setLoadingCompetitors(false);
    }
  }, [jsonFetch]);

  const fetchCompetitorGaps = useCallback(async () => {
    setLoadingCompetitorGaps(true);
    setCompetitorGapsData(null);
    setCompetitorGapsError(null);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/competitor-intelligence/gaps?limit=50');
      setCompetitorGapsData(data);
    } catch (err) {
      setCompetitorGapsError(err.message || 'Competitor scan failed.');
      console.warn('Failed to load competitor keyword gaps:', err.message);
    } finally {
      setLoadingCompetitorGaps(false);
    }
  }, [jsonFetch]);

  const handleExecuteConquest = async (gap, action = 'OPTIMIZE_PRODUCTS') => {
    setExecutingConquestQuery(gap.id);
    setStatusMessage(null);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/competitor-intelligence/conquest', {
        method: 'POST',
        body: JSON.stringify({ query: gap.query, action }),
      });
      setStatusMessage(`⚔️ Conquest Active: ${data.message}`);
      await Promise.all([
        fetchCompetitorGaps(),
        fetchOpportunities(),
        fetchCollections(),
      ]);
    } catch (err) {
      setCompetitorGapsError(`Could not create draft for "${gap.query}": ${err.message}`);
    } finally {
      setExecutingConquestQuery(null);
    }
  };

  const handleRunAutopilotNow = async () => {
    if (!window.confirm('Run the full daily autonomous pipeline now? This will process 30-day new arrivals, top search trends, collections, 90-day lifecycle, and stage everything for Monday release.')) return;
    setRunningAutopilot(true);
    setStatusMessage(null);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/autopilot/run-now', { method: 'POST' });
      const sum = data.summary || {};
      const lifecycle = sum.lifecycleAudit || {};
      setStatusMessage(`🚀 Autonomous Pipeline Complete! Staged ${sum.day1Products?.queued || 0} Day-1 fabrics, ${sum.catalogTrendProducts?.queued || 0} Trend fabrics, ${sum.existingCollections?.queued || 0} Collections; lifecycle refreshes: ${lifecycle.refreshQueued || 0} queued, ${lifecycle.refreshRetrySucceeded || 0} retries recovered, ${lifecycle.refreshRetryScheduled || 0} retries scheduled, ${lifecycle.refreshLeaseContended || 0} concurrent skips, ${lifecycle.refreshFailed || 0} failed (${lifecycle.winnersProtected || 0} winners protected).`);
      fetchQueueSummary();
      fetchOpportunities();
      fetchTrackerData();
      fetchAutopilotStatus();
    } catch (err) {
      alert(`Error running autopilot: ${err.message}`);
    } finally {
      setRunningAutopilot(false);
    }
  };

  useEffect(() => {
    fetchQueries();
    fetchOpportunities();
    fetchDay1Candidates();
    fetchCollections();
    fetchTrackerData();
    fetchBlogMeta();
    fetchQueueSummary();
    fetchAdRecommendations();
    fetchExclusions();
    fetchHomepageData();
    fetchAutopilotStatus();
    fetchCompetitors();
  }, [fetchQueries, fetchOpportunities, fetchDay1Candidates, fetchCollections, fetchTrackerData, fetchBlogMeta, fetchQueueSummary, fetchAdRecommendations, fetchExclusions, fetchHomepageData, fetchAutopilotStatus, fetchCompetitors]);

  useEffect(() => {
    let requestInFlight = false;
    const refreshVisibleWatchdog = async () => {
      if (document.visibilityState !== 'visible' || requestInFlight) return;
      requestInFlight = true;
      try {
        await fetchAutopilotStatus();
      } finally {
        requestInFlight = false;
      }
    };
    const intervalId = window.setInterval(refreshVisibleWatchdog, 60 * 1000);
    document.addEventListener('visibilitychange', refreshVisibleWatchdog);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshVisibleWatchdog);
    };
  }, [fetchAutopilotStatus]);

  // Exclusion Action Handlers
  const handleOpenExcludeModal = (q) => {
    setSelectedQueryForExclude(q);
    const words = String(q?.query || '')
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2 && !['for', 'the', 'and', 'with', 'fabric', 'fabrics', 'online', 'buy', 'price'].includes(w));
    
    // Pick the most distinct word/stem by default
    setPickedExclusionStem(words[words.length - 1] || words[0] || q.query);
    setCustomExclusionInput('');
    setExcludeActionType('PERMANENT');
    setShowExcludeModal(true);
  };

  const handleConfirmExclude = async () => {
    if (!selectedQueryForExclude) return;
    setSubmittingExclusion(true);
    try {
      const targetWord = (customExclusionInput || pickedExclusionStem || '').trim().toLowerCase();
      if (excludeActionType === 'PERMANENT') {
        if (!targetWord) {
          alert('Please select or enter a keyword/stem to exclude.');
          setSubmittingExclusion(false);
          return;
        }
        await jsonFetch('/api/v1/seo-engine/settings/exclude-keyword', {
          method: 'POST',
          body: JSON.stringify({
            keyword: targetWord,
            queryToDismiss: selectedQueryForExclude.query,
          }),
        });
      } else {
        await jsonFetch('/api/v1/seo-engine/queries/dismiss', {
          method: 'POST',
          body: JSON.stringify({
            query: selectedQueryForExclude.query,
          }),
        });
      }
      setShowExcludeModal(false);
      setSelectedQueryForExclude(null);
      await Promise.all([fetchQueries(), fetchExclusions()]);
    } catch (err) {
      alert(`Exclusion failed: ${err.message}`);
    } finally {
      setSubmittingExclusion(false);
    }
  };

  const handleRemoveExclusion = async (kw) => {
    try {
      await jsonFetch('/api/v1/seo-engine/settings/remove-exclusion', {
        method: 'POST',
        body: JSON.stringify({ keyword: kw }),
      });
      await Promise.all([fetchQueries(), fetchExclusions()]);
    } catch (err) {
      alert(`Failed to remove exclusion: ${err.message}`);
    }
  };

  const handleAddManualExclusion = async () => {
    const kw = String(newManualExclusion || '').trim().toLowerCase();
    if (!kw) return;
    try {
      await jsonFetch('/api/v1/seo-engine/settings/exclude-keyword', {
        method: 'POST',
        body: JSON.stringify({ keyword: kw }),
      });
      setNewManualExclusion('');
      await Promise.all([fetchQueries(), fetchExclusions()]);
    } catch (err) {
      alert(`Failed to add exclusion: ${err.message}`);
    }
  };

  // Generate or view AI Ad Copy for a recommendation
  const handleGenerateAdCopy = async (rec, forceRefresh = false) => {
    setSelectedAdRecForCopy(rec);
    if (!forceRefresh && generatedAdCopyMap[rec.endUse]) return; // Already cached in state or Firestore
    setLoadingAdCopy(true);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/ad-intelligence/generate-copy', {
        method: 'POST',
        body: JSON.stringify({
          endUse: rec.endUse,
          sampleQueries: rec.sampleQueries,
          sampleTitles: rec.sampleTitles,
          forceRefresh,
        }),
      });
      if (data.copy) {
        setGeneratedAdCopyMap((prev) => ({ ...prev, [rec.endUse]: data.copy }));
      }
    } catch (err) {
      alert(`Failed to generate ad copy: ${err.message}`);
    } finally {
      setLoadingAdCopy(false);
    }
  };

  // Download authenticated Google Ads Expansion CSV (full or deltaOnly update)
  const handleDownloadAdCsv = async (endUse = null, deltaOnly = false) => {
    if (endUse) {
      setDownloadingClusterEndUse(endUse);
    } else {
      setDownloadingAdCsv(true);
    }
    try {
      const params = [];
      if (endUse) params.push(`endUse=${encodeURIComponent(endUse)}`);
      if (deltaOnly) params.push('deltaOnly=true');
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const url = `/api/v1/seo-engine/ad-intelligence/download-csv${queryString}`;

      const res = await authedFetch(url);
      if (!res.ok) {
        throw new Error(`Failed to generate CSV: status ${res.status}`);
      }
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const prefix = deltaOnly ? 'google_ads_search_update_' : 'google_ads_search_';
      a.download = `${prefix}${endUse ? endUse.toLowerCase().replace(/[^a-z0-9]+/g, '_') : 'all_expansion'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      await fetchAdRecommendations();
    } catch (err) {
      alert(`Error downloading Google Ads CSV: ${err.message}`);
    } finally {
      setDownloadingAdCsv(false);
      setDownloadingClusterEndUse(null);
    }
  };

  // Copy text helper with feedback
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(label || text);
    setTimeout(() => setCopiedText(null), 2500);
  };

  // Toggle Live status in Google Ads for an Asset Group
  const handleToggleAssetGroupLive = async (endUse, isLive, copyToSave = null) => {
    setUpdatingLiveStatusEndUse(endUse);
    try {
      const res = await jsonFetch('/api/v1/seo-engine/ad-intelligence/mark-live', {
        method: 'POST',
        body: JSON.stringify({
          endUse,
          isLive,
          campaignId: '24320551082',
          currentCopy: copyToSave || generatedAdCopyMap[endUse] || null,
        }),
      });
      if (res.success) {
        await fetchAdRecommendations();
      }
    } catch (err) {
      alert(`Failed to update live status: ${err.message}`);
    } finally {
      setUpdatingLiveStatusEndUse(null);
    }
  };

  // Toggle Live status in Google Ads for a Search Campaign / Ad Group
  const handleToggleSearchAdLive = async (endUse, isLive) => {
    setUpdatingSearchLiveEndUse(endUse);
    try {
      const res = await jsonFetch('/api/v1/seo-engine/ad-intelligence/search/mark-live', {
        method: 'POST',
        body: JSON.stringify({
          endUse,
          isLive,
          campaignId: '24320551082',
        }),
      });
      if (res.success) {
        await fetchAdRecommendations();
      }
    } catch (err) {
      alert(`Failed to update search ad live status: ${err.message}`);
    } finally {
      setUpdatingSearchLiveEndUse(null);
    }
  };

  // Add search keyword to Search Ad Group
  const handleAddSearchKeyword = async (endUse, keyword) => {
    if (!keyword || !keyword.trim()) return;
    const cleanKw = keyword.trim();
    setAddingKeywordEndUse(`${endUse}::${cleanKw}`);
    try {
      const res = await jsonFetch('/api/v1/seo-engine/ad-intelligence/search/add-keyword', {
        method: 'POST',
        body: JSON.stringify({
          endUse,
          keyword: cleanKw,
        }),
      });
      if (res.success) {
        await fetchAdRecommendations();
        setCustomSearchKeywordInputs((prev) => ({ ...prev, [endUse]: '' }));
      }
    } catch (err) {
      alert(`Failed to add keyword: ${err.message}`);
    } finally {
      setAddingKeywordEndUse(null);
    }
  };

  // Remove search keyword from Search Ad Group
  const handleRemoveSearchKeyword = async (endUse, keyword) => {
    if (!keyword) return;
    try {
      const res = await jsonFetch('/api/v1/seo-engine/ad-intelligence/search/remove-keyword', {
        method: 'POST',
        body: JSON.stringify({
          endUse,
          keyword,
        }),
      });
      if (res.success) {
        await fetchAdRecommendations();
      }
    } catch (err) {
      alert(`Failed to remove keyword: ${err.message}`);
    }
  };

  // Mark Google Search Ad Group as updated in Google Ads (syncs exported keywords snapshot)
  const handleMarkSearchAdUpdated = async (endUse) => {
    setUpdatingSearchLiveEndUse(endUse);
    try {
      const res = await jsonFetch('/api/v1/seo-engine/ad-intelligence/search/mark-updated', {
        method: 'POST',
        body: JSON.stringify({ endUse }),
      });
      if (res.success) {
        await fetchAdRecommendations();
        setStatusMessage(`Marked [Search] ${endUse} as updated with latest keywords in Google Ads.`);
      }
    } catch (err) {
      alert(`Failed to mark updated: ${err.message}`);
    } finally {
      setUpdatingSearchLiveEndUse(null);
    }
  };

  // Trigger discovery scan for top queries
  const handleDiscoverQueries = async () => {
    setDiscovering(true);
    setStatusMessage('Scanning Google Search Console, Google Trends RSS & A-Z Harvest...');
    try {
      const data = await jsonFetch('/api/v1/seo-engine/discover-queries', {
        method: 'POST',
        body: JSON.stringify({ limit: 20 }),
      });
      setQueries(data.queries || []);
      setQueriesLastUpdated(data.lastUpdated || data.updatedAt || new Date().toISOString());
      setStatusMessage(`Found ${data.totalDiscovered || (data.queries || []).length} opportunity queries from Google Trends & Search Console.`);
    } catch (err) {
      setStatusMessage(`Discovery issue: ${err.message}`);
    } finally {
      setDiscovering(false);
    }
  };

  // Generate 3-5 products for a specific selected query
  const handleGenerateForQuery = async (queryItem) => {
    setRunningQueryText(queryItem.query);
    setStatusMessage(`Matching Made-to-Order fabrics and generating AI SEO for "${queryItem.query}"...`);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/generate-for-query', {
        method: 'POST',
        body: JSON.stringify({
          queryText: queryItem.query,
          queryDetails: queryItem,
        }),
      });
      setStatusMessage(data.entityType === 'COLLECTION'
        ? `Generated an SEO proposal for the existing collection that owns "${queryItem.query}".`
        : `Generated ${data.candidatesGenerated} candidate products for "${queryItem.query}". Products locked and ready for review.`);
      await Promise.all([fetchOpportunities(), fetchQueries(), ...(data.entityType === 'COLLECTION' ? [fetchCollections()] : [])]);
      
      const reviewSection = document.getElementById('review-opportunities-section');
      if (reviewSection) {
        reviewSection.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (err) {
      alert(`Generation failed: ${err.message}`);
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setRunningQueryText(null);
    }
  };

  // Auto-run next un-run opportunity query
  const handleAutoRunTop = async () => {
    const nextQuery = queries.find((q) => q.status === 'READY_TO_RUN') || queries[0];
    if (!nextQuery) {
      alert('No un-run queries found. Please refresh queries first.');
      return;
    }
    await handleGenerateForQuery(nextQuery);
  };

  // Trigger full daily background routine (simulates the 6:30 AM IST cron job)
  const handleTriggerDailyScan = async () => {
    if (!window.confirm('Run the full daily background scan now? This sweeps all trends, identifies top opportunities, and automatically prepares candidate drafts for your review.')) {
      return;
    }
    setRunningDailyScan(true);
    setStatusMessage('Running daily background scan: Sweeping trends & generating candidate drafts...');
    try {
      const data = await jsonFetch('/api/v1/seo-engine/trigger-daily-scan', {
        method: 'POST',
      });
      setStatusMessage(data.message || 'Daily background scan completed successfully.');
      setQueriesLastUpdated(new Date().toISOString());
      await Promise.all([fetchOpportunities(), fetchQueries()]);
    } catch (err) {
      alert(`Daily scan failed: ${err.message}`);
      setStatusMessage(`Daily scan error: ${err.message}`);
    } finally {
      setRunningDailyScan(false);
    }
  };

  // Approve single product opportunity (Immediate Direct Sync)
  const handleApprove = async (id) => {
    try {
      await jsonFetch(`/api/v1/seo-engine/opportunities/${id}/approve`, {
        method: 'POST',
      });
      setStatusMessage('Successfully synced to Shopify with rollback snapshot. Product locked for 90 days.');
      fetchOpportunities();
      fetchQueries();
      fetchTrackerData();
      fetchQueueSummary();
    } catch (err) {
      alert(`Approve failed: ${err.message}`);
    }
  };

  // Stage single opportunity in Monday 01:00 AM IST Release Queue
  const handleApproveToQueue = async (id, entityType = 'product') => {
    try {
      const data = await jsonFetch(`/api/v1/seo-engine/queue/approve-to-queue/${id}`, {
        method: 'POST',
        body: JSON.stringify({ entityType }),
      });
      setStatusMessage(data.message || 'Queued for Monday 01:00 AM IST release window.');
      await Promise.all([fetchOpportunities(), fetchCollections(), fetchQueueSummary(), fetchQueries()]);
    } catch (err) {
      alert(`Queue failed: ${err.message}`);
    }
  };

  // Un-queue single opportunity back to draft review
  const handleUnqueue = async (id, entityType = 'product') => {
    try {
      const data = await jsonFetch(`/api/v1/seo-engine/queue/unqueue/${id}`, {
        method: 'POST',
        body: JSON.stringify({ entityType }),
      });
      setStatusMessage(data.message || 'Removed from release queue and returned to draft review.');
      await Promise.all([fetchOpportunities(), fetchCollections(), fetchQueueSummary(), fetchQueries()]);
    } catch (err) {
      alert(`Unqueue failed: ${err.message}`);
    }
  };

  // Reject single opportunity
  const handleReject = async (id) => {
    try {
      await jsonFetch(`/api/v1/seo-engine/opportunities/${id}/reject`, {
        method: 'POST',
      });
      setStatusMessage('Opportunity dismissed. Product is now unlocked and available for other queries.');
      fetchOpportunities();
      fetchQueries();
      fetchQueueSummary();
    } catch (err) {
      alert(`Dismiss failed: ${err.message}`);
    }
  };

  // Batch queue all pending products for Monday
  const handleQueueAllPendingProducts = async () => {
    const unqueuedOpps = opportunities.filter((o) => o.status === 'READY_FOR_REVIEW');
    if (unqueuedOpps.length === 0) return;
    if (!window.confirm(`Stage all ${unqueuedOpps.length} pending products in Monday 01:00 AM IST Release Queue?`)) return;

    setStatusMessage(`Staging ${unqueuedOpps.length} products in Monday Release Queue...`);
    for (const opp of unqueuedOpps) {
      await jsonFetch(`/api/v1/seo-engine/queue/approve-to-queue/${opp.id}`, {
        method: 'POST',
        body: JSON.stringify({ entityType: 'product' }),
      });
    }
    setStatusMessage(`Successfully staged ${unqueuedOpps.length} products for Monday release window!`);
    await Promise.all([fetchOpportunities(), fetchQueueSummary(), fetchQueries()]);
  };

  // Batch approve and immediately sync all pending products
  const handleApproveAll = async () => {
    const unqueuedOpps = opportunities.filter((o) => o.status === 'READY_FOR_REVIEW');
    if (unqueuedOpps.length === 0) return;
    if (!window.confirm(`Are you sure you want to approve and immediately sync all ${unqueuedOpps.length} pending products to Shopify?`)) return;

    setStatusMessage(`Syncing ${unqueuedOpps.length} products to Shopify...`);
    for (const opp of unqueuedOpps) {
      await handleApprove(opp.id);
    }
    setStatusMessage(`Batch sync complete! ${unqueuedOpps.length} products updated in Shopify with 90-day cooldown protection.`);
    await Promise.all([fetchOpportunities(), fetchQueueSummary(), fetchTrackerData()]);
  };

  // Force trigger batch release now (emergency/admin override)
  const handleExecuteReleaseNow = async () => {
    const total = queueSummary?.totalQueued || 0;
    if (total === 0) {
      alert('The release queue is currently empty. Queue some products or collections first.');
      return;
    }
    if (!window.confirm(`Are you sure you want to execute manual release now for ${total} queued items?\n\nThis will immediately push all queued products and collections to Shopify.`)) {
      return;
    }
    setExecutingRelease(true);
    setStatusMessage(`Executing immediate batch release for ${total} items...`);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/queue/execute-release', {
        method: 'POST',
      });
      setStatusMessage(data.message || 'Batch release executed successfully!');
      await Promise.all([fetchOpportunities(), fetchCollections(), fetchTrackerData(), fetchQueueSummary(), fetchQueries()]);
    } catch (err) {
      alert(`Release execution failed: ${err.message}`);
      setStatusMessage(`Release error: ${err.message}`);
    } finally {
      setExecutingRelease(false);
    }
  };

  // 1-Click Rollback
  const handleRollback = async (backupId) => {
    if (!window.confirm('Are you sure you want to rollback this product to its previous SEO metadata?')) return;
    try {
      await jsonFetch(`/api/v1/seo-engine/rollback/${backupId}`, { method: 'POST' });
      setStatusMessage('Product SEO rolled back successfully to previous snapshot.');
      fetchTrackerData();
    } catch (err) {
      alert(`Rollback failed: ${err.message}`);
    }
  };

  // Collection handlers
  const toggleCollectionAccordion = (colNumericId) => {
    setExpandedCollectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(colNumericId)) {
        next.delete(colNumericId);
      } else {
        next.add(colNumericId);
      }
      return next;
    });
  };

  const REGENERATE_PRESET_TAGS = [
    'Focus on B2B / Wholesale & Boutiques',
    'Highlight Fabric GSM, Softness & Weave Texture',
    'Mention Kurtis, Suits & Lehenga Use Cases',
    'Focus on Western Dresses, Tops & Gowns',
    'Keep Meta Description strictly under 150 characters',
    'Shorten Meta Title under 55 characters',
    'Add Pricing, MOQ & Swatch Sample FAQs',
    'Add Detailed Washing & Fabric Care Guide',
    'Exclude ready-to-wear / stitched dress terminology',
    'Emphasize Surat direct manufacturer pricing',
  ];

  const handleOpenRegenerateModal = (col) => {
    setSelectedCollectionForRegenerate(col);
    setRegenerateReasonChips([]);
    // Pre-fill the previous comment/directive so the user can easily see and refine it
    const prevReason = col?.opportunity?.lastRegenerateReason || col?.opportunity?.proposedSeo?.userInstruction || customRegenerateInstruction || '';
    setCustomRegenerateInstruction(prevReason);
    setShowRegenerateModal(true);
  };

  const handleToggleRegenerateChip = (tag) => {
    setRegenerateReasonChips((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleConfirmRegenerate = async () => {
    if (!selectedCollectionForRegenerate) return;
    const col = selectedCollectionForRegenerate;

    const instructions = [];
    if (regenerateReasonChips.length > 0) {
      instructions.push(regenerateReasonChips.join('; '));
    }
    if (customRegenerateInstruction.trim()) {
      instructions.push(customRegenerateInstruction.trim());
    }
    const mergedInstruction = instructions.join('. ');

    setSubmittingRegenerate(true);
    setRunningCollectionId(col.numericId);
    setStatusMessage(`Re-generating AI SEO for "${col.title}" with custom merchandiser feedback...`);
    try {
      await jsonFetch(`/api/v1/seo-engine/collections/${col.numericId}/generate`, {
        method: 'POST',
        body: JSON.stringify({
          userInstruction: mergedInstruction,
          reason: mergedInstruction,
        }),
      });
      setStatusMessage(`AI SEO proposal re-generated for "${col.title}" with your feedback.`);
      setShowRegenerateModal(false);
      setSelectedCollectionForRegenerate(null);
      setExpandedCollectionIds((prev) => new Set([...prev, col.numericId]));
      await fetchCollections();
    } catch (err) {
      alert(`Collection SEO re-generation failed: ${err.message}`);
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setSubmittingRegenerate(false);
      setRunningCollectionId(null);
    }
  };

  const handleGenerateCollection = async (col) => {
    setRunningCollectionId(col.numericId);
    setStatusMessage(`Generating AI SEO Title, Rich Category Intro & FAQs for "${col.title}"...`);
    try {
      await jsonFetch(`/api/v1/seo-engine/collections/${col.numericId}/generate`, {
        method: 'POST',
      });
      setStatusMessage(`AI SEO proposal generated for "${col.title}". Ready for review below.`);
      setExpandedCollectionIds((prev) => new Set([...prev, col.numericId]));
      await fetchCollections();
    } catch (err) {
      alert(`Collection SEO generation failed: ${err.message}`);
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setRunningCollectionId(null);
    }
  };


  const handleApproveCollection = async (oppId, colTitle) => {
    try {
      await jsonFetch(`/api/v1/seo-engine/collections/${oppId}/approve`, {
        method: 'POST',
      });
      setStatusMessage(`Successfully synced "${colTitle}" to Shopify with rollback backup snapshot.`);
      await Promise.all([fetchCollections(), fetchTrackerData(), fetchQueueSummary()]);
    } catch (err) {
      alert(`Approve collection failed: ${err.message}`);
    }
  };

  const handleRejectCollection = async (oppId) => {
    try {
      await jsonFetch(`/api/v1/seo-engine/collections/${oppId}/reject`, {
        method: 'POST',
      });
      setStatusMessage('Collection opportunity dismissed.');
      await Promise.all([fetchCollections(), fetchQueueSummary()]);
    } catch (err) {
      alert(`Dismiss collection failed: ${err.message}`);
    }
  };

  // Homepage SEO Handlers
  const HOMEPAGE_PRESET_TAGS = [
    '🏭 Emphasize Surat Direct Manufacturer & Mill Pricing',
    '🌱 Highlight 100% Pure Natural Fibers & Dyeability',
    '🧵 Feature Custom Digital Printing on Demand',
    '📦 Add Boutique Swatch Sampling & Low MOQ FAQs',
    '✈️ Highlight Worldwide & Pan-India Express Shipping',
    '📏 Keep Meta Description strictly under 150 characters',
    '🌟 Emphasize Japanese Digital Print Colorfastness',
    '👗 Mention Boutique Designer & Apparel Exporter Focus',
  ];

  const handleOpenHomepageRegenerateModal = () => {
    setHomepageReasonChips([]);
    // Pre-fill previous homepage comment/directive so the user can easily see and refine it
    const prevReason = homepageData?.lastRegenerateReason || customHomepageInstruction || '';
    setCustomHomepageInstruction(prevReason);
    setShowHomepageRegenerateModal(true);
  };

  const handleToggleHomepageChip = (tag) => {
    setHomepageReasonChips((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleGenerateHomepage = async (isModal = false) => {
    let instruction = '';
    if (isModal) {
      const parts = [];
      if (homepageReasonChips.length > 0) parts.push(homepageReasonChips.join('; '));
      if (customHomepageInstruction.trim()) parts.push(customHomepageInstruction.trim());
      instruction = parts.join('. ');
    }

    setGeneratingHomepage(true);
    setStatusMessage('Generating AI Homepage SEO Title, Meta Description, Brand Story & Trust FAQs...');
    try {
      await jsonFetch('/api/v1/seo-engine/homepage/generate', {
        method: 'POST',
        body: JSON.stringify({ userInstruction: instruction, reason: instruction }),
      });
      setStatusMessage('AI Homepage SEO proposal generated successfully. Ready for review below.');
      setShowHomepageRegenerateModal(false);
      await fetchHomepageData();
    } catch (err) {
      alert(`Homepage SEO generation failed: ${err.message}`);
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setGeneratingHomepage(false);
    }
  };

  const handleApproveHomepage = async () => {
    if (!window.confirm('Sync this AI Homepage SEO proposal to Shopify?\n\nA backup snapshot of your current homepage metadata will be saved automatically for 1-click rollback.')) return;
    setSyncingHomepage(true);
    setStatusMessage('Syncing Homepage SEO, Brand Story HTML, and Trust FAQs to Shopify...');
    try {
      const data = await jsonFetch('/api/v1/seo-engine/homepage/approve', {
        method: 'POST',
      });
      setStatusMessage(data.message || 'Homepage SEO successfully synced to Shopify!');
      await Promise.all([fetchHomepageData(), fetchTrackerData()]);
    } catch (err) {
      alert(`Homepage sync failed: ${err.message}`);
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setSyncingHomepage(false);
    }
  };

  const handleDismissHomepage = async () => {
    if (!window.confirm('Dismiss current Homepage SEO proposal?')) return;
    try {
      await jsonFetch('/api/v1/seo-engine/homepage/dismiss', {
        method: 'POST',
      });
      setStatusMessage('Homepage proposal dismissed.');
      fetchHomepageData();
    } catch (err) {
      alert(`Dismiss failed: ${err.message}`);
    }
  };

// Client-side quick keyword extractor for collection attributes
const PARSER_FABRICS = [
  'organza', 'modal satin', 'viscose georgette', 'cotton cambric', 'pure muslin',
  'chiffon', 'chinon', 'crepe', 'upada silk', 'japan satin', 'velvet', 'rayon',
  'linen', 'chanderi', 'silk', 'kora silk', 'tusser silk', 'satin', 'georgette', 'cotton',
];

const PARSER_PATTERNS = [
  'floral', 'flower', 'botanical', 'geometric', 'chevron', 'stripe', 'check', 'plaid',
  'polka dot', 'polka', 'abstract', 'bandhani', 'kalamkari', 'ajrakh', 'ikat', 'patola',
  'paisley', 'buta', 'tie dye', 'shibori', 'leheriya', 'animal', 'solid', 'plain', 'embroidery',
];

const PARSER_COLORS = [
  'pink', 'blue', 'green', 'yellow', 'red', 'black', 'white', 'purple', 'lavender',
  'orange', 'peach', 'maroon', 'mustard', 'beige', 'grey', 'gray', 'ivory', 'navy',
  'mint', 'sage', 'teal', 'magenta', 'gold',
];

const PARSER_GARMENTS = [
  'lehenga', 'kurti', 'kurta', 'suit', 'saree', 'sari', 'dupatta', 'dress', 'gown',
  'anarkali', 'co-ord', 'coord', 'blouse', 'top', 'shirt', 'kaftan',
];

function formatProperCollectionTitle(queryText = '') {
  const noiseWords = ['online', 'for women', 'for men', 'for ladies', 'near me', 'buy', 'shop', 'best', 'wholesale', 'price', 'in india', 'india'];
  let clean = String(queryText || '').toLowerCase().trim();
  
  for (const nw of noiseWords) {
    clean = clean.replace(new RegExp(`\\b${nw}\\b`, 'gi'), ' ').trim();
  }
  clean = clean.replace(/\s+/g, ' ').trim();

  const words = clean.split(' ').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  let formatted = words.join(' ');

  if (!formatted.toLowerCase().includes('fabric')) {
    formatted = `${formatted} Fabrics`;
  } else if (formatted.endsWith(' Fabric')) {
    formatted = `${formatted}s`;
  }
  return formatted || 'Custom Fabric Collection';
}

function extractAttributesFromQuery(queryText = '') {
  const q = String(queryText || '').toLowerCase().trim();
  let fabric = '';
  let pattern = '';
  let color = '';
  let garment = '';

  for (const f of PARSER_FABRICS) {
    if (q.includes(f)) {
      fabric = f.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      break;
    }
  }

  for (const p of PARSER_PATTERNS) {
    if (q.includes(p)) {
      pattern = p === 'flower' || p === 'botanical' ? 'Floral' : (p.charAt(0).toUpperCase() + p.slice(1));
      break;
    }
  }

  for (const c of PARSER_COLORS) {
    if (q.includes(c)) {
      color = c.charAt(0).toUpperCase() + c.slice(1);
      break;
    }
  }

  for (const g of PARSER_GARMENTS) {
    if (q.includes(g)) {
      garment = g.charAt(0).toUpperCase() + g.slice(1);
      break;
    }
  }

  const title = formatProperCollectionTitle(queryText);
  return { fabric, pattern, color, garment, title };
}


  // Open Smart Collection creation modal directly for a discovered search query (Prefills without running search yet)
  const handleOpenSmartCollectionForQuery = (queryText) => {
    setShowCreateSmartModal(true);
    setSmartQuery(queryText);
    const parsed = extractAttributesFromQuery(queryText);
    setSmartTitle(parsed.title || queryText);
    setSmartFabric(parsed.fabric || '');
    setSmartPattern(parsed.pattern || '');
    setSmartColor(parsed.color || '');
    setSmartGarment(parsed.garment || '');
    setSmartError(null);
    setSmartSuccessMessage(null);
    setSmartPreviewDraft(null);
    setLoadingSmartPreview(false);
  };


  // Preview / Match products for new Smart Collection
  const handlePreviewSmartCollection = async (e) => {
    if (e) e.preventDefault();
    if (!smartQuery && !smartTitle) {
      setSmartError('Please enter a target search query or collection title.');
      return;
    }
    setLoadingSmartPreview(true);
    setSmartError(null);
    setSmartSuccessMessage(null);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/collections/preview-smart-collection', {
        method: 'POST',
        body: JSON.stringify({
          query: smartQuery,
          title: smartTitle || smartQuery,
          fabric: smartFabric,
          pattern: smartPattern,
          color: smartColor,
          garment: smartGarment,
        }),
      });
      setSmartPreviewDraft(data.draft);
      if (!smartTitle && data.draft?.collectionTitle) {
        setSmartTitle(data.draft.collectionTitle);
      }
      const attrs = data.draft?.productMatch?.matchedAttributes || {};
      if (attrs.fabric && !smartFabric) setSmartFabric(attrs.fabric);
      if (attrs.pattern && !smartPattern) setSmartPattern(attrs.pattern);
      if (attrs.color && !smartColor) setSmartColor(attrs.color);
      if (attrs.garment && !smartGarment) setSmartGarment(attrs.garment);
    } catch (err) {
      setSmartError(err.message || 'Failed to analyze products and preview collection draft.');
    } finally {
      setLoadingSmartPreview(false);
    }
  };


  // Publish / Create Smart Collection in Shopify
  const handleCreateSmartCollection = async () => {
    if (!smartPreviewDraft) return;
    setCreatingSmartCollection(true);
    try {
      const productIds = smartPreviewDraft.productMatch?.matchedProductIds?.length > 0
        ? smartPreviewDraft.productMatch.matchedProductIds
        : (smartPreviewDraft.productMatch?.sampleProducts || []).map((p) => p.id);
      const data = await jsonFetch('/api/v1/seo-engine/collections/create-smart-collection', {
        method: 'POST',
        body: JSON.stringify({
          title: smartPreviewDraft.collectionTitle || smartTitle || smartQuery,
          handle: smartPreviewDraft.candidateHandle,
          seoTitle: smartPreviewDraft.proposedSeo?.title,
          seoDescription: smartPreviewDraft.proposedSeo?.description,
          htmlDescription: smartPreviewDraft.proposedSeo?.htmlDescription,
          faqs: smartPreviewDraft.proposedSeo?.faqs || [],
          targetKeyword: smartQuery || smartTitle,
          collectionTag: smartPreviewDraft.proposedTag,
          productIds,
        }),
      });
      setSmartSuccessMessage(data.message || 'Smart collection created successfully on Shopify!');
      setStatusMessage(data.message || 'Smart collection created successfully!');
      fetchCollections();
      setTimeout(() => {
        setShowCreateSmartModal(false);
        setSmartPreviewDraft(null);
        setSmartSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setSmartError(err.message || 'Failed to create collection on Shopify.');
    } finally {
      setCreatingSmartCollection(false);
    }
  };

  // Open Blog Creator modal for a search query
  const handleOpenBlogForQuery = (queryText, updateHandle = null, gscMetrics = null) => {
    setShowCreateBlogModal(true);
    setBlogQuery(queryText);
    setBlogUpdateHandle(updateHandle);
    setBlogGscMetrics(gscMetrics);
    const capitalized = queryText.replace(/\b\w/g, (c) => c.toUpperCase());
    setBlogTitle(`${capitalized} Fabric Guide for Indian Fashion`);
    setBlogKeywords(queryText);
    if (/\b(co[- ]?ord|cord set|fabric set|matching set|two piece|duo)\b/i.test(queryText)) {
      setBlogProductCategories(['digital_printed', 'fabric_set']);
    } else if (queryText.toLowerCase().includes('plain')) {
      setBlogProductCategories(['plain_dyed']);
    } else if (queryText.toLowerCase().includes('print')) {
      setBlogProductCategories(['digital_printed']);
    } else {
      setBlogProductCategories(['digital_printed', 'plain_dyed']);
    }
    setBlogError(null);
    setBlogSuccessMessage(null);
    setBlogPreviewDraft(null);
    setLoadingBlogPreview(false);
    setBlogActiveView('preview');
  };

  const toggleBlogProductCategory = (catId) => {
    setBlogProductCategories((prev) => {
      if (prev.includes(catId)) {
        if (prev.length <= 1) return prev; // Keep at least one selected
        return prev.filter((c) => c !== catId);
      }
      return [...prev, catId];
    });
  };

  // Preview / Generate Blog Draft
  const handlePreviewBlog = async (e) => {
    if (e) e.preventDefault();
    if (!blogQuery && !blogTitle) {
      setBlogError('Please enter a target query or blog title.');
      return;
    }
    setLoadingBlogPreview(true);
    setBlogError(null);
    setBlogSuccessMessage(null);
    try {
      const kwArray = blogKeywords.split(',').map((k) => k.trim()).filter(Boolean);
      const data = await jsonFetch('/api/v1/seo-engine/blogs/preview-blog', {
        method: 'POST',
        body: JSON.stringify({
          query: blogQuery,
          title: blogTitle || blogQuery,
          blogCategory,
          productCategories: blogProductCategories,
          productCategory: blogProductCategories.join(','),
          keywords: kwArray.length > 0 ? kwArray : [blogQuery],
          author: blogAuthor,
        }),
      });
      setBlogPreviewDraft(data.draft);
      if (data.draft?.title) setBlogTitle(data.draft.title);
      if (data.draft?.blog_category) setBlogCategory(data.draft.blog_category);
    } catch (err) {
      setBlogError(err.message || 'Failed to generate blog draft.');
    } finally {
      setLoadingBlogPreview(false);
    }
  };

  // Publish Blog directly to Shopify
  const handlePublishBlog = async () => {
    if (!blogPreviewDraft) return;
    setPublishingBlog(true);
    setBlogError(null);
    try {
      const data = await jsonFetch('/api/v1/seo-engine/blogs/publish-blog', {
        method: 'POST',
        body: JSON.stringify({
          query: blogQuery,
          title: blogPreviewDraft.title || blogTitle,
          seo_title: blogPreviewDraft.seo_title,
          seo_description: blogPreviewDraft.seo_description,
          main_content: blogPreviewDraft.main_content,
          expert_summary: blogPreviewDraft.expert_summary,
          tags: blogPreviewDraft.tags,
          blog_category: blogPreviewDraft.blog_category,
          hero_image_url: blogPreviewDraft.hero_image_url,
          author: blogPreviewDraft.author || blogAuthor,
          updateExistingHandle: blogUpdateHandle,
          gscMetrics: blogGscMetrics,
        }),
      });
      setBlogSuccessMessage(data.message || 'Blog published successfully to Shopify!');
      setStatusMessage(data.message || 'Blog published successfully!');
      fetchQueries();
      setTimeout(() => {
        setShowCreateBlogModal(false);
        setBlogPreviewDraft(null);
        setBlogSuccessMessage(null);
      }, 3500);
    } catch (err) {
      setBlogError(err.message || 'Failed to publish blog to Shopify.');
    } finally {
      setPublishingBlog(false);
    }
  };

  const toggleTrace = (id) => {
    setExpandedTraceId(expandedTraceId === id ? null : id);
  };

  const pendingCollectionsCount = collectionsData?.collections?.filter((c) => c.status === 'READY_FOR_REVIEW').length || 0;

  // Centralized pending actions computation for the Action Center
  const pendingActions = useMemo(() => {
    // 1. Pending Product Drafts waiting for approval
    const pendingProducts = (opportunities || []).filter((o) => o.status === 'READY_FOR_REVIEW');

    // 2. Watchdog QA Issues / Warning flags on titles (> 70 chars) or failed QA
    const qaAlerts = (opportunities || []).filter((o) => 
      (o.status === 'READY_FOR_REVIEW' || o.status === 'QUEUED_FOR_RELEASE') && 
      (o.proposedSeo?.title?.length > 70 || o.qaAudit?.status === 'FAILED')
    );

    // 3. Collections awaiting approval/review
    const pendingCollections = (collectionsData?.collections || []).filter((c) =>
      c.status === 'READY_FOR_REVIEW' || c.status === 'PENDING_APPROVAL' || c.status === 'ACTION_NEEDED'
    );

    // 4. Google Ads Asset Groups needing action (Update recommended OR Novel draft with >=3 products)
    const adUpdates = (adRecommendations || []).filter((r) =>
      r.updateNeeded || (!r.isLiveInGoogleAds && (r.priority === 'HIGH_NEW_CATEGORY' || r.productCount >= 3))
    );

    // 5. High-Value Competitor Conquests ready to execute
    const competitorGaps = (competitorGapsData?.gaps || []).filter((g) =>
      !g.conquered && (g.opportunityScore >= 75 || g.conquestAction === 'READY')
    ).slice(0, 5);

    // 6. Homepage SEO proposal awaiting review
    const homepagePending = (homepageData?.status === 'READY_FOR_REVIEW' || homepageData?.proposedSeo?.title) ? [homepageData] : [];

    const totalCount = pendingProducts.length + pendingCollections.length + adUpdates.length + competitorGaps.length + (homepageData?.status === 'READY_FOR_REVIEW' ? 1 : 0);

    return {
      pendingProducts,
      qaAlerts,
      pendingCollections,
      adUpdates,
      competitorGaps,
      homepagePending,
      totalCount,
    };
  }, [opportunities, collectionsData, adRecommendations, competitorGapsData, homepageData]);

  return (
    <div className="seo-engine-container">
      {/* Header */}
      <div className="seo-engine-header">
        <div>
          <h1>SEO Opportunity &amp; Merchandising Engine</h1>
          <p>Closed-Loop Search Console &amp; Trends Intelligence for Products &amp; Category Hubs</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {(activeTab === 'action_center' || activeTab === 'opportunities') && opportunities.filter((o) => o.status === 'READY_FOR_REVIEW').length > 0 && (
            <button
              className="btn-queue-monday"
              style={{ padding: '10px 18px', fontSize: 14, fontWeight: 700 }}
              onClick={handleQueueAllPendingProducts}
              title="Stage all pending products for Monday 01:00 AM IST release window"
            >
              🕒 Approve &amp; Queue All for Monday ({opportunities.filter((o) => o.status === 'READY_FOR_REVIEW').length})
            </button>
          )}
          {activeTab === 'opportunities' && (
            <>
              <button
                className="btn-primary-seo"
                style={{ background: '#059669' }}
                onClick={handleTriggerDailyScan}
                disabled={discovering || runningDailyScan || Boolean(runningQueryText)}
                title="Runs the full automated daily scan (Cloud Scheduler cron runs at 06:30 AM IST)"
              >
                {runningDailyScan ? '⚡ Running Daily Scan...' : '⚡ Run Daily Scan Now'}
              </button>
              <button
                className="btn-primary-seo"
                style={{ background: '#7c3aed' }}
                onClick={handleAutoRunTop}
                disabled={discovering || runningDailyScan || Boolean(runningQueryText) || queries.length === 0}
              >
                ▶ Auto-Run Top Opportunity
              </button>
              <button
                className="btn-primary-seo"
                onClick={handleDiscoverQueries}
                disabled={discovering || runningDailyScan || Boolean(runningQueryText)}
              >
                {discovering ? 'Scanning Trends...' : '⟳ Scan & Refresh Trends'}
              </button>
            </>
          )}
          {activeTab === 'collections' && (
            <>
              <button
                className="btn-approve"
                style={{ background: '#16a34a', padding: '9px 16px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => {
                  setShowCreateSmartModal(true);
                  setSmartPreviewDraft(null);
                  setSmartError(null);
                  setSmartSuccessMessage(null);
                }}
              >
                ✨ + Create New Smart Collection
              </button>
              <button
                className="btn-primary-seo"
                onClick={fetchCollections}
                disabled={loadingCollections}
              >
                {loadingCollections ? 'Refreshing Collections...' : '⟳ Refresh Collections'}
              </button>
            </>
          )}
          {activeTab === 'homepage' && (
            <button
              className="btn-primary-seo"
              onClick={fetchHomepageData}
              disabled={loadingHomepage}
            >
              {loadingHomepage ? 'Auditing Homepage...' : '⟳ Refresh Live Audit'}
            </button>
          )}
          {activeTab === 'tracker' && (
            <button
              className="btn-primary-seo"
              onClick={fetchTrackerData}
              disabled={loadingTracker}
            >
              {loadingTracker ? 'Refreshing GSC...' : '⟳ Refresh GSC Rankings'}
            </button>
          )}
          {activeTab === 'competitors' && (
            <button
              className="btn-primary-seo"
              onClick={() => fetchCompetitorGaps()}
              disabled={loadingCompetitorGaps}
              style={{ background: '#dc2626' }}
            >
              {loadingCompetitorGaps ? 'Scanning Gaps...' : '⟳ Rescan Competitor Gaps'}
            </button>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="seo-nav-tabs">
        <button
          className={`seo-tab-btn ${activeTab === 'action_center' ? 'active' : ''}`}
          onClick={() => setActiveTab('action_center')}
          style={{ fontWeight: 700 }}
        >
          ⚡ Action Center {pendingActions.totalCount > 0 ? (
            <span className="tab-pill" style={{ background: '#dc2626', color: '#ffffff' }}>
              {pendingActions.totalCount} Pending
            </span>
          ) : (
            <span className="tab-pill green" style={{ background: '#16a34a' }}>
              ✓ All Done
            </span>
          )}
        </button>
        <button
          className={`seo-tab-btn ${activeTab === 'opportunities' ? 'active' : ''}`}
          onClick={() => setActiveTab('opportunities')}
        >
          🎯 Product Opportunities {opportunities.length > 0 && <span className="tab-pill">{opportunities.length}</span>}
        </button>
        <button
          className={`seo-tab-btn ${activeTab === 'collections' ? 'active' : ''}`}
          onClick={() => setActiveTab('collections')}
        >
          📁 Collection Optimizer {pendingCollectionsCount > 0 && <span className="tab-pill orange">{pendingCollectionsCount}</span>}
        </button>
        <button
          className={`seo-tab-btn ${activeTab === 'homepage' ? 'active' : ''}`}
          onClick={() => setActiveTab('homepage')}
        >
          🏠 Homepage SEO Studio {homepageData?.status === 'READY_FOR_REVIEW' && <span className="tab-pill orange">Review</span>}
        </button>
        <button
          className={`seo-tab-btn ${activeTab === 'tracker' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracker')}
        >
          📈 Live Ranking & Performance Tracker {trackerData?.totalOptimized > 0 && <span className="tab-pill green">{trackerData.totalOptimized}</span>}
        </button>
        <button
          className={`seo-tab-btn ${activeTab === 'competitors' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('competitors');
            if (!competitorGapsData) fetchCompetitorGaps();
          }}
        >
          ⚔️ Competitor Gap Hunter {competitorGapsData?.conquestReadyCount > 0 && (
            <span className="tab-pill" style={{ background: '#dc2626' }}>
              {competitorGapsData.conquestReadyCount} Ready
            </span>
          )}
        </button>
        <button
          className={`seo-tab-btn ${activeTab === 'google-ads' ? 'active' : ''}`}
          onClick={() => setActiveTab('google-ads')}
        >
          🚀 Google Ads Expansion Bridge {adRecommendations.filter((r) => r.isNewNovelGroup).length > 0 && (
            <span className="tab-pill" style={{ background: '#7c3aed' }}>
              {adRecommendations.filter((r) => r.isNewNovelGroup).length} New
            </span>
          )}
        </button>
      </div>

      {statusMessage && (
        <div style={{ background: '#f0fdf4', color: '#166534', padding: '12px 16px', borderRadius: 8, marginBottom: 20, border: '1px solid #bbf7d0', fontSize: 14 }}>
          {statusMessage}
        </div>
      )}

      {/* 4-PILLAR AUTONOMOUS AUTO-PILOT & WATCHDOG BANNER */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 16,
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 12px rgba(49, 46, 129, 0.15)',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            fontSize: 24,
            background: 'rgba(255, 255, 255, 0.15)',
            width: 44,
            height: 44,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            🤖
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
                Zero-Touch Autonomous SEO Pipeline & Sentinel Watchdog
              </h3>
              <span style={{
                background: '#10b981',
                color: '#ffffff',
                fontSize: 10,
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: 999,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Active (Cloud 24/7)
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: '#e0e7ff', lineHeight: 1.4 }}>
              <strong>4 Pillars Running:</strong> Daily 20–25 Products (30-day New Arrivals & Trends) • Weekly 5–7 Collections • 2–3 Pillar Blogs • 90-Day Winner Protection • Monday 01:00 AM IST Auto-Push.
              {(autopilotStatus?.watchdogHealth?.status || autopilotStatus?.latestRun?.watchdogAudit?.status) && (
                <span
                  title={((autopilotStatus.watchdogHealth || autopilotStatus.latestRun.watchdogAudit).issues || []).join('\n') || undefined}
                  style={{
                    marginLeft: 8,
                    color: (autopilotStatus.watchdogHealth?.status || autopilotStatus.latestRun.watchdogAudit.status) === 'HEALTHY' ? '#6ee7b7' : '#fde68a',
                    fontWeight: 600,
                    cursor: (autopilotStatus.watchdogHealth || autopilotStatus.latestRun.watchdogAudit).issues?.length ? 'help' : 'default',
                  }}
                >
                  • Watchdog Health: {autopilotStatus.watchdogHealth?.status || autopilotStatus.latestRun.watchdogAudit.status}
                </span>
              )}
              <span
                title={autopilotStatusError || (autopilotStatusCheckedAt ? new Date(autopilotStatusCheckedAt).toLocaleString() : undefined)}
                style={{ marginLeft: 8, color: autopilotStatusError ? '#fecaca' : '#cbd5e1', fontSize: 11, fontWeight: 500 }}
              >
                {autopilotStatusError
                  ? `Status may be stale${autopilotStatusCheckedAt ? ` · last checked ${new Date(autopilotStatusCheckedAt).toLocaleTimeString()}` : ''}`
                  : autopilotStatusCheckedAt
                    ? `Updated ${new Date(autopilotStatusCheckedAt).toLocaleTimeString()}`
                    : 'Checking status…'}
              </span>
            </div>
            {autopilotStatus?.latestRun?.lifecycleAudit && (() => {
              const lifecycle = autopilotStatus.latestRun.lifecycleAudit;
              const hasIssues = Number(lifecycle.refreshSkipped || 0) > 0 || Number(lifecycle.refreshFailed || 0) > 0 || Number(lifecycle.refreshRetryExhausted || 0) > 0;
              const reasonSummary = JSON.stringify({
                skipped: lifecycle.refreshSkippedByReason || {},
                failed: lifecycle.refreshFailedByStage || {},
                retryBlocked: lifecycle.refreshRetryBlocked || 0,
                retryExhausted: lifecycle.refreshRetryExhausted || 0,
              });
              return (
                <div
                  title={hasIssues ? reasonSummary : undefined}
                  style={{ marginTop: 4, fontSize: 11.5, color: hasIssues ? '#fde68a' : '#d1fae5', lineHeight: 1.35 }}
                >
                  Lifecycle refresh: {lifecycle.refreshRecommended || 0} recommended · {lifecycle.refreshQueued || 0} queued · {lifecycle.refreshRetrySucceeded || 0} recovered · {lifecycle.refreshRetryScheduled || 0} retry scheduled · {lifecycle.refreshLeaseContended || 0} concurrent skips · {lifecycle.refreshSkipped || 0} skipped · {lifecycle.refreshFailed || 0} failed
                  {hasIssues && <span> · Hover for reason counts</span>}
                </div>
              );
            })()}
            {autopilotStatus?.lifecycleRefreshHealth && (() => {
              const health = autopilotStatus.lifecycleRefreshHealth;
              const counts = health.counts || {};
              const attention = Number(health.attentionCount || 0) > 0;
              const issues = health.issues || [];
              return (
                <div style={{ marginTop: 7, color: attention ? '#fde68a' : '#d1fae5', fontSize: 11.5, lineHeight: 1.4 }}>
                  Refresh health: {counts.scheduled || 0} scheduled · {counts.running || 0} running · {counts.stuck || 0} stuck · {counts.blocked || 0} blocked · {counts.exhausted || 0} exhausted
                  {attention && issues.length > 0 && (
                    <details style={{ marginTop: 3 }}>
                      <summary style={{ cursor: 'pointer', width: 'fit-content' }}>
                        View {health.attentionCount} item{health.attentionCount === 1 ? '' : 's'} needing diagnosis
                        {health.truncated ? ' (first 20 shown)' : ''}
                      </summary>
                      <div style={{ marginTop: 5, display: 'grid', gap: 4 }}>
                        {issues.map((issue) => (
                          <div key={issue.opportunityId} style={{ color: '#fff', overflowWrap: 'anywhere' }}>
                            <strong>{issue.productTitle}</strong>{issue.productHandle ? ` (${issue.productHandle})` : ''} · {issue.state} · {issue.attempts} attempt{issue.attempts === 1 ? '' : 's'}
                            {issue.lastFailureAt ? ` · ${new Date(issue.lastFailureAt).toLocaleString()}` : ''}
                            <div style={{ color: '#e5e7eb' }}>{issue.lastFailureReason}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })()}
            {(() => {
              const retryRun = autopilotStatus?.latestLifecycleRetryRun;
              if (!retryRun) {
                return <div style={{ marginTop: 4, color: '#cbd5e1', fontSize: 11.5 }}>Hourly retry worker: waiting for its first scheduled run</div>;
              }
              const runFailed = retryRun.status === 'FAILED' || retryRun.status === 'ATTENTION_NEEDED';
              return (
                <div
                  title={retryRun.error || undefined}
                  style={{ marginTop: 4, color: runFailed ? '#fecaca' : '#cbd5e1', fontSize: 11.5, lineHeight: 1.4 }}
                >
                  Hourly retry worker: {retryRun.executedAt ? new Date(retryRun.executedAt).toLocaleString() : 'time unavailable'} · {retryRun.status === 'NO_WORK'
                    ? 'no retries due'
                    : `${retryRun.attemptedCount || 0}/${retryRun.dueCount || 0} processed · ${retryRun.queued || 0} queued · ${retryRun.deferredCount || 0} deferred · ${retryRun.failed || 0} failed`}
                  {retryRun.error ? ` · ${retryRun.error}` : ''}
                </div>
              );
            })()}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={handleRunAutopilotNow}
            disabled={runningAutopilot}
            style={{
              background: '#ec4899',
              color: '#ffffff',
              border: 'none',
              padding: '9px 16px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: runningAutopilot ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 6px rgba(236, 72, 153, 0.3)',
            }}
          >
            {runningAutopilot ? '⚡ Processing Auto-Pilot...' : '▶ Run Auto-Pilot Pipeline Now'}
          </button>
        </div>
      </div>

      {/* MONDAY RELEASE QUEUE BANNER */}
      <div className="release-queue-banner">
        <div className="queue-info-group">
          <div className="queue-icon-badge">🕒</div>
          <div>
            <div className="queue-title-row">
              <h3>Weekly Monday Release Queue (01:00 AM – 06:00 AM IST)</h3>
              <span className={`queue-pill-count ${(queueSummary?.totalQueued || 0) > 0 ? '' : 'empty'}`}>
                {(queueSummary?.totalQueued || 0)} Items Staged
              </span>
            </div>
            <p className="queue-subtext">
              {queueSummary?.nextReleaseTime ? (
                <>
                  Next Scheduled Batch: <strong>{queueSummary.nextReleaseTime}</strong> ({queueSummary.daysRemaining} days away) • Staged: <strong>{queueSummary.queuedProductsCount || 0} Products</strong> & <strong>{queueSummary.queuedCollectionsCount || 0} Collections</strong>. Google Merchant & Meta ad catalogs remain safe from mid-week re-approval dips.
                </>
              ) : (
                'All approved product and collection SEO changes are held in queue and pushed in a single off-peak batch every Monday at 01:00 AM IST.'
              )}
            </p>
          </div>
        </div>
        <div className="queue-actions-group">
          <button
            className="btn-unqueue"
            onClick={fetchQueueSummary}
            disabled={loadingQueue}
            style={{ padding: '8px 12px', fontSize: 12, background: 'rgba(255,255,255,0.1)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.2)' }}
            title="Refresh Release Queue counts"
          >
            {loadingQueue ? '⟳...' : '⟳ Refresh Queue'}
          </button>
          {(queueSummary?.totalQueued || 0) > 0 && (
            <button
              className="btn-force-release"
              onClick={handleExecuteReleaseNow}
              disabled={executingRelease}
              title="Admin override: Immediately sync all staged items to Shopify right now"
            >
              {executingRelease ? '⚡ Releasing Now...' : `⚡ Force Release All Now (${queueSummary.totalQueued})`}
            </button>
          )}
        </div>
      </div>

      {/* TAB 0: ACTION CENTER (PENDING ACTIONS INBOX) */}
      {activeTab === 'action_center' && (
        <div className="action-center-container">
          {/* Action Center KPI Summary Cards */}
          <div className="seo-stats-grid" style={{ marginBottom: 20 }}>
            <div className="seo-stat-card" style={{ borderLeft: pendingActions.totalCount > 0 ? '4px solid #dc2626' : '4px solid #16a34a' }}>
              <div className="seo-stat-label">Total Actions Pending</div>
              <div className="seo-stat-value" style={{ color: pendingActions.totalCount > 0 ? '#dc2626' : '#16a34a' }}>
                {pendingActions.totalCount > 0 ? `${pendingActions.totalCount} Require Input` : '0 (All Clear)'}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Product Drafts for Review</div>
              <div className="seo-stat-value" style={{ color: pendingActions.pendingProducts.length > 0 ? '#ea580c' : '#0f172a' }}>
                {pendingActions.pendingProducts.length}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Google Ads Updates Needed</div>
              <div className="seo-stat-value" style={{ color: pendingActions.adUpdates.length > 0 ? '#7c3aed' : '#0f172a' }}>
                {pendingActions.adUpdates.length}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Collections In Review</div>
              <div className="seo-stat-value" style={{ color: pendingActions.pendingCollections.length > 0 ? '#0284c7' : '#0f172a' }}>
                {pendingActions.pendingCollections.length}
              </div>
            </div>
          </div>

          {/* ZERO-STATE: ALL SYSTEMS AUTONOMOUS */}
          {pendingActions.totalCount === 0 ? (
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
              border: '1.5px solid #86efac',
              borderRadius: 16,
              padding: '40px 32px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(22, 163, 74, 0.08)',
              marginBottom: 32,
            }}>
              <div style={{
                width: 72,
                height: 72,
                background: '#dcfce7',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 36,
                marginBottom: 16,
                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.2)',
              }}>
                🛡️
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#166534', margin: '0 0 8px' }}>
                All Systems Fully Autonomous — Zero Pending Actions!
              </h2>
              <p style={{ maxWidth: 640, margin: '0 auto 24px', fontSize: 14, color: '#15803d', lineHeight: 1.6 }}>
                There are no pending drafts, failed syncs, or urgent decisions required from your side. The AI Sentinel Watchdog and autonomous background pipelines are operating normally.
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12,
                maxWidth: 820,
                margin: '0 auto 28px',
                textAlign: 'left',
              }}>
                <div style={{ background: '#ffffff', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>🕒 Next Release Batch</div>
                  <strong style={{ fontSize: 13, color: '#0f172a' }}>{queueSummary?.totalQueued || 0} Items Queued for Monday</strong>
                </div>
                <div style={{ background: '#ffffff', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>🛡️ Cooldown Protection</div>
                  <strong style={{ fontSize: 13, color: '#0f172a' }}>300 Products in 90-Day Lock</strong>
                </div>
                <div style={{ background: '#ffffff', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>🚀 Google Ads Bridge</div>
                  <strong style={{ fontSize: 13, color: '#0f172a' }}>{adRecommendations.filter(r => r.isLiveInGoogleAds).length} Live Campaigns Active</strong>
                </div>
                <div style={{ background: '#ffffff', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>🤖 Watchdog Sentinel</div>
                  <strong style={{ fontSize: 13, color: '#0f172a' }}>All QA Guardrails Passed</strong>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button
                  onClick={() => setActiveTab('opportunities')}
                  className="btn-primary-seo"
                  style={{ background: '#059669', padding: '9px 18px', fontSize: 13, fontWeight: 700 }}
                >
                  🎯 View Product Opportunities Board
                </button>
                <button
                  onClick={() => setActiveTab('google-ads')}
                  className="btn-primary-seo"
                  style={{ background: '#7c3aed', padding: '9px 18px', fontSize: 13, fontWeight: 700 }}
                >
                  🚀 Open Google Ads Hub
                </button>
                <button
                  onClick={() => setActiveTab('tracker')}
                  className="btn-secondary"
                  style={{ padding: '9px 18px', fontSize: 13, fontWeight: 600 }}
                >
                  📈 Check Ranking Tracker
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              {/* Batch Action Banner */}
              {pendingActions.pendingProducts.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                  border: '1.5px solid #fdba74',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12,
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: '#9a3412' }}>
                      ⚡ {pendingActions.pendingProducts.length} Product Drafts Awaiting Approval
                    </h3>
                    <p style={{ margin: 0, fontSize: 13, color: '#c2410c' }}>
                      Stage all pending drafts in 1-click for Monday 01:00 AM release, or review each draft individually below.
                    </p>
                  </div>
                  <button
                    onClick={handleQueueAllPendingProducts}
                    className="btn-queue-monday"
                    style={{ padding: '10px 20px', fontSize: 13, fontWeight: 800, background: '#ea580c' }}
                  >
                    🕒 Approve &amp; Queue All ({pendingActions.pendingProducts.length}) for Monday
                  </button>
                </div>
              )}

              {/* SECTION 1: PRODUCT DRAFTS AWAITING APPROVAL */}
              {pendingActions.pendingProducts.length > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 20 }}>🎯</span>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                        Product SEO Drafts Awaiting Review ({pendingActions.pendingProducts.length})
                      </h3>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {pendingActions.pendingProducts.map((opp) => {
                      const titleLength = (opp.proposedSeo?.title || '').length;
                      const isTitleLong = titleLength > 70;
                      return (
                        <div
                          key={opp.id}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 12,
                            padding: '18px 20px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: 16, color: '#0f172a' }}>{opp.productTitle}</strong>
                                {opp.productSku && (
                                  <span style={{ background: '#f1f5f9', color: '#475569', fontSize: 11, padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace' }}>
                                    SKU: {opp.productSku}
                                  </span>
                                )}
                                <span style={{ background: '#dbeafe', color: '#1e40af', fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 700 }}>
                                  Target: "{opp.keyword}"
                                </span>
                                {opp.source && (
                                  <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 600 }}>
                                    {opp.source === 'day1_new_arrival' ? '✨ Day-1 New Arrival' : 'Trend Match'}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <button
                                className="btn-queue-monday"
                                onClick={() => handleApproveToQueue(opp.id, 'product')}
                                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700 }}
                              >
                                🕒 Queue for Monday
                              </button>
                              <button
                                className="btn-approve"
                                onClick={() => handleApprove(opp.id)}
                                style={{ background: '#059669', padding: '6px 12px', fontSize: 12, fontWeight: 700 }}
                              >
                                ⚡ Sync Now
                              </button>
                              <button
                                className="btn-reject"
                                onClick={() => handleReject(opp.id)}
                                style={{ padding: '6px 10px', fontSize: 12 }}
                              >
                                ✕ Dismiss
                              </button>
                            </div>
                          </div>

                          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', fontSize: 13, marginBottom: 8 }}>
                            <div style={{ marginBottom: 6 }}>
                              <span style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginRight: 6 }}>Proposed Title:</span>
                              <strong style={{ color: isTitleLong ? '#b45309' : '#0f172a' }}>{opp.proposedSeo?.title}</strong>
                              <span style={{ marginLeft: 8, fontSize: 11, color: isTitleLong ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                                ({titleLength} chars {isTitleLong ? '⚠️ Long' : '✓ Optimal'})
                              </span>
                            </div>
                            <div>
                              <span style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginRight: 6 }}>Proposed Meta:</span>
                              <span style={{ color: '#334155' }}>{opp.proposedSeo?.description}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* SECTION 2: GOOGLE ADS CAMPAIGNS NEEDING ACTION */}
              {pendingActions.adUpdates.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>🚀</span>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                      Google Ads Expansion Hub Actions ({pendingActions.adUpdates.length})
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
                    {pendingActions.adUpdates.map((rec) => (
                      <div
                        key={rec.id}
                        style={{
                          background: '#ffffff',
                          border: rec.updateNeeded ? '1.5px solid #f59e0b' : '1px solid #e2e8f0',
                          borderRadius: 12,
                          padding: '18px 20px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                              {rec.endUse}
                            </h4>
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                              {rec.productCount} Active Catalog SKUs
                            </span>
                          </div>
                          {rec.updateNeeded ? (
                            <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, padding: '3px 8px', borderRadius: 9999, fontWeight: 800 }}>
                              ⚠️ Update Recommended
                            </span>
                          ) : (
                            <span style={{ background: '#f3e8ff', color: '#6b21a8', fontSize: 11, padding: '3px 8px', borderRadius: 9999, fontWeight: 800 }}>
                              ✨ Novel Cluster Ready
                            </span>
                          )}
                        </div>

                        <p style={{ margin: '0 0 14px', fontSize: 13, color: rec.updateNeeded ? '#b45309' : '#475569', lineHeight: 1.4 }}>
                          {rec.updateNeeded
                            ? `⚠️ ${rec.newProductsSinceSync} new fabrics have been synced since you created this asset group. Click below to refresh your ad headlines & video blueprint.`
                            : `High search demand cluster with ${rec.productCount} in-stock SKUs ready for Performance Max, RSA Search, and Video ads.`}
                        </p>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className="btn-primary-seo"
                            onClick={() => {
                              setModalCampaignTab('pmax');
                              handleGenerateAdCopy(rec);
                            }}
                            style={{ background: '#7c3aed', padding: '7px 14px', fontSize: 12, fontWeight: 700 }}
                          >
                            📋 View / Copy Multi-Campaign Assets &amp; Video
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => handleToggleAssetGroupLive(rec.endUse, true)}
                            style={{ padding: '7px 12px', fontSize: 12, fontWeight: 600 }}
                          >
                            {rec.updateNeeded ? '✓ Confirm Live Update' : '✓ Mark as Created'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 3: COLLECTIONS AWAITING SEO APPROVAL */}
              {pendingActions.pendingCollections.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>📁</span>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                      Shopify Collections Needing SEO Approval ({pendingActions.pendingCollections.length})
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
                    {pendingActions.pendingCollections.map((col) => (
                      <div
                        key={col.id}
                        style={{
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: 12,
                          padding: '16px 18px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}
                      >
                        <h4 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{col.title}</h4>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                          Target: <code>"{col.targetKeyword || col.primaryKeyword}"</code> • {col.productsCount || 0} Products
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            className="btn-approve"
                            onClick={() => handleApproveCollection(col.id, col.title)}
                            style={{ background: '#059669', padding: '6px 12px', fontSize: 12, fontWeight: 700 }}
                          >
                            ⚡ Sync to Shopify
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => setActiveTab('collections')}
                            style={{ padding: '6px 12px', fontSize: 12 }}
                          >
                            📁 View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 4: TOP COMPETITOR CONQUEST OPPORTUNITIES */}
              {pendingActions.competitorGaps.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>⚔️</span>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                      High-Opportunity Competitor Keyword Gaps ({pendingActions.competitorGaps.length})
                    </h3>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
                    {pendingActions.competitorGaps.map((gap, i) => (
                      <div
                        key={i}
                        style={{
                          background: '#ffffff',
                          border: '1px solid #fecaca',
                          borderRadius: 10,
                          padding: '14px 16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <div>
                          <strong style={{ fontSize: 14, color: '#991b1b', display: 'block' }}>"{gap.query}"</strong>
                          <span style={{ fontSize: 11, color: '#64748b' }}>
                            Competitor: {gap.competitorName || 'Rival Store'} • Score: {gap.opportunityScore}
                          </span>
                        </div>
                        <button
                          className="btn-primary-seo"
                          onClick={() => handleExecuteConquest(gap.query, 'OPTIMIZE_PRODUCTS')}
                          disabled={executingConquestQuery === gap.query}
                          style={{ background: '#dc2626', padding: '6px 12px', fontSize: 12, fontWeight: 700 }}
                        >
                          {executingConquestQuery === gap.query ? '⚔️...' : '⚔️ Launch Conquest'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB 1: OPPORTUNITY ENGINE */}
      {activeTab === 'opportunities' && (() => {
        const pendingOpps = opportunities.filter((o) => o.status === 'READY_FOR_REVIEW');
        const queuedOpps = opportunities.filter((o) => o.status === 'QUEUED_FOR_RELEASE');
        const displayedOpps = oppFilterStatus === 'READY_FOR_REVIEW'
          ? pendingOpps
          : oppFilterStatus === 'QUEUED_FOR_RELEASE'
          ? queuedOpps
          : opportunities;

        return (
        <>
          {/* High-Level Stats */}
          <div className="seo-stats-grid">
            <div className="seo-stat-card">
              <div className="seo-stat-label">Discovered Search Trends</div>
              <div className="seo-stat-value">{queries.length}</div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Pending Drafts for Review</div>
              <div className="seo-stat-value" style={{ color: pendingOpps.length > 0 ? '#ea580c' : '#0f172a' }}>
                {pendingOpps.length}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">🕒 Queued for Monday 01:00 AM</div>
              <div className="seo-stat-value" style={{ color: queuedOpps.length > 0 ? '#6366f1' : '#64748b' }}>
                {queuedOpps.length}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Cooldown Protection</div>
              <div className="seo-stat-value" style={{ color: '#2563eb', fontSize: 20 }}>90-Day Safe Lock</div>
            </div>
          </div>

          {/* DAY 1: NEW ARRIVALS SEO INTAKE BANNER */}
          <div style={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
            border: '1px solid #a7f3d0',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 28, background: '#d1fae5', width: 48, height: 48, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ✨
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#065f46' }}>
                    Day-1 New Arrivals SEO Intake
                  </h3>
                  <span style={{ background: '#059669', color: '#ffffff', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>
                    {day1Candidates.length} New Fabrics Awaiting Day-1 SEO
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', color: '#047857', fontSize: 13 }}>
                  Automatically generate high-intent search titles, True GSM specs, HTML tables, and buyer FAQs for newly uploaded fabrics so Google indexes them with high authority from Day 1.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-unqueue"
                onClick={fetchDay1Candidates}
                disabled={loadingDay1Candidates}
                style={{ padding: '8px 12px', fontSize: 12, background: '#ffffff', color: '#065f46', border: '1px solid #a7f3d0' }}
                title="Check for newly added fabrics"
              >
                {loadingDay1Candidates ? '⟳...' : '⟳ Check New Arrivals'}
              </button>
              {day1Candidates.length > 0 && (
                <button
                  type="button"
                  className="btn-primary-seo"
                  onClick={() => handleGenerateDay1Batch(5)}
                  disabled={generatingDay1}
                  style={{ background: '#059669', borderColor: '#047857', padding: '8px 16px', fontSize: 13 }}
                >
                  {generatingDay1 ? '✨ Generating Day-1 SEO...' : `✨ Generate Day-1 SEO (Next 5 Fabrics)`}
                </button>
              )}
            </div>
          </div>

          {/* PHASE 1: KEYWORD OPPORTUNITY BOARD */}
          <div className="queries-board-card">
            <div className="queries-board-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0 }}>Phase 1: Keyword Opportunity Board</h2>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '3px 10px',
                      borderRadius: 16,
                      fontSize: 12,
                      fontWeight: 600,
                      background: queriesLastUpdated ? '#f0fdf4' : '#f1f5f9',
                      color: queriesLastUpdated ? '#166534' : '#64748b',
                      border: `1px solid ${queriesLastUpdated ? '#bbf7d0' : '#e2e8f0'}`,
                    }}
                    title={queriesLastUpdated ? new Date(queriesLastUpdated).toLocaleString() : 'Click Scan & Refresh Trends to update'}
                  >
                    <span style={{ fontSize: 13 }}>🕒</span>
                    Last Updated: {formatLastUpdatedTime(queriesLastUpdated)}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
                  High-converting Search Console & Google Trends queries. Click <strong>"⚡ Run 3-5 Products"</strong> to generate candidate fabrics.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setShowManageExclusionsModal(true)}
                  style={{
                    padding: '7px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 6,
                    background: '#ffffff',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                  title="View and manage negative keyword filters (e.g. embroidery, stitched, out-of-stock fabrics)"
                >
                  <span>⚙️</span> Exclusions ({loadingExclusions ? '...' : (exclusionsData?.allActiveExclusions?.length || 0)})
                </button>
                <button
                  type="button"
                  className="btn-primary-seo"
                  onClick={handleDiscoverQueries}
                  disabled={discovering || runningDailyScan || Boolean(runningQueryText)}
                  style={{ padding: '7px 14px', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  title="Manually sweeps Google Search Console & Google Trends for fresh opportunity keywords"
                >
                  {discovering ? '🔄 Scanning...' : '⟳ Scan & Refresh Trends'}
                </button>
              </div>
            </div>

            {/* Trend Filter Pills */}
            {queries.length > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setTrendFilter('ACTION_NEEDED')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: trendFilter === 'ACTION_NEEDED' ? 700 : 500,
                    background: trendFilter === 'ACTION_NEEDED' ? '#eff6ff' : '#f8fafc',
                    color: trendFilter === 'ACTION_NEEDED' ? '#1d4ed8' : '#64748b',
                    border: `1px solid ${trendFilter === 'ACTION_NEEDED' ? '#93c5fd' : '#e2e8f0'}`,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>🔥 Action Needed (Untapped)</span>
                  <span style={{
                    background: trendFilter === 'ACTION_NEEDED' ? '#2563eb' : '#cbd5e1',
                    color: '#ffffff',
                    fontSize: 11,
                    padding: '2px 7px',
                    borderRadius: 12,
                    fontWeight: 700,
                  }}>
                    {actionNeededCount}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setTrendFilter('OPTIMIZED')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: trendFilter === 'OPTIMIZED' ? 700 : 500,
                    background: trendFilter === 'OPTIMIZED' ? '#f0fdf4' : '#f8fafc',
                    color: trendFilter === 'OPTIMIZED' ? '#15803d' : '#64748b',
                    border: `1px solid ${trendFilter === 'OPTIMIZED' ? '#86efac' : '#e2e8f0'}`,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>✅ Optimized / Action Taken</span>
                  <span style={{
                    background: trendFilter === 'OPTIMIZED' ? '#16a34a' : '#cbd5e1',
                    color: '#ffffff',
                    fontSize: 11,
                    padding: '2px 7px',
                    borderRadius: 12,
                    fontWeight: 700,
                  }}>
                    {optimizedCount}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setTrendFilter('ALL')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: trendFilter === 'ALL' ? 700 : 500,
                    background: trendFilter === 'ALL' ? '#f1f5f9' : '#f8fafc',
                    color: trendFilter === 'ALL' ? '#0f172a' : '#64748b',
                    border: `1px solid ${trendFilter === 'ALL' ? '#94a3b8' : '#e2e8f0'}`,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>🌐 All Trends</span>
                  <span style={{
                    background: trendFilter === 'ALL' ? '#475569' : '#cbd5e1',
                    color: '#ffffff',
                    fontSize: 11,
                    padding: '2px 7px',
                    borderRadius: 12,
                    fontWeight: 700,
                  }}>
                    {queries.length}
                  </span>
                </button>
              </div>
            )}

            {loadingQueries && <p>Loading discovered search queries...</p>}

            {!loadingQueries && queries.length === 0 && (
              <div style={{ background: '#f8fafc', padding: 24, borderRadius: 8, textAlign: 'center', color: '#64748b' }}>
                No queries discovered yet. Click <strong>"⟳ Scan & Refresh Trends"</strong> to pull opportunities from Google Search Console & Trends.
              </div>
            )}

            {!loadingQueries && queries.length > 0 && displayedQueries.length === 0 && (
              <div style={{ background: '#f8fafc', padding: 24, borderRadius: 8, textAlign: 'center', color: '#64748b' }}>
                {trendFilter === 'ACTION_NEEDED' ? (
                  <div>
                    <span style={{ fontSize: 24, display: 'block', marginBottom: 6 }}>🎉</span>
                    <strong>All current trend queries have been optimized!</strong>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      Switch to the <strong>"✅ Optimized / Action Taken"</strong> tab to view historical queries, or click <strong>"⟳ Scan & Refresh Trends"</strong> to discover newly emerging search trends.
                    </div>
                  </div>
                ) : (
                  <div>No queries in this category.</div>
                )}
              </div>
            )}

            {displayedQueries.length > 0 && (
              <div className="queries-table-container">
                <table className="queries-table">
                  <thead>
                    <tr>
                      <th>Target Search Query</th>
                      <th>Source</th>
                      <th>Intent & Category</th>
                      <th>Search Volume / Demand</th>
                      <th>Current Position</th>
                      <th>Opportunity Score</th>
                      <th>Lifecycle</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedQueries.map((q, idx) => {
                      const isRunningThis = runningQueryText === q.query;
                      const isRealGsc = !q.isTrend && q.source === 'google_search_console' && q.impressions != null;
                      return (
                        <tr key={idx}>
                          <td>
                            <strong style={{ fontSize: 14, color: '#0f172a' }}>"{q.query}"</strong>
                            {q.ownerPage && (
                              <div style={{ fontSize: 11, color: q.ownerType === 'AMBIGUOUS' ? '#b45309' : '#475569', marginTop: 3, overflowWrap: 'anywhere' }}>
                                GSC landing page: <strong>{q.ownerType === 'AMBIGUOUS' ? 'Multiple pages' : q.ownerType}</strong>
                                {q.ownerType !== 'AMBIGUOUS' ? ` · ${q.ownerPage}` : ` · leading page has ${Math.round((q.ownerShare || 0) * 100)}% of impressions`}
                                {q.competingPageCount > 1 && q.ownerType !== 'AMBIGUOUS' ? ` · ${q.competingPageCount} pages compete` : ''}
                              </div>
                            )}
                            {q.detectedFabricBase && (
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                Detected Fabric: <strong>{q.detectedFabricBase}</strong>
                              </div>
                            )}
                            <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>
                              Recommended page: <strong>{getRecommendedPageLabel(q)}</strong>
                            </div>
                            {q.routingStatus === 'HELD' && (
                              <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>
                                Automatic action held: {q.routingReason || 'Search intent or page ownership is not clear enough.'}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="badge-intent" style={{
                              background: q.source === 'google_search_console' ? '#eff6ff' : '#fff7ed',
                              color: q.source === 'google_search_console' ? '#1d4ed8' : '#c2410c',
                            }}>
                              {q.source === 'google_search_console' ? '📊 GSC' : '🔥 Trends A-Z'}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: 12, fontWeight: 500, color: '#475569', textTransform: 'capitalize' }}>
                              {String(q.intent || 'category').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>
                            {isRealGsc ? (
                              <span><strong>{Number(q.impressions).toLocaleString()}</strong> <small style={{ color: '#64748b' }}>impr</small></span>
                            ) : (
                              <span className="badge-trend-demand">⚡ High Demand</span>
                            )}
                          </td>
                          <td>
                            {isRealGsc && q.position != null ? (
                              <span>Pos <strong>{q.position}</strong></span>
                            ) : (
                              <span className="badge-unranked">🎯 Unranked (New)</span>
                            )}
                          </td>
                          <td>
                            <span className="badge-score">{q.opportunityScore || 'N/A'}</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {/* Collection Lifecycle Badge */}
                              {q.hasCollection || q.collection ? (
                                <a
                                  href={q.collection?.url || `https://www.fabvoguestudio.com/collections/${q.collection?.handle}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    background: '#ecfdf5',
                                    color: '#047857',
                                    border: '1px solid #a7f3d0',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    width: 'fit-content',
                                  }}
                                  title={`Collection "${q.collection?.title}" is live on Shopify. Click to view.`}
                                >
                                  📁 {q.collection?.title ? (q.collection.title.length > 20 ? `${q.collection.title.slice(0, 18)}...` : q.collection.title) : 'Collection Live'} ↗
                                </a>
                              ) : null}

                              {/* Blog Lifecycle Badge */}
                              {q.hasBlog || q.blog ? (
                                <a
                                  href={q.blog?.url || `https://www.fabvoguestudio.com/blogs/news/${q.blog?.handle}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    background: '#f5f3ff',
                                    color: '#6d28d9',
                                    border: '1px solid #ddd6fe',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    width: 'fit-content',
                                  }}
                                  title={`Blog Post "${q.blog?.title}" is published on Shopify. Click to read.`}
                                >
                                  📝 {q.blog?.title ? (q.blog.title.length > 20 ? `${q.blog.title.slice(0, 18)}...` : q.blog.title) : 'Blog Live'} ↗
                                </a>
                              ) : null}

                              {/* Product Opportunities Lifecycle Badge */}
                              {q.status === 'SYNCED' ? (
                                <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 11 }}>
                                  ✅ Products Synced
                                </span>
                              ) : q.status === 'READY_FOR_REVIEW' ? (
                                <span style={{ color: '#ea580c', fontWeight: 600, fontSize: 11 }}>
                                  ⚡ Products in Review
                                </span>
                              ) : !q.hasCollection && !q.hasBlog ? (
                                <span style={{ color: '#64748b', fontSize: 11 }}>Ready</span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                className="btn-run-query"
                                onClick={() => handleGenerateForQuery(q)}
                                disabled={Boolean(runningQueryText) || q.routingStatus === 'HELD' || q.ownerActionable === false || (q.recommendedPageType !== 'product' && q.ownerType !== 'COLLECTION')}
                                title={q.ownerType === 'COLLECTION' ? 'Generate an SEO proposal for the existing GSC-owned collection' : q.ownerType === 'PRODUCT' ? 'Optimize the product currently receiving this query in Google Search Console' : q.ownerActionable === false ? 'Automatic change held because this query has multiple competing landing pages or a non-editable owner' : 'Generate product opportunities for this query'}
                              >
                                {isRunningThis ? 'Generating...' : q.ownerType === 'COLLECTION' ? '⚡ Optimize collection' : q.ownerType === 'PRODUCT' ? '⚡ Optimize product' : q.ownerActionable === false ? '⚠ Resolve owner' : '⚡ Products'}
                              </button>
                              <button
                                className="btn-primary-seo"
                                style={{
                                  background: q.hasCollection ? '#059669' : '#0284c7',
                                  padding: '6px 10px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#ffffff',
                                }}
                                onClick={() => handleOpenSmartCollectionForQuery(q.query)}
                                disabled={q.routingStatus === 'HELD' || q.recommendedPageType !== 'collection' || Boolean(q.ownerPage)}
                                title={q.hasCollection ? "Collection already created. Click to re-preview or update." : "Create a Shopify Smart Collection directly for this search query"}
                              >
                                {q.hasCollection ? '📁 Collection Live' : '📁 Collection'}
                              </button>
                              <button
                                className="btn-primary-seo"
                                style={{
                                  background: q.hasBlog ? '#7c3aed' : '#8b5cf6',
                                  padding: '6px 10px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  borderRadius: 6,
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#ffffff',
                                }}
                                onClick={() => handleOpenBlogForQuery(q.query, q.ownerType === 'BLOG' ? q.ownerHandle : null, q.source === 'google_search_console' ? q : null)}
                                disabled={q.routingStatus === 'HELD' || Boolean(q.ownerPage && q.ownerType !== 'BLOG') || (!q.ownerPage && q.intent !== 'informational') || (q.hasBlog && q.ownerType !== 'BLOG')}
                                title={q.ownerType === 'BLOG' ? 'Generate and review an update for the GSC-owned blog article' : q.hasBlog ? 'A matching blog exists; use its GSC-owned query to update it safely' : q.ownerPage ? 'Another page type owns this query; conflicting blog creation is disabled' : q.intent === 'informational' ? 'Generate an informational fabric guide' : 'Blog creation is reserved for informational search intent'}
                              >
                                {q.ownerType === 'BLOG' ? '📝 Update blog' : q.hasBlog ? '📝 Blog Live' : '📝 Blog'}
                              </button>
                              <button
                                type="button"
                                style={{
                                  background: '#f8fafc',
                                  color: '#64748b',
                                  border: '1px solid #cbd5e1',
                                  padding: '6px 9px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                onClick={() => handleOpenExcludeModal(q)}
                                title="Exclude keyword or dismiss this search query from board"
                              >
                                🚫 Exclude
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* PHASE 2: PRODUCT REVIEW & DIFFS BOARD */}
          <div id="review-opportunities-section" style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Phase 2: Product Review & Staging Board</h2>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
                  Review proposed SEO titles, descriptions, and FAQs. Stage items in the Monday release queue or push immediately.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {pendingOpps.length > 0 && (
                  <>
                    <button
                      className="btn-queue-monday"
                      onClick={handleQueueAllPendingProducts}
                      title="Stage all pending products for Monday 01:00 AM IST release window"
                    >
                      🕒 Approve & Queue All for Monday ({pendingOpps.length})
                    </button>
                    <button
                      className="btn-approve"
                      style={{ padding: '8px 14px', fontSize: 13, background: '#059669' }}
                      onClick={handleApproveAll}
                      title="Direct immediate sync to Shopify (bypasses Monday queue)"
                    >
                      ⚡ Force Sync All to Shopify ({pendingOpps.length})
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Quick Filter Tabs */}
            <div className="queue-filter-tabs">
              <button
                className={`queue-filter-btn ${oppFilterStatus === 'ALL' ? 'active' : ''}`}
                onClick={() => setOppFilterStatus('ALL')}
              >
                All Opportunities ({opportunities.length})
              </button>
              <button
                className={`queue-filter-btn ${oppFilterStatus === 'READY_FOR_REVIEW' ? 'active' : ''}`}
                onClick={() => setOppFilterStatus('READY_FOR_REVIEW')}
              >
                Pending Review ({pendingOpps.length})
              </button>
              <button
                className={`queue-filter-btn ${oppFilterStatus === 'QUEUED_FOR_RELEASE' ? 'active' : ''}`}
                onClick={() => setOppFilterStatus('QUEUED_FOR_RELEASE')}
              >
                🕒 Queued for Monday ({queuedOpps.length})
              </button>
            </div>

            {loadingOpps && <p>Loading product cards...</p>}

            {!loadingOpps && displayedOpps.length === 0 && (
              <div style={{ background: '#f8fafc', padding: 32, borderRadius: 8, textAlign: 'center', color: '#64748b' }}>
                {oppFilterStatus === 'QUEUED_FOR_RELEASE'
                  ? 'No products currently queued for Monday release. Approve pending drafts to stage them.'
                  : 'No products pending review. Select a search query from the table above and click "⚡ Run (3-5 Products)" to generate candidates.'}
              </div>
            )}

            {displayedOpps.map((opp) => {
              const trace = opp.pipelineTrace;
              const isTraceOpen = expandedTraceId === opp.id;
              const isQueued = opp.status === 'QUEUED_FOR_RELEASE';

              return (
                <div key={opp.id} className="opportunity-card" style={isQueued ? { border: '1.5px solid #a5b4fd', background: '#faf5ff' } : {}}>
                  <div className="opp-card-header">
                    <div>
                      <div className="opp-title-badge-group">
                        <span className="badge-intent" style={{
                          background: opp.isDay1 || opp.source === 'day1_new_arrival' ? '#ecfdf5' : (opp.source === 'google_search_console' ? '#eff6ff' : '#fff7ed'),
                          color: opp.isDay1 || opp.source === 'day1_new_arrival' ? '#047857' : (opp.source === 'google_search_console' ? '#1d4ed8' : '#c2410c'),
                        }}>
                          {opp.isDay1 || opp.source === 'day1_new_arrival' ? '✨ Day-1 Fresh Arrival' : (opp.source === 'google_search_console' ? '📊 GSC Opportunity' : '🔥 Market Trend')}
                        </span>
                        <span className="badge-mto">Made to Order</span>
                        {isQueued ? (
                          <span className="badge-queued-release">
                            🕒 Queued for Monday ({opp.scheduledReleaseFormatted || '01:00 AM IST'})
                          </span>
                        ) : (
                          <span className="badge-score">Score: {opp.opportunityScore || 'N/A'}</span>
                        )}
                        <strong style={{ fontSize: 16 }}>"{opp.keyword}"</strong>
                      </div>
                      <div style={{ color: '#475569', fontSize: 13, marginTop: 6 }}>
                        Target Fabric: <strong>{opp.productTitle}</strong> • ID: <code>{opp.targetEntityId}</code> • Spec: <strong>{trace?.agent3_catalogMatcher?.resolvedFabricSpec || 'Ground-Truth'}</strong> {trace?.agent3_catalogMatcher?.trueGsm ? `(${trace.agent3_catalogMatcher.trueGsm})` : ''}
                      </div>
                    </div>

                    <div className="gsc-metrics-pill">
                      {opp.gscMetrics?.isTrend || opp.source !== 'google_search_console' ? (
                        <>
                          <span style={{ color: '#92400e', fontWeight: 600 }}>⚡ High Trend Demand</span>
                          <span style={{ color: '#475569' }}>Target: <strong>Top 10</strong></span>
                          <span>Cluster Rank: <strong>{trace?.agent3_catalogMatcher?.clusterRank || 'Top Match'}</strong></span>
                        </>
                      ) : (
                        <>
                          <span><strong>{Number(opp.gscMetrics?.impressions || 0).toLocaleString()}</strong> Impr</span>
                          <span>Pos <strong>{opp.gscMetrics?.position ? Number(opp.gscMetrics.position).toFixed(1) : 'N/A'}</strong></span>
                          <span>Cluster Rank: <strong>{trace?.agent3_catalogMatcher?.clusterRank || 'Top Match'}</strong></span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Side by Side Diff */}
                  <div className="seo-diff-grid">
                    <div className="diff-col">
                      <h4>Current Storefront SEO</h4>
                      <div className="diff-field-label">Current Meta Title:</div>
                      <div className="diff-content">{opp.currentSeo?.title || '(Empty / Default Title)'}</div>
                      <div className="diff-field-label">Current Meta Description:</div>
                      <div className="diff-content">{opp.currentSeo?.description || '(Empty / Default Description)'}</div>
                    </div>

                    <div className="diff-col" style={{ background: '#f0fdf4', padding: 14, borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <h4 style={{ color: '#15803d', margin: 0 }}>Proposed AI SEO (Gemini 2.5 Flash)</h4>
                        {opp.qaAudit && (
                          <span style={{
                            background: opp.qaAudit.score === 100 ? '#dcfce7' : '#fef3c7',
                            color: opp.qaAudit.score === 100 ? '#15803d' : '#92400e',
                            border: `1px solid ${opp.qaAudit.score === 100 ? '#86efac' : '#fcd34d'}`,
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}>
                            🛡️ QA Verified ({opp.qaAudit.passedChecks}/{opp.qaAudit.totalChecks})
                          </span>
                        )}
                      </div>

                      {opp.qaAudit && (
                        <div style={{
                          marginBottom: 12,
                          background: '#ffffff',
                          border: '1px solid #bbf7d0',
                          borderRadius: 6,
                          padding: '8px 10px',
                          fontSize: 12,
                        }}>
                          <div style={{ fontWeight: 600, color: '#166534', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                            <span>🛡️ Automated QA Auditor Score: {opp.qaAudit.score}/100</span>
                            {opp.qaAudit.autoHealed && (
                              <span style={{ color: '#047857', fontWeight: 600, fontSize: 11 }}>✨ Auto-Healed Discrepancies</span>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '4px 8px' }}>
                            {(opp.qaAudit.checks || []).map((chk, cIdx) => (
                              <div key={cIdx} style={{ fontSize: 11, color: chk.passed ? '#15803d' : '#b45309', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span>{chk.passed ? '✅' : '⚠️'}</span>
                                <span><strong>{chk.name}:</strong> {chk.passed ? 'Verified' : 'Review Needed'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="diff-field-label">Proposed Meta Title:</div>
                      <div className="diff-content" style={{ fontWeight: 600 }}>{opp.proposedSeo?.title}</div>
                      <div className="diff-field-label">Proposed Meta Description:</div>
                      <div className="diff-content">{opp.proposedSeo?.description}</div>

                      {opp.proposedSeo?.tags && (
                        <>
                          <div className="diff-field-label">Proposed Search Boost Tags:</div>
                          <div className="diff-tags-container">
                            {opp.proposedSeo.tags.map((t, idx) => (
                              <span key={idx} className="tag-pill">{t}</span>
                            ))}
                          </div>
                        </>
                      )}

                      {opp.proposedSeo?.faqs && opp.proposedSeo.faqs.length > 0 && (
                        <div className="proposed-faqs-container">
                          <div className="diff-field-label">Proposed Fabric FAQs (Smart-Merged Metafields):</div>
                          <div className="faqs-list">
                            {opp.proposedSeo.faqs.map((faq, fIdx) => (
                              <div key={fIdx} className="faq-item">
                                <div className="faq-q"><strong>Q: {faq.question}</strong></div>
                                <div className="faq-a">{faq.answer}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {opp.proposedSeo?.imageAltTags && (
                        <div style={{ marginTop: 12, borderTop: '1px dashed #86efac', paddingTop: 8 }}>
                          <div className="diff-field-label">🖼️ Optimized Image Alt Tags (Google Image Search):</div>
                          <div style={{ fontSize: 12, color: '#334155', background: '#ffffff', padding: '8px 10px', borderRadius: 6, border: '1px solid #dcfce7', marginTop: 4 }}>
                            <div><strong>Main Swatch Photo:</strong> {opp.proposedSeo.imageAltTags.mainMediaAlt}</div>
                            <div style={{ marginTop: 4 }}><strong>Lookbook Model Photo:</strong> {opp.proposedSeo.imageAltTags.lookbookMediaAlt}</div>
                          </div>
                        </div>
                      )}

                      {opp.proposedSeo?.enhancedDescriptionHtml && (
                        <div style={{ marginTop: 14, borderTop: '1px dashed #86efac', paddingTop: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div className="diff-field-label" style={{ margin: 0 }}>
                              📖 Standardized Harmonized Product Description:
                            </div>
                            <button
                              style={{
                                background: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: 4,
                                padding: '2px 8px',
                                fontSize: 11,
                                cursor: 'pointer',
                                color: '#334155',
                                fontWeight: 600,
                              }}
                              onClick={() => setRawProductHtmlMap((prev) => ({ ...prev, [opp.id]: !prev[opp.id] }))}
                            >
                              {rawProductHtmlMap[opp.id] ? '👁️ View Rendered' : '<> View Raw HTML'}
                            </button>
                          </div>

                          {rawProductHtmlMap[opp.id] ? (
                            <pre style={{
                              background: '#0f172a',
                              color: '#f8fafc',
                              padding: 10,
                              borderRadius: 6,
                              fontSize: 11,
                              overflowX: 'auto',
                              whiteSpace: 'pre-wrap',
                              margin: 0
                            }}>
                              {opp.proposedSeo.enhancedDescriptionHtml}
                            </pre>
                          ) : (
                            <div
                              className="collection-html-preview-rendered"
                              style={{ background: '#ffffff', border: '1px solid #dcfce7', padding: 12, fontSize: 12 }}
                              dangerouslySetInnerHTML={{ __html: opp.proposedSeo.enhancedDescriptionHtml }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>


                  {/* Step-by-Step Agent Pipeline Inspector */}
                  <div style={{ marginTop: 12 }}>
                    <button
                      onClick={() => toggleTrace(opp.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2563eb',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13,
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      {isTraceOpen ? '▼ Hide Step-by-Step Agent Details' : '▶ 🔍 View What Each Agent Did Step-by-Step'}
                    </button>

                    {isTraceOpen && trace && (
                      <div className="agent-trace-box">
                        <div className="trace-step">
                          <span className="trace-step-num">1</span>
                          <div>
                            <strong>Agent 1: Data Collector ({trace.agent1_dataCollector?.source})</strong>
                            <div className="trace-detail">
                              Pulled query: <code>"{trace.agent1_dataCollector?.query}"</code> • Impressions: {trace.agent1_dataCollector?.impressions} • Avg Position: {trace.agent1_dataCollector?.avgPosition} • Clicks: {trace.agent1_dataCollector?.clicks}
                            </div>
                          </div>
                        </div>

                        <div className="trace-step">
                          <span className="trace-step-num">2</span>
                          <div>
                            <strong>Agent 2: Keyword Intelligence & Query Triage</strong>
                            <div className="trace-detail">
                              Category: <strong>{trace.agent2_keywordIntelligence?.category || 'CORE_APPAREL'}</strong> • Detected Fabric: <strong>{trace.agent2_keywordIntelligence?.detectedFabricBase}</strong> • Pattern: <strong>{trace.agent2_keywordIntelligence?.detectedPattern}</strong> • Product Type: <code>{trace.agent2_keywordIntelligence?.targetProductType || 'running_fabric'}</code> • Opportunity Score: {trace.agent2_keywordIntelligence?.opportunityScore}
                            </div>
                          </div>
                        </div>

                        <div className="trace-step">
                          <span className="trace-step-num">3</span>
                          <div>
                            <strong>Agent 3: Deterministic Catalog Matcher (Cluster Selection)</strong>
                            <div className="trace-detail">
                              Scanned {trace.agent3_catalogMatcher?.scannedProductsCount} active fabrics • Found {trace.agent3_catalogMatcher?.matchingCandidatesCount} matches • Cluster Rank: <strong>{trace.agent3_catalogMatcher?.clusterRank}</strong> • Resolved Spec: <strong>{trace.agent3_catalogMatcher?.resolvedFabricSpec || 'N/A'}</strong> • Selected SKU: <strong>{trace.agent3_catalogMatcher?.selectedProductTitle}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="trace-step">
                          <span className="trace-step-num">4</span>
                          <div>
                            <strong>Agent 4: AI Copywriter (Gemini 2.5 Flash)</strong>
                            <div className="trace-detail">
                              Branding: <strong>{trace.agent4_aiCopywriter?.branding || 'FAB VOGUE Studio'}</strong> • Generated {trace.agent4_aiCopywriter?.variantsGenerated} candidate SEO titles & descriptions • Smart-Merged {trace.agent4_aiCopywriter?.faqsGenerated || opp.proposedSeo?.faqs?.length} fabric FAQs
                            </div>
                          </div>
                        </div>

                        <div className="trace-step">
                          <span className="trace-step-num">5</span>
                          <div>
                            <strong>Agent 5: Deterministic SEO QA Guardrails</strong>
                            <div className="trace-detail">
                              Title Length: {trace.agent5_seoQaGuardrails?.titleLength} chars ({trace.agent5_seoQaGuardrails?.titlePassed ? 'Passed' : 'Warning'}) • Meta Desc: {trace.agent5_seoQaGuardrails?.descriptionLength} chars ({trace.agent5_seoQaGuardrails?.descriptionPassed ? 'Passed' : 'Warning'}) • Brand Suffix: Verified • Fabric Consistency: {trace.agent5_seoQaGuardrails?.fabricConsistency ? 'Verified' : 'Check'} • Overall: <span style={{ color: '#16a34a', fontWeight: 700 }}>PASSED</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="opp-card-actions">
                    {isQueued ? (
                      <>
                        <button className="btn-unqueue" onClick={() => handleUnqueue(opp.id, 'product')}>
                          ↩ Un-queue (Move to Draft)
                        </button>
                        <button className="btn-approve" style={{ background: '#059669' }} onClick={() => handleApprove(opp.id)}>
                          ⚡ Force Sync to Shopify Now
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn-reject" onClick={() => handleReject(opp.id)}>
                          Dismiss / Release Product
                        </button>
                        <button className="btn-queue-monday" onClick={() => handleApproveToQueue(opp.id, 'product')}>
                          🕒 Approve (Queue for Monday)
                        </button>
                        <button className="btn-approve" style={{ background: '#059669' }} onClick={() => handleApprove(opp.id)}>
                          ⚡ Sync Immediately
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
        );
      })()}

      {/* TAB 2: COLLECTION OPTIMIZER */}
      {activeTab === 'collections' && (
        <div className="collections-container">
          {/* High-Level Stats */}
          <div className="seo-stats-grid">
            <div className="seo-stat-card">
              <div className="seo-stat-label">Total Store Collections</div>
              <div className="seo-stat-value">{collectionsData?.totalCollections || 0}</div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">15+ SKU Eligible Hubs</div>
              <div className="seo-stat-value" style={{ color: '#16a34a' }}>
                {collectionsData?.eligibleCount || 0}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Collections In Review</div>
              <div className="seo-stat-value" style={{ color: pendingCollectionsCount > 0 ? '#ea580c' : '#0f172a' }}>
                {pendingCollectionsCount}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Thin Collections (&lt; 15 SKUs)</div>
              <div className="seo-stat-value" style={{ color: '#64748b' }}>
                {Math.max(0, (collectionsData?.totalCollections || 0) - (collectionsData?.eligibleCount || 0))}
              </div>
            </div>
          </div>

          {/* Collections Table Board */}
          <div className="queries-board-card">
            <div className="queries-board-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2>Shopify Collection Hubs & Category Merchandising</h2>
                <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 13 }}>
                  Optimize broad fabric category pages (e.g. <code>/collections/organza-fabric</code>) with high-converting SEO titles, 150-word HTML styling guides, and collection FAQs.
                </p>
              </div>
              <button
                className="btn-approve"
                style={{ background: '#16a34a', padding: '8px 14px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                onClick={() => {
                  setShowCreateSmartModal(true);
                  setSmartPreviewDraft(null);
                  setSmartError(null);
                  setSmartSuccessMessage(null);
                }}
              >
                ✨ + Create New Smart Collection
              </button>
            </div>

            {loadingCollections && <p>Loading Shopify collections and Search Console metrics...</p>}

            {!loadingCollections && (!collectionsData?.collections || collectionsData.collections.length === 0) && (
              <div style={{ background: '#f8fafc', padding: 24, borderRadius: 8, textAlign: 'center', color: '#64748b' }}>
                No collections found. Click <strong>"⟳ Refresh Collections"</strong> to load category hubs from Shopify.
              </div>
            )}

            {collectionsData?.collections && collectionsData.collections.length > 0 && (
              <div className="queries-table-container">
                <table className="queries-table">
                  <thead>
                    <tr>
                      <th style={{ width: '26%' }}>Collection Hub</th>
                      <th style={{ width: '13%' }}>Active SKUs</th>
                      <th style={{ width: '13%' }}>GSC Traffic</th>
                      <th style={{ width: '26%' }}>Current SEO Status</th>
                      <th style={{ width: '22%' }}>Action & Proposal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collectionsData.collections.map((col) => {
                      const isRunning = runningCollectionId === col.numericId;
                      const opp = col.opportunity;
                      const hasReview = opp && opp.status === 'READY_FOR_REVIEW';
                      const isQueued = opp && opp.status === 'QUEUED_FOR_RELEASE';
                      const isSynced = opp && opp.status === 'SYNCED';
                      const isExpanded = expandedCollectionIds.has(col.numericId);
                      const isRawHtml = Boolean(rawHtmlViewMap[col.numericId]);

                      return (
                        <React.Fragment key={col.collectionId}>
                          <tr className={isExpanded ? 'collection-row-selected' : ''}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: 14, color: '#0f172a' }}>{col.title}</strong>
                                {hasReview && (
                                  <span style={{ background: '#ecfdf5', color: '#166534', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>
                                    ✨ Proposal Ready
                                  </span>
                                )}
                                {isQueued && (
                                  <span style={{ background: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>
                                    🕒 Queued for Monday
                                  </span>
                                )}
                                {isSynced && (
                                  <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                                    ✅ Synced
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                                <a href={col.url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                  /{col.handle} ↗
                                </a>
                              </div>
                            </td>
                            <td>
                              <span style={{
                                background: col.isThinCollection ? '#fef3c7' : '#ecfdf5',
                                color: col.isThinCollection ? '#92400e' : '#047857',
                                padding: '3px 8px',
                                borderRadius: 9999,
                                fontSize: 12,
                                fontWeight: 600,
                              }}>
                                {col.skuCount} SKUs {col.isThinCollection ? '(Thin)' : '✅'}
                              </span>
                            </td>
                            <td>
                              <div><strong>{col.gscMetrics.impressions}</strong> Impr</div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>Pos {col.gscMetrics.position}</div>
                            </td>
                            <td>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                                {col.currentSeoTitle || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Default Collection Title</span>}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                {col.currentSeoDescription ? (
                                  col.currentSeoDescription.length > 70
                                    ? `${col.currentSeoDescription.slice(0, 70)}...`
                                    : col.currentSeoDescription
                                ) : (
                                  <span style={{ color: '#ea580c', fontStyle: 'italic' }}>Missing meta description</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                {(hasReview || isQueued || isSynced) ? (
                                  <>
                                    <button
                                      className="btn-primary-seo"
                                      style={{
                                        background: isExpanded ? '#0f172a' : (hasReview ? '#16a34a' : (isQueued ? '#6366f1' : '#f1f5f9')),
                                        color: (isExpanded || hasReview || isQueued) ? '#ffffff' : '#334155',
                                        border: '1px solid ' + (isExpanded ? '#0f172a' : (hasReview ? '#16a34a' : (isQueued ? '#6366f1' : '#cbd5e1'))),
                                        padding: '5px 10px',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => toggleCollectionAccordion(col.numericId)}
                                    >
                                      {isExpanded ? '▲ Collapse' : (hasReview || isQueued ? '👁️ View Proposal' : '👁️ View SEO')}
                                    </button>
                                    {hasReview && (
                                      <>
                                        <button
                                          className="btn-queue-monday"
                                          style={{ padding: '5px 9px', fontSize: 12 }}
                                          onClick={() => handleApproveToQueue(opp.id, 'collection')}
                                          title="Stage for Monday 01:00 AM IST release"
                                        >
                                          🕒 Queue
                                        </button>
                                        <button
                                          className="btn-approve"
                                          style={{ padding: '5px 9px', fontSize: 12, background: '#059669' }}
                                          onClick={() => handleApproveCollection(opp.id, col.title)}
                                          title="Sync to Shopify immediately"
                                        >
                                          ⚡ Sync
                                        </button>
                                        <button
                                          className="btn-run-query"
                                          style={{ padding: '5px 9px', fontSize: 12, background: '#ffffff', color: '#7c3aed', border: '1px solid #7c3aed' }}
                                          onClick={() => handleOpenRegenerateModal(col)}
                                          disabled={Boolean(runningCollectionId)}
                                          title="Refine & Re-Generate AI Proposal with feedback"
                                        >
                                          ↻ Re-Gen
                                        </button>
                                      </>
                                    )}
                                    {isQueued && (
                                      <>
                                        <button
                                          className="btn-unqueue"
                                          style={{ padding: '5px 9px', fontSize: 12 }}
                                          onClick={() => handleUnqueue(opp.id, 'collection')}
                                        >
                                          ↩ Un-queue
                                        </button>
                                        <button
                                          className="btn-approve"
                                          style={{ padding: '5px 9px', fontSize: 12, background: '#059669' }}
                                          onClick={() => handleApproveCollection(opp.id, col.title)}
                                        >
                                          ⚡ Sync Now
                                        </button>
                                        <button
                                          className="btn-run-query"
                                          style={{ padding: '5px 9px', fontSize: 12, background: '#ffffff', color: '#7c3aed', border: '1px solid #7c3aed' }}
                                          onClick={() => handleOpenRegenerateModal(col)}
                                          disabled={Boolean(runningCollectionId)}
                                          title="Refine & Re-Generate AI Proposal with feedback"
                                        >
                                          ↻ Re-Gen
                                        </button>
                                      </>
                                    )}
                                  </>
                                ) : col.isThinCollection ? (
                                  <span style={{ color: '#94a3b8', fontSize: 11 }}>
                                    ⚠️ Add &ge;15 SKUs
                                  </span>
                                ) : (
                                  <button
                                    className="btn-run-query"
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                    onClick={() => handleGenerateCollection(col)}
                                    disabled={Boolean(runningCollectionId)}
                                  >
                                    {isRunning ? 'Generating...' : '⚡ Generate AI SEO'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Accordion Detail Row */}
                          {isExpanded && (
                            <tr className="collection-accordion-row">
                              <td colSpan={5}>
                                <div className="collection-accordion-card">
                                  {/* Accordion Header */}
                                  <div className="accordion-header-bar">
                                    <div>
                                      <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>
                                        📁 {col.title} — {hasReview || isQueued ? 'AI Merchandising & SEO Proposal' : 'Collection SEO Overview'}
                                      </h3>
                                      <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>
                                        Shopify Collection URL: <code>https://fabvoguestudio.com/collections/{col.handle}</code> • {col.skuCount} active SKUs
                                      </p>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                      {hasReview && (
                                        <>
                                          <button
                                            className="btn-queue-monday"
                                            style={{ padding: '7px 14px', fontSize: 13 }}
                                            onClick={() => handleApproveToQueue(opp.id, 'collection')}
                                          >
                                            🕒 Approve (Queue for Monday)
                                          </button>
                                          <button
                                            className="btn-approve"
                                            style={{ padding: '7px 14px', fontSize: 13, background: '#059669' }}
                                            onClick={() => handleApproveCollection(opp.id, col.title)}
                                          >
                                            ⚡ Sync Immediately
                                          </button>
                                          <button
                                            className="btn-reject"
                                            style={{ padding: '7px 12px', fontSize: 13 }}
                                            onClick={() => handleRejectCollection(opp.id)}
                                          >
                                            ✕ Dismiss Draft
                                          </button>
                                        </>
                                      )}
                                      {isQueued && (
                                        <>
                                          <button
                                            className="btn-unqueue"
                                            style={{ padding: '7px 14px', fontSize: 13 }}
                                            onClick={() => handleUnqueue(opp.id, 'collection')}
                                          >
                                            ↩ Un-queue (Return to Draft)
                                          </button>
                                          <button
                                            className="btn-approve"
                                            style={{ padding: '7px 14px', fontSize: 13, background: '#059669' }}
                                            onClick={() => handleApproveCollection(opp.id, col.title)}
                                          >
                                            ⚡ Force Sync to Shopify Now
                                          </button>
                                        </>
                                      )}
                                      {!col.isThinCollection && (
                                        <button
                                          className="btn-run-query"
                                          style={{
                                            background: (hasReview || isQueued) ? '#ffffff' : '#7c3aed',
                                            color: (hasReview || isQueued) ? '#7c3aed' : '#ffffff',
                                            border: '1px solid #7c3aed',
                                            padding: '7px 14px',
                                            fontSize: 12,
                                          }}
                                          onClick={() => {
                                            if (hasReview || isQueued) {
                                              handleOpenRegenerateModal(col);
                                            } else {
                                              handleGenerateCollection(col);
                                            }
                                          }}
                                          disabled={Boolean(runningCollectionId)}
                                        >
                                          {isRunning ? 'Generating AI SEO...' : (hasReview || isQueued ? '↻ Re-Generate AI' : '⚡ Generate AI SEO')}
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {/* Merchandiser Feedback Banner if regenerated */}
                                  {opp?.lastRegenerateReason && (
                                    <div style={{
                                      background: '#f5f3ff',
                                      border: '1px solid #ddd6fe',
                                      borderRadius: 8,
                                      padding: '10px 14px',
                                      marginBottom: 14,
                                      fontSize: 12,
                                      color: '#6d28d9',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 10,
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 16 }}>💡</span>
                                        <div>
                                          <strong>Custom Revision Feedback Applied:</strong>
                                          <span style={{ marginLeft: 6, color: '#4c1d95', fontStyle: 'italic' }}>"{opp.lastRegenerateReason}"</span>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenRegenerateModal(col)}
                                        style={{
                                          background: '#7c3aed',
                                          color: '#ffffff',
                                          border: 'none',
                                          borderRadius: 4,
                                          padding: '4px 10px',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        Refine Again ↻
                                      </button>
                                    </div>
                                  )}

                                  {/* Side-by-Side SEO Comparison */}
                                  <div className="seo-diff-grid" style={{ marginBottom: 16 }}>
                                    {/* Current SEO Card */}
                                    <div className="diff-col" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <h4 style={{ margin: 0, color: '#64748b', fontSize: 12 }}>CURRENT SHOPIFY SEO</h4>
                                        <span style={{ fontSize: 11, color: '#94a3b8' }}>Original Snapshot</span>
                                      </div>
                                      <div className="diff-field-label">Meta Title:</div>
                                      <div className="diff-content" style={{ fontWeight: 500, color: '#334155' }}>
                                        {col.currentSeoTitle || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Default Collection Title</span>}
                                      </div>
                                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                        Length: {(col.currentSeoTitle || '').length} chars
                                      </div>

                                      <div className="diff-field-label" style={{ marginTop: 12 }}>Meta Description:</div>
                                      <div className="diff-content" style={{ color: col.currentSeoDescription ? '#475569' : '#ea580c', fontStyle: col.currentSeoDescription ? 'normal' : 'italic' }}>
                                        {col.currentSeoDescription || 'Missing meta description in Shopify'}
                                      </div>
                                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                        Length: {(col.currentSeoDescription || '').length} chars
                                      </div>
                                    </div>

                                    {/* Proposed SEO Card */}
                                    <div className="diff-col" style={{ background: '#ffffff', border: '1.5px solid #86efac', borderRadius: 8, padding: 14 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <h4 style={{ margin: 0, color: '#15803d', fontSize: 12 }}>✨ PROPOSED AI SEO (OPTIMIZED)</h4>
                                        <span style={{ background: '#ecfdf5', color: '#166534', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 9999 }}>
                                          {isSynced ? 'Synced & Active' : 'Ready to Sync'}
                                        </span>
                                      </div>
                                      <div className="diff-field-label">Meta Title (Target 45-60 Chars):</div>
                                      <div className="diff-content" style={{ fontWeight: 700, color: '#0f172a' }}>
                                        {opp?.proposedSeo?.title || 'N/A'}
                                      </div>
                                      <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2, fontWeight: 600 }}>
                                        Length: {(opp?.proposedSeo?.title || '').length} chars ✅
                                      </div>

                                      <div className="diff-field-label" style={{ marginTop: 12 }}>Meta Description (Target 125-155 Chars):</div>
                                      <div className="diff-content" style={{ color: '#1e293b' }}>
                                        {opp?.proposedSeo?.description || 'N/A'}
                                      </div>
                                      <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2, fontWeight: 600 }}>
                                        Length: {(opp?.proposedSeo?.description || '').length} chars ✅
                                      </div>
                                    </div>
                                  </div>

                                  {/* Rich 150-Word HTML Styling Guide */}
                                  {opp?.proposedSeo?.htmlDescription && (
                                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                                        <div>
                                          <h4 style={{ margin: 0, fontSize: 13, color: '#0f172a', fontWeight: 700 }}>
                                            📖 150-Word HTML Styling & Fabric Guide
                                          </h4>
                                          <span style={{ fontSize: 12, color: '#64748b' }}>
                                            Injected into Collection description with GSM feel, styling tips & wash care.
                                          </span>
                                        </div>
                                        <button
                                          style={{
                                            background: '#f1f5f9',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: 4,
                                            padding: '4px 10px',
                                            fontSize: 11,
                                            cursor: 'pointer',
                                            color: '#334155',
                                            fontWeight: 600,
                                          }}
                                          onClick={() => setRawHtmlViewMap((prev) => ({ ...prev, [col.numericId]: !prev[col.numericId] }))}
                                        >
                                          {isRawHtml ? '👁️ View Rendered' : '<> View Raw HTML'}
                                        </button>
                                      </div>

                                      {isRawHtml ? (
                                        <pre style={{
                                          background: '#0f172a',
                                          color: '#f8fafc',
                                          padding: 12,
                                          borderRadius: 6,
                                          fontSize: 12,
                                          overflowX: 'auto',
                                          whiteSpace: 'pre-wrap',
                                          margin: 0
                                        }}>
                                          {opp.proposedSeo.htmlDescription}
                                        </pre>
                                      ) : (
                                        <div
                                          className="collection-html-preview-rendered"
                                          dangerouslySetInnerHTML={{ __html: opp.proposedSeo.htmlDescription }}
                                        />
                                      )}
                                    </div>
                                  )}

                                  {/* Category FAQs */}
                                  {opp?.proposedSeo?.faqs && opp.proposedSeo.faqs.length > 0 && (
                                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
                                      <div style={{ marginBottom: 10, borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                                        <h4 style={{ margin: 0, fontSize: 13, color: '#0f172a', fontWeight: 700 }}>
                                          ❓ Category FAQs ({opp.proposedSeo.faqs.length} Structured Q&As)
                                        </h4>
                                        <span style={{ fontSize: 12, color: '#64748b' }}>
                                          Synced to <code>custom.collection_faqs</code> metafield for Google FAQ schema rich snippets.
                                        </span>
                                      </div>
                                      <div className="faqs-list">
                                        {opp.proposedSeo.faqs.map((f, fIdx) => (
                                          <div key={fIdx} className="faq-item" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 12px' }}>
                                            <div className="faq-q" style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                                              Q: {f.question}
                                            </div>
                                            <div className="faq-a" style={{ marginTop: 4, color: '#475569', fontSize: 12 }}>
                                              A: {f.answer}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        </div>
      )}

      {/* TAB: HOMEPAGE SEO STUDIO */}
      {activeTab === 'homepage' && (
        <div className="homepage-seo-container" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Header Card */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                    🏠 Homepage SEO Studio & Brand Authority Hub
                  </h2>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: 9999,
                    fontSize: 12,
                    fontWeight: 700,
                    background: homepageData?.status === 'SYNCED_TO_SHOPIFY' ? '#ecfdf5' : (homepageData?.status === 'READY_FOR_REVIEW' ? '#fef3c7' : '#f1f5f9'),
                    color: homepageData?.status === 'SYNCED_TO_SHOPIFY' ? '#166534' : (homepageData?.status === 'READY_FOR_REVIEW' ? '#b45309' : '#475569'),
                  }}>
                    {homepageData?.status === 'SYNCED_TO_SHOPIFY' ? '✅ Synced & Active in Shopify' : (homepageData?.status === 'READY_FOR_REVIEW' ? '✨ AI Proposal Ready for Review' : '⚪ Needs Generation')}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                  Anchor domain authority, brand positioning, and Google Rich Snippet FAQ schema for <code>https://fabvoguestudio.com</code>
                </p>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {homepageData?.proposedSeo && (
                  <>
                    <button
                      className="btn-approve"
                      style={{ padding: '8px 16px', fontSize: 13, background: '#059669', color: '#ffffff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}
                      onClick={handleApproveHomepage}
                      disabled={syncingHomepage}
                    >
                      {syncingHomepage ? '⏳ Syncing to Shopify...' : '⚡ Sync to Shopify Immediately'}
                    </button>
                    <button
                      className="btn-reject"
                      style={{ padding: '8px 12px', fontSize: 13 }}
                      onClick={handleDismissHomepage}
                    >
                      ✕ Dismiss Draft
                    </button>
                  </>
                )}

                <button
                  className="btn-run-query"
                  style={{
                    background: homepageData?.proposedSeo ? '#ffffff' : '#7c3aed',
                    color: homepageData?.proposedSeo ? '#7c3aed' : '#ffffff',
                    border: '1px solid #7c3aed',
                    padding: '8px 16px',
                    fontSize: 13,
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (homepageData?.proposedSeo) {
                      handleOpenHomepageRegenerateModal();
                    } else {
                      handleGenerateHomepage(false);
                    }
                  }}
                  disabled={generatingHomepage}
                >
                  {generatingHomepage ? '⏳ Generating Proposal...' : (homepageData?.proposedSeo ? '↻ Refine & Re-Generate AI' : '⚡ Generate AI Proposal')}
                </button>
              </div>
            </div>
          </div>

          {/* Active Merchandiser Directive Banner */}
          {homepageData?.lastRegenerateReason && (
            <div style={{
              background: '#f5f3ff',
              border: '1px solid #ddd6fe',
              borderRadius: 8,
              padding: '12px 16px',
              fontSize: 13,
              color: '#6d28d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>💡</span>
                <div>
                  <strong>Custom Directive Applied:</strong>
                  <span style={{ marginLeft: 6, color: '#4c1d95', fontStyle: 'italic' }}>"{homepageData.lastRegenerateReason}"</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleOpenHomepageRegenerateModal}
                style={{
                  background: '#7c3aed',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '4px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Refine Again ↻
              </button>
            </div>
          )}

          {/* Side-by-Side SEO Comparison Grid */}
          <div className="seo-diff-grid" style={{ marginBottom: 0 }}>
            {/* Current Live Shopify SEO */}
            <div className="diff-col" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, color: '#64748b', fontSize: 13, fontWeight: 700 }}>CURRENT LIVE SHOPIFY SEO</h4>
                <span style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc', padding: '2px 8px', borderRadius: 4 }}>
                  Online Store Preferences
                </span>
              </div>

              <div className="diff-field-label">Homepage Meta Title:</div>
              <div className="diff-content" style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
                {homepageData?.currentSeo?.title || 'FAB VOGUE Studio'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Length: {homepageData?.currentSeo?.titleLength || 0} chars
              </div>

              <div className="diff-field-label" style={{ marginTop: 16 }}>Homepage Meta Description:</div>
              <div className="diff-content" style={{
                color: homepageData?.currentSeo?.isDescriptionTooLong ? '#b91c1c' : '#334155',
                fontSize: 13,
                lineHeight: 1.5,
              }}>
                {homepageData?.currentSeo?.description || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No meta description configured</span>}
              </div>
              <div style={{ fontSize: 12, color: homepageData?.currentSeo?.isDescriptionTooLong ? '#b91c1c' : '#64748b', marginTop: 4, fontWeight: homepageData?.currentSeo?.isDescriptionTooLong ? 600 : 400 }}>
                Length: {homepageData?.currentSeo?.descriptionLength || 0} chars {homepageData?.currentSeo?.isDescriptionTooLong && '⚠️ Exceeds 160 chars (Truncated on Google Search)'}
              </div>
            </div>

            {/* Proposed AI Homepage SEO */}
            <div className="diff-col" style={{ background: '#ffffff', border: '1.5px solid #86efac', borderRadius: 10, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, color: '#15803d', fontSize: 13, fontWeight: 700 }}>✨ PROPOSED AI HOMEPAGE SEO</h4>
                <span style={{ background: '#ecfdf5', color: '#166534', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 9999 }}>
                  {homepageData?.status === 'SYNCED_TO_SHOPIFY' ? 'Active in Store' : 'Optimized Draft'}
                </span>
              </div>

              <div className="diff-field-label">Meta Title (Target 45-60 Chars):</div>
              <div className="diff-content" style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                {homepageData?.proposedSeo?.title || (loadingHomepage ? 'Loading...' : 'Click "Generate AI Proposal" to create')}
              </div>
              {homepageData?.proposedSeo?.title && (
                <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4, fontWeight: 600 }}>
                  Length: {homepageData.proposedSeo.title.length} chars ✅ Optimal
                </div>
              )}

              <div className="diff-field-label" style={{ marginTop: 16 }}>Meta Description (Target 125-155 Chars):</div>
              <div className="diff-content" style={{ color: '#0f172a', fontSize: 13, lineHeight: 1.5 }}>
                {homepageData?.proposedSeo?.description || 'N/A'}
              </div>
              {homepageData?.proposedSeo?.description && (
                <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4, fontWeight: 600 }}>
                  Length: {homepageData.proposedSeo.description.length} chars ✅ Strictly &le;155 chars
                </div>
              )}
            </div>
          </div>

          {/* 200-Word Semantic Brand Story & Surat Authority Overview */}
          {homepageData?.proposedSeo?.brandStoryHtml && (
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                    📖 200-Word Semantic Brand Story & Surat Manufacturing Authority
                  </h3>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Injected into Homepage content via <code>custom.brand_story_html</code> for Google E-E-A-T and buyer confidence.
                  </span>
                </div>
                <button
                  type="button"
                  style={{
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: 4,
                    padding: '4px 12px',
                    fontSize: 12,
                    cursor: 'pointer',
                    color: '#334155',
                    fontWeight: 600,
                  }}
                  onClick={() => setRawBrandStoryView((prev) => !prev)}
                >
                  {rawBrandStoryView ? '👁️ View Rendered' : '<> View Raw HTML'}
                </button>
              </div>

              {rawBrandStoryView ? (
                <pre style={{
                  background: '#0f172a',
                  color: '#f8fafc',
                  padding: 14,
                  borderRadius: 8,
                  fontSize: 12,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                }}>
                  {homepageData.proposedSeo.brandStoryHtml}
                </pre>
              ) : (
                <div
                  className="collection-html-preview-rendered"
                  style={{ background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  dangerouslySetInnerHTML={{ __html: homepageData.proposedSeo.brandStoryHtml }}
                />
              )}
            </div>
          )}

          {/* Brand Trust FAQs */}
          {homepageData?.proposedSeo?.faqs && homepageData.proposedSeo.faqs.length > 0 && (
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20 }}>
              <div style={{ marginBottom: 14, borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                  ❓ Homepage Brand Trust FAQs ({homepageData.proposedSeo.faqs.length} Structured Q&As)
                </h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Synced to <code>custom.homepage_faqs</code> metafield & feeds into Google FAQ rich snippet schema.
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {homepageData.proposedSeo.faqs.map((faq, idx) => (
                  <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                      Q: {faq.question}
                    </div>
                    <div style={{ marginTop: 4, color: '#475569', fontSize: 13, lineHeight: 1.4 }}>
                      A: {faq.answer}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Schema.org JSON-LD Structured Data Viewer */}
          {homepageData?.proposedSeo?.structuredDataJson && (
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                    🔍 Schema.org JSON-LD (Organization, WebSite & FAQPage)
                  </h3>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Structured data graph providing Google Knowledge Graph, Sitelinks SearchBox, and Rich Snippets.
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      borderRadius: 4,
                      padding: '4px 12px',
                      fontSize: 12,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                    onClick={() => setShowHomepageSchemaView((prev) => !prev)}
                  >
                    {showHomepageSchemaView ? 'Hide Schema Code' : '👁️ View JSON-LD'}
                  </button>
                  <button
                    type="button"
                    style={{
                      background: copiedSchema ? '#16a34a' : '#0f172a',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 4,
                      padding: '4px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(homepageData.proposedSeo.structuredDataJson);
                      setCopiedSchema(true);
                      setTimeout(() => setCopiedSchema(false), 2000);
                    }}
                  >
                    {copiedSchema ? '✓ Copied!' : '📋 Copy JSON-LD'}
                  </button>
                </div>
              </div>

              {showHomepageSchemaView && (
                <pre style={{
                  background: '#0f172a',
                  color: '#38bdf8',
                  padding: 14,
                  borderRadius: 8,
                  fontSize: 12,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                }}>
                  {homepageData.proposedSeo.structuredDataJson}
                </pre>
              )}
            </div>
          )}

          {/* Theme Developer Liquid Integration Spec Box */}
          <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowHomepageThemeSpec((prev) => !prev)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>💻</span>
                <strong style={{ fontSize: 13, color: '#334155' }}>Shopify Theme Liquid Integration Guide (For Theme Agent)</strong>
              </div>
              <span style={{ fontSize: 12, color: '#64748b' }}>{showHomepageThemeSpec ? '▲ Collapse' : '▼ Expand'}</span>
            </div>
            {showHomepageThemeSpec && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                <p style={{ margin: '0 0 8px' }}>
                  When approved, metadata and FAQs are stored in <code>shop.metafields.custom.*</code>. To render the structured data and FAQ accordion in your Shopify theme:
                </p>
                <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 6, overflowX: 'auto' }}>
{`<!-- 1. Inject JSON-LD in theme.liquid <head> -->
{% if template == 'index' and shop.metafields.custom.homepage_schema_json != blank %}
  <script type="application/ld+json">
    {{ shop.metafields.custom.homepage_schema_json }}
  </script>
{% endif %}

<!-- 2. Render Semantic Brand Story in sections/homepage-brand-story.liquid -->
{% if shop.metafields.custom.brand_story_html != blank %}
  <div class="homepage-brand-story-section">
    {{ shop.metafields.custom.brand_story_html }}
  </div>
{% endif %}`}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: RANKING & PERFORMANCE TRACKER */}
      {activeTab === 'tracker' && (
        <div className="tracker-container">
          {/* Tracker High-Level Summary Cards */}
          <div className="seo-stats-grid">
            <div className="seo-stat-card">
              <div className="seo-stat-label">Tracked SEO Pages</div>
              <div className="seo-stat-value" style={{ color: '#2563eb' }}>
                {trackerData?.totalOptimized || 0}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Active in Live GSC</div>
              <div className="seo-stat-value" style={{ color: '#059669' }}>
                {trackerData?.liveTrackingCount || 0} <span style={{ fontSize: 13, color: '#64748b' }}>({trackerData?.indexingCount || 0} Indexing)</span>
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Avg Measured Position Gain</div>
              <div className="seo-stat-value" style={{ color: '#16a34a' }}>
                {trackerData?.comparableCount > 0
                  ? `${trackerData.averagePositionGain > 0 ? '+' : ''}${trackerData.averagePositionGain} Pos`
                  : '—'}
              </div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Top 3 Winners 🏆</div>
              <div className="seo-stat-value" style={{ color: '#ea580c' }}>
                {trackerData?.topPerformersCount || 0}
              </div>
            </div>
          </div>

          {trackerData?.gscCoverage?.note && (
            <div role="status" style={{ margin: '0 0 14px', padding: '10px 14px', borderRadius: 6, background: trackerData.gscCoverage.withinConfiguredLimit ? '#f0fdf4' : '#fff7ed', color: trackerData.gscCoverage.withinConfiguredLimit ? '#166534' : '#9a3412', fontSize: 12 }}>
              {trackerData.gscCoverage.note}
            </div>
          )}

          {/* 90-DAY LIFECYCLE AUTO-PILOT MATRIX BANNER */}
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
            color: '#ffffff',
            borderRadius: 12,
            padding: '18px 24px',
            marginBottom: 24,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}>
            <div style={{ maxWidth: 720 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255, 255, 255, 0.15)', padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, marginBottom: 8, letterSpacing: '0.5px' }}>
                🛡️ 90-DAY LIFECYCLE & WINNER PROTECTION ENGINE
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px', color: '#ffffff' }}>
                Automated Post-Cooldown Audit & Tiered Pivot Strategy
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: '#c7d2fe', lineHeight: 1.5 }}>
                • <strong>🏆 Page 1 Winners (Pos 1–10)</strong>: Auto-renew +90d lock (Never overwrite).<br />
                • <strong>🎯 Striking Distance (Pos 11–25)</strong>: Extend +60d with internal blog/collection link priority.<br />
                • <strong>📈 Growth Probation (Pos 26–50 & Impr &ge; 150)</strong>: Extend +30d to monitor climb.<br />
                • <strong>🔄 Sustained low traction</strong>: Refresh recommendation only after exact page/query evidence; weak or missing data stays inconclusive and protected.
              </p>
            </div>
            <div>
              <button
                type="button"
                className="btn-primary-seo"
                onClick={handleRunLifecycleEvaluation}
                disabled={evaluatingLifecycle}
                style={{
                  background: '#6366f1',
                  borderColor: '#4f46e5',
                  padding: '10px 20px',
                  fontSize: 13,
                  fontWeight: 700,
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.2)',
                }}
              >
                {evaluatingLifecycle ? '🛡️ Auditing Lifecycle...' : '🛡️ Run Lifecycle Audit'}
              </button>
            </div>
          </div>

          <div className="queries-board-card">
            <div className="queries-board-header">
              <div>
                <h2>Google Search Console 90-Day Ranking Averages</h2>
                <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 13 }}>
                  Exact query-to-page visibility. Weekly snapshots refresh Monday; post-change gains wait for a complete 90-day window and GSC reporting delay.
                </p>
              </div>
            </div>

            {loadingTracker && <p>Loading live GSC ranking data...</p>}

            {!loadingTracker && (!trackerData?.items || trackerData.items.length === 0) && (
              <div style={{ background: '#f8fafc', padding: 32, borderRadius: 8, textAlign: 'center', color: '#64748b' }}>
                No SEO pages are being tracked yet. Use the <strong>"🎯 Opportunity Engine"</strong> to optimize a product, collection, or blog.
              </div>
            )}

            {trackerData?.items && trackerData.items.length > 0 && (
              <div className="queries-table-container">
                <table className="queries-table">
                  <thead>
                    <tr>
                      <th>Page & Target Query</th>
                      <th>Lifecycle & Cooldown</th>
                      <th>Baseline (At Sync)</th>
                      <th>Current GSC Rank</th>
                      <th>Weekly GSC Trend</th>
                      <th>Rank Delta</th>
                      <th>Status & Decision</th>
                      <th>Rollback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackerData.items.map((item) => {
                      const posGain = item.gains.positionGain;
                      const hasLive = item.hasLiveData && item.current.position != null;
                      const isWinner = hasLive && item.current.position <= 3;
                      const isTop10 = hasLive && item.current.position <= 10;
                      const isRising = posGain != null && posGain >= 2;
                      const isDropped = posGain != null && posGain <= -2;
                      const lifecycleStatus = item.lifecycle?.status;

                      return (
                        <tr key={item.id}>
                          <td>
                            <strong style={{ fontSize: 14, color: '#0f172a' }}>{item.productTitle}</strong>
                            <div style={{ fontSize: 12, color: '#2563eb', marginTop: 2 }}>
                              Target: <strong>"{item.keyword}"</strong>
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              ID: <code>{item.productId}</code>
                            </div>
                          </td>
                          <td>
                            {item.entityType === 'BLOG' ? (
                              <div>
                                <span style={{ background: '#f3e8ff', color: '#6b21a8', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>📝 Blog measurement</span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Updated {item.daysSinceSync} days ago</div>
                              </div>
                            ) : lifecycleStatus === 'WINNER_PROTECTED' ? (
                              <div>
                                <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                                  🏆 +90d Winner Lock
                                </span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Day {item.daysSinceSync} • Protected</div>
                              </div>
                            ) : lifecycleStatus === 'STRIKING_DISTANCE_BOOST' ? (
                              <div>
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                                  🎯 +60d Priority Boost
                                </span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Day {item.daysSinceSync} • Page 2 Contender</div>
                              </div>
                            ) : lifecycleStatus === 'GROWTH_PROBATION' ? (
                              <div>
                                <span style={{ background: '#f3e8ff', color: '#6b21a8', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                                  📈 +30d Probation
                                </span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Day {item.daysSinceSync} • Growing Impr</div>
                              </div>
                            ) : lifecycleStatus === 'PIVOT_RECOMMENDED' ? (
                              <div>
                                <span style={{ background: '#ffedd5', color: '#c2410c', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                                  🔄 Pivot Available
                                </span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Day {item.daysSinceSync} • Unlocked</div>
                              </div>
                            ) : lifecycleStatus === 'REFRESH_RECOMMENDED' ? (
                              <div>
                                <span style={{ background: '#ffedd5', color: '#c2410c', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                                  🔄 Refresh Recommended
                                </span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Recheck in 30 days · current SEO protected</div>
                              </div>
                            ) : lifecycleStatus === 'MEASUREMENT_INCONCLUSIVE' ? (
                              <div>
                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                                  ⏸ Measurement Inconclusive
                                </span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Recheck in 30 days · no SEO change</div>
                              </div>
                            ) : (
                              <div>
                                <span style={{ fontWeight: 600, color: '#475569' }}>Day {item.daysSinceSync}</span>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>of 90-day cooldown</div>
                              </div>
                            )}
                          </td>
                          <td>
                            {item.baseline.isKnown && item.baseline.position != null ? (
                              <>
                                <div>Pos: <strong>{item.baseline.position}</strong></div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{item.baseline.impressions} Impr • {item.baseline.clicks} Clicks</div>
                                <div style={{ fontSize: 10, color: '#94a3b8' }}>{item.baseline.windowDays || 90}-day baseline{item.baseline.capturedAt ? ` · ${new Date(item.baseline.capturedAt).toLocaleDateString()}` : ''}</div>
                              </>
                            ) : (
                              <div>
                                <span style={{ color: '#475569', fontWeight: 600 }}>🎯 Fresh Trend</span>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>No prior ranking</div>
                              </div>
                            )}
                          </td>
                          <td>
                            {hasLive ? (
                              <>
                                <div style={{ fontSize: 14, fontWeight: 700, color: isWinner ? '#16a34a' : isTop10 ? '#2563eb' : '#0f172a' }}>
                                  Pos {item.current.position}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{item.current.impressions} Impr • {item.current.clicks} Clicks</div>
                              </>
                            ) : (
                              <div>
                                <span style={{ color: '#d97706', fontWeight: 600, fontSize: 12 }}>⏳ Indexing in Google</span>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>Awaiting first impressions</div>
                              </div>
                            )}
                          </td>
                          <td>{renderWeeklyGscTrend(item.history || [])}</td>
                          <td>
                            {posGain != null ? (
                              <span className="badge-score" style={{
                                background: posGain > 0 ? '#f0fdf4' : (posGain < 0 ? '#fef2f2' : '#f8fafc'),
                                color: posGain > 0 ? '#15803d' : (posGain < 0 ? '#dc2626' : '#64748b'),
                                fontSize: 13,
                              }}>
                                {posGain > 0 ? `▲ +${posGain}` : (posGain < 0 ? `▼ ${posGain}` : '0.0')}
                              </span>
                            ) : hasLive ? (
                              <span style={{ background: '#f0fdf4', color: '#15803d', padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                                🎯 Live: #{item.current.position}
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: 13 }}>—</span>
                            )}
                          </td>
                          <td>
                            {item.rankStatus === 'TOP_3_WINNER' || isWinner ? (
                              <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>
                                  🏆 Top 3 Average
                              </span>
                            ) : item.rankStatus === 'TOP_10' || isTop10 ? (
                              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '3px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                                🌟 Top 10 Ranked
                              </span>
                            ) : item.rankStatus === 'RISING_STAR' || isRising ? (
                              <span style={{ background: '#ecfdf5', color: '#047857', padding: '3px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                                🚀 Rising Star
                              </span>
                            ) : item.rankStatus === 'NEWLY_RANKED' ? (
                              <span style={{ background: '#f3e8ff', color: '#6b21a8', padding: '3px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                                ✨ Newly Ranked
                              </span>
                            ) : item.rankStatus === 'RANK_DROPPED' || isDropped ? (
                              <span style={{ background: '#fef2f2', color: '#dc2626', padding: '3px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                                📉 Rank Dropped
                              </span>
                            ) : item.rankStatus === 'INDEXING_PENDING' || !hasLive ? (
                              <span style={{ background: '#fffbeb', color: '#b45309', padding: '3px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                                ⏳ Indexing ({item.daysSinceSync}d)
                              </span>
                            ) : (
                              <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                                ❄️ Cooldown
                              </span>
                            )}
                            <div style={{ marginTop: 6 }}>
                              <strong style={{ fontSize: 11, color: item.performance?.outcome === 'IMPROVING' ? '#047857' : item.performance?.outcome === 'DECLINING' ? '#b91c1c' : item.performance?.outcome === 'UNCHANGED' ? '#475569' : '#b45309' }}>
                                {item.performance?.outcome || 'INCONCLUSIVE'}
                              </strong>
                              <div style={{ fontSize: 10, color: '#64748b', maxWidth: 260 }}>{item.performance?.reason || 'Insufficient evidence for a performance decision.'}</div>
                            </div>
                          </td>
                          <td>
                            {item.backupId && (
                              <button
                                className="btn-reject"
                                style={{ padding: '4px 10px', fontSize: 11 }}
                                onClick={() => handleRollback(item.backupId)}
                              >
                                ↺ Rollback
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB: COMPETITOR KEYWORD GAP HUNTER */}
      {activeTab === 'competitors' && (
        <div>
          {/* Header Card */}
          <div style={{
            background: 'linear-gradient(135deg, #450a0a 0%, #7f1d1d 50%, #991b1b 100%)',
            color: '#ffffff',
            borderRadius: 12,
            padding: '24px 28px',
            marginBottom: 24,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}>
            <div style={{ maxWidth: 720 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255, 255, 255, 0.15)', padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700, marginBottom: 10, letterSpacing: '0.5px' }}>
                ⚔️ COMPETITOR KEYWORD OPPORTUNITY SCAN
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px', color: '#ffffff' }}>
                Find Catalog-Aligned Keyword Opportunities
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: '#fecaca', lineHeight: 1.5 }}>
                Compare curated fabric keyword themes for Fabcurate, Printsbank, Fabriclore, SourceItRight, Fabric Pandit, and iTokri with eligible FabVogueStudio catalog products. Competitor rankings and search volumes are not verified in this data mode.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
              onClick={() => fetchCompetitorGaps()}
                disabled={loadingCompetitorGaps}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#ffffff',
                  color: '#991b1b',
                  padding: '12px 20px',
                  borderRadius: 8,
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: loadingCompetitorGaps ? 'not-allowed' : 'pointer',
                  opacity: loadingCompetitorGaps ? 0.8 : 1,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              >
                {loadingCompetitorGaps ? '⏳ Scanning Competitor Gaps...' : '⟳ Refresh Gap Scan'}
              </button>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="seo-metrics-grid" style={{ marginBottom: 24 }}>
            <div className="metric-card" style={{ borderLeft: '4px solid #ef4444' }}>
              <span className="metric-label">Reference Competitors</span>
              <span className="metric-val">{competitorsList.length}</span>
              <span className="metric-sub">Context only; no ranking data used</span>
            </div>
            <div className="metric-card" style={{ borderLeft: '4px solid #3b82f6' }}>
              <span className="metric-label">Total Gaps Scanned</span>
              <span className="metric-val">{competitorGapsData?.totalGapsScanned ?? 0}</span>
              <span className="metric-sub">Google GSC, Trends and Autocomplete queries</span>
            </div>
            <div className="metric-card" style={{ borderLeft: '4px solid #16a34a' }}>
              <span className="metric-label">Collection Draft Ready</span>
              <span className="metric-val" style={{ color: '#16a34a' }}>
                {competitorGapsData?.conquestReadyCount || 0}
              </span>
              <span className="metric-sub">15+ eligible catalog matches</span>
            </div>
            <div className="metric-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
              <span className="metric-label">Product Optimize Ready</span>
              <span className="metric-val" style={{ color: '#8b5cf6' }}>
                {competitorGapsData?.productReadyCount || 0}
              </span>
              <span className="metric-sub">Product-intent matches</span>
            </div>
          </div>

          {/* Competitor reference context; not a source of ranking or keyword evidence. */}
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 24,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 12, textTransform: 'uppercase' }}>
              Competitor reference set {loadingCompetitors && <span style={{ fontSize: 11, color: '#94a3b8', textTransform: 'none' }}>(Loading...)</span>}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {competitorsList.map((comp) => (
                <span key={comp.id} style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, color: '#475569' }}>
                  {comp.name} <span style={{ color: '#94a3b8' }}>({comp.domain})</span>
                </span>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>
              These domains are reference context only. This tool does not claim they rank for any query.
            </div>
          </div>

          {/* Keyword Gaps Table */}
          <div className="seo-section-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b' }}>
                  🎯 Google Demand & Catalog Gap Explorer
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                  Your GSC query performance and Google Trends/Autocomplete suggestions matched to eligible products and collections.
                </p>
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Showing <strong>{competitorGapsData?.gaps?.length || 0}</strong> keyword opportunities
              </div>
            </div>

            {competitorGapsError && (
              <div role="alert" style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', fontSize: 13 }}>
                {competitorGapsError}
              </div>
            )}
            {competitorGapsData?.dataMode === 'GOOGLE_SIGNALS' && (
              <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 6, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1e3a8a', fontSize: 13 }}>
                Google-only signals: GSC metrics describe your site; Trends and Autocomplete are demand ideas, not exact search-volume or competitor-ranking data. Catalog counts are eligible matches, not verified stock.
              </div>
            )}
            {loadingCompetitorGaps ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: '#64748b' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Scanning Competitor Keywords & Catalog Matches...</div>
                <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>Refreshing Google Search Console and Google Trends signals, then matching them to the catalog</div>
              </div>
            ) : !competitorGapsData?.gaps?.length ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: '#64748b' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{competitorGapsData ? 'No relevant Google signals found' : 'Scan not run yet'}</div>
                <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>Refresh the scan to load current GSC, Trends and Autocomplete opportunities.</div>
              </div>
            ) : (
              <div className="seo-table-container">
                <table className="seo-table">
                  <thead>
                    <tr>
                      <th style={{ width: '28%' }}>Keyword & Intent</th>
                      <th style={{ width: '20%' }}>Google Signal</th>
                      <th style={{ width: '18%' }}>Catalog Matches</th>
                      <th style={{ width: '16%' }}>Recommended Path</th>
                      <th style={{ width: '18%', textAlign: 'right' }}>Draft Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitorGapsData.gaps.map((gap) => {
                      const isExecuting = executingConquestQuery === gap.id;
                      const statusLabel = {
                        COLLECTION_DRAFT_READY: 'Collection draft ready',
                        PRODUCT_OPTIMIZE_READY: 'Product drafts available',
                        BLOG_DRAFT_READY: 'Blog workflow available',
                        COLLECTION_INVENTORY_THIN: 'Collection match set is thin',
                        GSC_OWNERSHIP_REVIEW: 'GSC landing-page ownership is unclear',
                        ALREADY_TARGETED: 'Already targeted',
                        CATALOG_GAP: 'No eligible catalog match',
                      }[gap.gapStatus] || gap.gapStatus;
                      const canCreateCollection = gap.gapStatus === 'COLLECTION_DRAFT_READY';
                      const canOptimizeProducts = gap.gapStatus === 'PRODUCT_OPTIMIZE_READY';
                      const canCreateBlog = gap.gapStatus === 'BLOG_DRAFT_READY';
                      return (
                        <tr key={gap.id}>
                          <td>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', textTransform: 'capitalize' }}>
                              {gap.query}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                              <span style={{ background: '#f1f5f9', color: '#334155', fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                                {gap.pageType} intent
                              </span>
                              {gap.source === 'google_search_console' ? (
                                <span style={{ fontSize: 11, color: '#1d4ed8' }}>
                                  GSC: {Number(gap.impressions || 0).toLocaleString()} impressions, {Number(gap.clicks || 0).toLocaleString()} clicks{gap.position != null ? `, avg. pos. ${Number(gap.position).toFixed(1)}` : ''}
                                </span>
                              ) : <span style={{ fontSize: 11, color: '#854d0e' }}>Suggestion signal; volume not provided</span>}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: '#334155' }}>{gap.competitor}</div>
                            {gap.ownerPage && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>Your landing page: {gap.ownerPage}</div>}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{
                                fontWeight: 700,
                                fontSize: 14,
                                color: gap.matchingProductsCount >= 15 ? '#15803d' : (gap.matchingProductsCount > 0 ? '#2563eb' : '#64748b'),
                              }}>
                                {gap.matchingProductsCount} eligible matches
                              </span>
                            </div>
                            {gap.sampleMatchingProducts?.length > 0 && (
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                {gap.sampleMatchingProducts.slice(0, 3).map((product) => product.sku || product.title).join(', ')}
                              </div>
                            )}
                          </td>
                          <td>
                            <span style={{ background: '#f1f5f9', color: '#334155', padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                              {statusLabel}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                              {canCreateCollection && (
                                <button
                                  onClick={() => handleExecuteConquest(gap, 'CREATE_COLLECTION')}
                                  disabled={isExecuting}
                                  style={{
                                    background: '#16a34a',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: 6,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: isExecuting ? 'not-allowed' : 'pointer',
                                    opacity: isExecuting ? 0.7 : 1,
                                  }}
                                >
                                  {isExecuting ? '⏳ Drafting...' : '📁 Create Draft'}
                                </button>
                              )}
                              {canOptimizeProducts && (
                                <button onClick={() => handleExecuteConquest(gap, 'OPTIMIZE_PRODUCTS')} disabled={isExecuting} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: isExecuting ? 'not-allowed' : 'pointer', opacity: isExecuting ? 0.6 : 1 }}>
                                  {isExecuting ? '⏳ Drafting...' : '🎯 Optimize Products'}
                                </button>
                              )}
                              {canCreateBlog && (
                                <button onClick={() => handleOpenBlogForQuery(gap.query, gap.ownerType === 'BLOG' ? gap.ownerHandle : null, gap.source === 'google_search_console' ? gap : null)} style={{ background: '#0f766e', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                  ✍️ Draft Blog
                                </button>
                              )}
                              {gap.gapStatus === 'COLLECTION_INVENTORY_THIN' && (
                                <span title="The collection workflow requires at least 15 matched products." style={{ color: '#64748b', fontSize: 11 }}>15+ matches required</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: GOOGLE ADS EXPANSION BRIDGE */}
      {activeTab === 'google-ads' && (
        <div>
          {/* Header Card */}
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
            color: '#ffffff',
            borderRadius: 12,
            padding: '24px 28px',
            marginBottom: 20,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}>
            <div style={{ maxWidth: 700 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255, 255, 255, 0.15)', padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700, marginBottom: 10, letterSpacing: '0.5px' }}>
                ⚡ MULTI-CAMPAIGN AD EXPANSION INTELLIGENCE
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px', color: '#ffffff' }}>
                Google Ads Expansion Bridge
              </h2>
              <p style={{ margin: 0, fontSize: 14, color: '#c7d2fe', lineHeight: 1.5 }}>
                Monitors high-intent queries and catalog depth across all fabric clusters. Generates verified live URLs, complete copy, keywords, and feed structures across Performance Max, Search (RSA), Shopping feeds, and Demand Gen.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => handleDownloadAdCsv()}
                disabled={downloadingAdCsv}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#22c55e',
                  color: '#ffffff',
                  padding: '12px 20px',
                  borderRadius: 8,
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: downloadingAdCsv ? 'not-allowed' : 'pointer',
                  opacity: downloadingAdCsv ? 0.8 : 1,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              >
                {downloadingAdCsv ? '⏳ Generating Bulk Copy & CSV (3s)...' : '📥 Download Full Search Campaign CSV'}
              </button>
              <button
                className="btn-primary-seo"
                style={{ background: 'rgba(255, 255, 255, 0.2)', border: '1px solid rgba(255, 255, 255, 0.3)', color: '#ffffff', padding: '12px 18px' }}
                onClick={fetchAdRecommendations}
                disabled={loadingAdRecs}
              >
                {loadingAdRecs ? '⟳ Analyzing...' : '⟳ Refresh Opportunities'}
              </button>
            </div>
          </div>

          {/* Campaign Type Sub-Navigation Bar */}
          <div style={{
            display: 'flex',
            gap: 8,
            borderBottom: '2px solid #e2e8f0',
            marginBottom: 20,
            paddingBottom: 2,
            overflowX: 'auto',
          }}>
            <button
              type="button"
              onClick={() => setAdCampaignTypeTab('pmax')}
              style={{
                padding: '10px 18px',
                border: 'none',
                background: 'none',
                borderBottom: adCampaignTypeTab === 'pmax' ? '3px solid #7c3aed' : '3px solid transparent',
                color: adCampaignTypeTab === 'pmax' ? '#7c3aed' : '#64748b',
                fontWeight: adCampaignTypeTab === 'pmax' ? 800 : 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.15s ease',
              }}
            >
              <span>🎯 Performance Max (PMax)</span>
              {adRecommendations.filter(r => r.updateNeeded).length > 0 && (
                <span style={{ background: '#f59e0b', color: '#ffffff', fontSize: 11, padding: '2px 7px', borderRadius: 9999 }}>
                  {adRecommendations.filter(r => r.updateNeeded).length} Update
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setAdCampaignTypeTab('search')}
              style={{
                padding: '10px 18px',
                border: 'none',
                background: 'none',
                borderBottom: adCampaignTypeTab === 'search' ? '3px solid #2563eb' : '3px solid transparent',
                color: adCampaignTypeTab === 'search' ? '#2563eb' : '#64748b',
                fontWeight: adCampaignTypeTab === 'search' ? 800 : 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.15s ease',
              }}
            >
              <span>🔍 Search Campaigns (RSA & Keywords)</span>
              <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: 11, padding: '2px 7px', borderRadius: 9999 }}>
                {adRecommendations.length} Groups
              </span>
            </button>

            <button
              type="button"
              onClick={() => setAdCampaignTypeTab('shopping')}
              style={{
                padding: '10px 18px',
                border: 'none',
                background: 'none',
                borderBottom: adCampaignTypeTab === 'shopping' ? '3px solid #059669' : '3px solid transparent',
                color: adCampaignTypeTab === 'shopping' ? '#059669' : '#64748b',
                fontWeight: adCampaignTypeTab === 'shopping' ? 800 : 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.15s ease',
              }}
            >
              <span>🛍️ Shopping & Smart Feeds</span>
              <span style={{ background: '#dcfce7', color: '#15803d', fontSize: 11, padding: '2px 7px', borderRadius: 9999 }}>
                5 Custom Labels
              </span>
            </button>

            <button
              type="button"
              onClick={() => setAdCampaignTypeTab('demand_gen')}
              style={{
                padding: '10px 18px',
                border: 'none',
                background: 'none',
                borderBottom: adCampaignTypeTab === 'demand_gen' ? '3px solid #db2777' : '3px solid transparent',
                color: adCampaignTypeTab === 'demand_gen' ? '#db2777' : '#64748b',
                fontWeight: adCampaignTypeTab === 'demand_gen' ? 800 : 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.15s ease',
              }}
            >
              <span>📱 Demand Gen & Visuals</span>
              <span style={{ background: '#fce7f3', color: '#be185d', fontSize: 11, padding: '2px 7px', borderRadius: 9999 }}>
                YouTube / Shorts
              </span>
            </button>
          </div>

          {/* Quick Metrics Bar */}
          <div className="seo-stats-grid" style={{ marginBottom: 20 }}>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Total Evaluated Clusters</div>
              <div className="seo-stat-value">{adRecommendations.length}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Garment End-Use Categories</div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Novel Emerging Categories</div>
              <div className="seo-stat-value" style={{ color: '#7c3aed' }}>
                {adRecommendations.filter((r) => r.isNewNovelGroup).length}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>High-intent unstitched clusters</div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Live In Google Ads</div>
              <div className="seo-stat-value" style={{ color: '#059669' }}>
                {adRecommendations.filter((r) => r.isLiveInGoogleAds).length} / {adRecommendations.length}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Tracked live asset groups</div>
            </div>
            <div className="seo-stat-card">
              <div className="seo-stat-label">Catalog Products Classified</div>
              <div className="seo-stat-value" style={{ color: '#16a34a' }}>
                {adRecommendations.reduce((sum, r) => sum + r.productCount, 0)}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Mapped to custom_label_2</div>
            </div>
          </div>

          {/* SUBTAB 1: PERFORMANCE MAX (PMAX) */}
          {adCampaignTypeTab === 'pmax' && (
            <div>
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '12px 18px',
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
              }}>
                <div style={{ fontSize: 13, color: '#334155' }}>
                  <strong>🎯 Performance Max Strategy:</strong> Each Asset Group targets a specific garment end-use. Click <strong>"View / Copy PMax Assets"</strong> to paste headlines, descriptions, verified Shopify URLs, and search themes directly into Google Ads.
                </div>
                <div style={{ fontSize: 12, background: '#e0e7ff', padding: '4px 10px', borderRadius: 6, fontWeight: 700, color: '#3730a3' }}>
                  Target Campaign ID: 24320551082
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {adRecommendations.map((rec) => {
                  const isNovel = rec.isNewNovelGroup;
                  const shopifyUrl = rec.finalUrl || rec.shopifyCollection?.url || `https://fabvoguestudio.com/collections/${rec.endUse.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                  const colTitle = rec.shopifyCollection?.title || rec.endUse;

                  return (
                    <div
                      key={rec.id}
                      style={{
                        background: '#ffffff',
                        border: isNovel ? '2px solid #8b5cf6' : '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '20px 24px',
                        boxShadow: isNovel ? '0 4px 12px rgba(139, 92, 246, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 320 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                              {rec.recommendedAssetGroupName}
                            </h3>
                            
                            {/* Live Status Badges */}
                            {rec.updateNeeded ? (
                              <span style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700 }}>
                                ⚠️ Update Recommended
                              </span>
                            ) : rec.isLiveInGoogleAds ? (
                              <span style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700 }}>
                                🟢 Live in Google Ads
                              </span>
                            ) : (
                              <span style={{ background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}>
                                🟡 Ready to Create
                              </span>
                            )}

                            {isNovel && (
                              <span style={{ background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700 }}>
                                ✨ New Emerging Category
                              </span>
                            )}
                          </div>

                          {rec.updateNeeded && (
                            <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#92400e', marginBottom: 8 }}>
                              🔔 <strong>Catalog Update:</strong> {rec.updateReason} Open ad copy to copy updated assets.
                            </div>
                          )}

                          {/* Verified Shopify Live URL */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155', marginBottom: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, color: '#475569' }}>Final URL (Verified Shopify):</span>
                            <a href={shopifyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}>
                              {shopifyUrl}
                            </a>
                            <span style={{ fontSize: 11, background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, color: '#64748b' }}>
                              Collection: "{colTitle}"
                            </span>
                            <button
                              onClick={() => copyToClipboard(shopifyUrl, 'Final URL')}
                              style={{ background: '#e0e7ff', color: '#3730a3', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                            >
                              📋 Copy URL
                            </button>
                          </div>

                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Listing Group Feed Filter: <code>custom_label_4 = "{rec.targetCustomLabel4 || 'SEO High Intent'}"</code> &amp; <code>custom_label_2 = "{rec.targetCustomLabel2}"</code>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            className="btn-primary-seo"
                            style={{ background: '#4338ca', padding: '8px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                            onClick={() => {
                              setModalCampaignTab('pmax');
                              handleGenerateAdCopy(rec);
                            }}
                          >
                            📋 View / Copy PMax Assets
                          </button>

                          {rec.isLiveInGoogleAds ? (
                            <button
                              onClick={() => handleToggleAssetGroupLive(rec.endUse, false)}
                              disabled={updatingLiveStatusEndUse === rec.endUse}
                              style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                border: '1px solid #cbd5e1',
                                borderRadius: 6,
                                padding: '8px 12px',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              {updatingLiveStatusEndUse === rec.endUse ? 'Saving...' : 'Mark Inactive'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleAssetGroupLive(rec.endUse, true)}
                              disabled={updatingLiveStatusEndUse === rec.endUse}
                              style={{
                                background: '#059669',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '8px 14px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                            >
                              {updatingLiveStatusEndUse === rec.endUse ? 'Saving...' : '✓ Mark as Created in Google Ads'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Activity Pills */}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '14px 0 12px' }}>
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}>
                          📦 <strong>{rec.productCount} Products</strong> ({rec.statusCounts.SYNCED || 0} Synced, {rec.statusCounts.QUEUED_FOR_RELEASE || 0} Queued)
                        </div>
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}>
                          📁 <strong>{rec.collectionCount} Smart Collections</strong>
                        </div>
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}>
                          ✍️ <strong>{rec.blogCount} Published Blogs</strong>
                        </div>
                        {rec.topImpressions > 0 && (
                          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}>
                            📈 <strong>{rec.topImpressions.toLocaleString()} Monthly GSC Impressions</strong>
                          </div>
                        )}
                      </div>

                      {/* Sample Search Queries */}
                      {rec.sampleQueries?.length > 0 && (
                        <div style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, color: '#64748b' }}>Search Themes / High-Intent Queries:</span>
                          {rec.sampleQueries.map((q, idx) => (
                            <span
                              key={idx}
                              onClick={() => copyToClipboard(q, `Query: ${q}`)}
                              title="Click to copy query"
                              style={{
                                background: '#f1f5f9',
                                padding: '2px 8px',
                                borderRadius: 4,
                                color: '#0f172a',
                                cursor: 'pointer',
                                border: '1px solid #e2e8f0',
                              }}
                            >
                              "{q}" 📋
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SUBTAB 2: SEARCH CAMPAIGNS (RSA & KEYWORDS) */}
          {adCampaignTypeTab === 'search' && (
            <div>
              {/* Search Strategy Banner */}
              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 10,
                padding: '16px 20px',
                marginBottom: 20,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#1e3a8a' }}>
                      🔍 Responsive Search Ads (RSA) &amp; Search Keyword Expansion
                    </h3>
                    <p style={{ margin: 0, fontSize: 13, color: '#3b82f6' }}>
                      Target intent-rich fabric searchers looking to buy running cuts by the meter. Recommended bid strategy: <strong>Maximize Conversion Value (Target ROAS 350%)</strong>.
                    </p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                      <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                        🟢 {adRecommendations.filter(r => r.isSearchLive).length} of {adRecommendations.length} Search Groups Live
                      </span>
                      {adRecommendations.some(r => r.searchUpdateNeeded) && (
                        <span style={{ background: '#fef3c7', color: '#b45309', border: '1.5px solid #f59e0b', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                          ⚠️ {adRecommendations.filter(r => r.searchUpdateNeeded).length} Groups Have New Keywords Pending Upload
                        </span>
                      )}
                      {adRecommendations.some(r => !r.isSearchLive) && (
                        <span style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                          🟡 {adRecommendations.filter(r => !r.isSearchLive).length} Draft / Pending Initial Upload
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadAdCsv()}
                    disabled={downloadingAdCsv}
                    style={{
                      background: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 16px',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: downloadingAdCsv ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {downloadingAdCsv ? 'Generating CSV...' : '📥 Export Search Bulk Upload CSV'}
                  </button>
                </div>
              </div>

              {/* Master Negative Keywords Box */}
              <div style={{
                background: '#fff1f2',
                border: '1px solid #fecdd3',
                borderRadius: 10,
                padding: '16px 20px',
                marginBottom: 20,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>🛡️</span>
                    <strong style={{ fontSize: 14, color: '#9f1239' }}>Campaign Negative Keywords Master List (30 Keywords)</strong>
                  </div>
                  <button
                    onClick={() => {
                      const negList = 'free\njob\nvacancy\nsalary\ncareer\nrecruitment\nstitching classes\ntailoring course\npdf download\nfree pattern\nfree design\ntutorial\nhow to stitch\ndiy craft\nscrap fabric\nwaste cloth\nchindi\nrepair\nsecond hand\nused fabric\nmarket address\nmarket timing\nshop address\nnear me\noffline store\nphysical store\nb2b export\ncontainer wholesale\nfake\nfirst copy';
                      copyToClipboard(negList, 'All 30 Negative Keywords');
                    }}
                    style={{ background: '#e11d48', color: '#ffffff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    📋 Copy All Negatives
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {['free', 'job', 'vacancy', 'stitching classes', 'tailoring course', 'free pattern', 'tutorial', 'how to stitch', 'chindi', 'scrap fabric', 'second hand', 'near me', 'offline store', 'container wholesale'].map((kw, i) => (
                    <span key={i} style={{ background: '#ffffff', border: '1px solid #fda4af', color: '#9f1239', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                      -{kw}
                    </span>
                  ))}
                  <span style={{ fontSize: 11, color: '#9f1239', alignSelf: 'center' }}>+16 more (prevents wasted ad spend)</span>
                </div>
              </div>

              {/* Search Ad Groups Grid */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {adRecommendations.map((rec) => {
                  const shopifyUrl = rec.finalUrl || rec.shopifyCollection?.url || 'https://fabvoguestudio.com/collections/all-fabrics';
                  const activeKws = rec.activeSearchKeywords || [];
                  const suggestedKws = rec.suggestedSearchTerms || [];
                  const customKws = rec.customKeywords || [];
                  const customInputValue = customSearchKeywordInputs[rec.endUse] || '';

                  return (
                    <div
                      key={rec.id}
                      style={{
                        background: '#ffffff',
                        border: rec.searchUpdateNeeded
                          ? '1.5px solid #f59e0b'
                          : (rec.isSearchLive ? '1.5px solid #86efac' : '1px solid #e2e8f0'),
                        borderRadius: 12,
                        padding: '20px 24px',
                        boxShadow: rec.searchUpdateNeeded
                          ? '0 2px 8px rgba(245, 158, 11, 0.12)'
                          : '0 1px 3px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                              Ad Group: [Search] {rec.endUse}
                            </h3>
                            <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>
                              Responsive Search Ad
                            </span>
                            {rec.searchUpdateNeeded ? (
                              <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                ⚠️ Update Pending in Google Ads ({rec.pendingKeywordsCount} new)
                              </span>
                            ) : rec.isSearchLive ? (
                              <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                🟢 LIVE &amp; Up to Date
                              </span>
                            ) : (
                              <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                🟡 Draft / Needs Upload
                              </span>
                            )}
                            {rec.searchExportedAt && (
                              <span style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 500 }}>
                                📥 Exported {new Date(rec.searchExportedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#475569' }}>
                            Final URL: <a href={shopifyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{shopifyUrl}</a>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleToggleSearchAdLive(rec.endUse, !rec.isSearchLive)}
                            disabled={updatingSearchLiveEndUse === rec.endUse}
                            style={{
                              background: rec.isSearchLive ? '#f8fafc' : '#16a34a',
                              color: rec.isSearchLive ? '#475569' : '#ffffff',
                              border: rec.isSearchLive ? '1px solid #cbd5e1' : 'none',
                              padding: '8px 14px',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: updatingSearchLiveEndUse === rec.endUse ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            {updatingSearchLiveEndUse === rec.endUse
                              ? '⏳ Updating...'
                              : rec.isSearchLive
                              ? 'Mark as Draft / Not Live'
                              : '✓ Mark Live in Google Ads'}
                          </button>
                          <button
                            onClick={() => handleDownloadAdCsv(rec.endUse)}
                            disabled={downloadingAdCsv}
                            style={{
                              background: '#ffffff',
                              color: '#2563eb',
                              border: '1px solid #93c5fd',
                              padding: '8px 12px',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: downloadingAdCsv ? 'not-allowed' : 'pointer',
                            }}
                          >
                            📥 Export CSV
                          </button>
                          <button
                            className="btn-primary-seo"
                            style={{ background: '#2563eb', padding: '8px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                            onClick={() => {
                              setModalCampaignTab('search');
                              handleGenerateAdCopy(rec);
                            }}
                          >
                            📋 View / Copy 15 RSA Headlines
                          </button>
                        </div>
                      </div>

                      {/* Delta Update Warning Box if Google Ads update needed */}
                      {rec.searchUpdateNeeded && (
                        <div style={{
                          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                          border: '1.5px solid #f59e0b',
                          borderRadius: 8,
                          padding: '12px 16px',
                          marginBottom: 14,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 12,
                        }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: 16 }}>⚠️</span>
                              <strong style={{ fontSize: 13, color: '#92400e' }}>
                                Google Ads Needs Update: {rec.pendingKeywordsCount} New Keyword{rec.pendingKeywordsCount === 1 ? '' : 's'} Not Yet Synced
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#b45309' }}>
                              New keywords waiting to be added in Google Ads: {(rec.pendingKeywords || []).slice(0, 4).map(k => `"${k}"`).join(', ')}{rec.pendingKeywords?.length > 4 ? ` +${rec.pendingKeywords.length - 4} more` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              onClick={() => copyToClipboard((rec.pendingKeywords || []).map(k => `"${k}"`).join('\n'), `${rec.pendingKeywordsCount} New Keywords`)}
                              style={{
                                background: '#d97706',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                              }}
                            >
                              📋 Copy Only New Keywords ({rec.pendingKeywordsCount})
                            </button>
                            <button
                              onClick={() => handleDownloadAdCsv(rec.endUse, true)}
                              disabled={downloadingAdCsv}
                              style={{
                                background: '#ffffff',
                                color: '#b45309',
                                border: '1px solid #f59e0b',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: downloadingAdCsv ? 'not-allowed' : 'pointer',
                              }}
                            >
                              📥 Export Update CSV
                            </button>
                            <button
                              onClick={() => handleMarkSearchAdUpdated(rec.endUse)}
                              disabled={updatingSearchLiveEndUse === rec.endUse}
                              style={{
                                background: '#16a34a',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: updatingSearchLiveEndUse === rec.endUse ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {updatingSearchLiveEndUse === rec.endUse ? '⏳ Syncing...' : '✓ Mark Updated in Google Ads'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Active Keywords Section */}
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🎯</span>
                            <strong>Active Keywords in Ad Group ({activeKws.length})</strong>
                            <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}>— Phrase match enabled in CSV export</span>
                          </div>
                          {activeKws.length > 0 && (
                            <button
                              onClick={() => copyToClipboard(activeKws.map(k => `"${k}"`).join('\n'), `All ${activeKws.length} Active Keywords`)}
                              style={{ background: '#ffffff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                            >
                              📋 Copy All Active ({activeKws.length})
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {activeKws.map((kw, i) => {
                            const isCustom = customKws.includes(kw.toLowerCase());
                            const isPending = (rec.pendingKeywords || []).some(pk => pk.toLowerCase() === kw.toLowerCase());
                            return (
                              <span
                                key={i}
                                style={{
                                  background: isPending ? '#fef3c7' : (isCustom ? '#ecfdf5' : '#ffffff'),
                                  border: isPending ? '1.5px solid #f59e0b' : (isCustom ? '1px solid #a7f3d0' : '1px solid #cbd5e1'),
                                  padding: '3px 8px',
                                  borderRadius: 4,
                                  fontSize: 12,
                                  color: isPending ? '#92400e' : (isCustom ? '#065f46' : '#1e293b'),
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <span
                                  onClick={() => copyToClipboard(`"${kw}"`, `Keyword "${kw}"`)}
                                  style={{ cursor: 'pointer' }}
                                  title="Click to copy"
                                >
                                  "{kw}" 📋
                                </span>
                                {isPending && (
                                  <span style={{ background: '#f59e0b', color: '#ffffff', fontSize: 9, fontWeight: 800, padding: '1px 4px', borderRadius: 3, letterSpacing: '0.3px' }}>
                                    NEW
                                  </span>
                                )}
                                {isCustom && (
                                  <button
                                    onClick={() => handleRemoveSearchKeyword(rec.endUse, kw)}
                                    title="Remove this keyword"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#991b1b',
                                      cursor: 'pointer',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      padding: '0 2px',
                                      lineHeight: 1,
                                    }}
                                  >
                                    &times;
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Suggested New Search Terms Web Panel */}
                      <div style={{
                        background: '#faf5ff',
                        border: '1px solid #e9d5ff',
                        borderRadius: 8,
                        padding: '12px 14px',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 15 }}>💡</span>
                              <strong style={{ fontSize: 13, color: '#6b21a8' }}>
                                Suggested New Search Terms ({suggestedKws.length} Discovered)
                              </strong>
                            </div>
                            <span style={{ fontSize: 11, color: '#7e22ce' }}>
                              Discovered from Google Search Console &amp; live fabric searches. 1-click add to include in your Search Ad Group.
                            </span>
                          </div>
                          {suggestedKws.length > 0 && (
                            <button
                              onClick={() => copyToClipboard(suggestedKws.map(k => `"${k}"`).join('\n'), `All ${suggestedKws.length} Suggested Keywords`)}
                              style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                            >
                              📋 Copy All Suggested ({suggestedKws.length})
                            </button>
                          )}
                        </div>

                        {suggestedKws.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                            {suggestedKws.map((term, idx) => {
                              const isAdding = addingKeywordEndUse === `${rec.endUse}::${term}`;
                              return (
                                <span
                                  key={idx}
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #d8b4fe',
                                    borderRadius: 6,
                                    padding: '4px 8px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                    color: '#4c1d95',
                                  }}
                                >
                                  <span
                                    onClick={() => copyToClipboard(`"${term}"`, `Term "${term}"`)}
                                    style={{ cursor: 'pointer' }}
                                    title="Click to copy"
                                  >
                                    "{term}"
                                  </span>
                                  <button
                                    onClick={() => handleAddSearchKeyword(rec.endUse, term)}
                                    disabled={Boolean(isAdding)}
                                    title="Add this search term to the Ad Group keywords"
                                    style={{
                                      background: '#16a34a',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: 4,
                                      padding: '2px 6px',
                                      fontSize: 11,
                                      fontWeight: 700,
                                      cursor: isAdding ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {isAdding ? '...' : '+ Add'}
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: '#6b21a8', fontStyle: 'italic', marginBottom: 10 }}>
                            ✓ All high-intent discovery search queries have been added to this ad group!
                          </div>
                        )}

                        {/* Custom Search Keyword Input */}
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder={`Type custom keyword to add to [Search] ${rec.endUse}...`}
                            value={customInputValue}
                            onChange={(e) => {
                              const val = e.target.value;
                              setCustomSearchKeywordInputs((prev) => ({ ...prev, [rec.endUse]: val }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && customInputValue.trim()) {
                                handleAddSearchKeyword(rec.endUse, customInputValue.trim());
                              }
                            }}
                            style={{
                              flex: 1,
                              maxWidth: 420,
                              padding: '5px 10px',
                              border: '1px solid #cbd5e1',
                              borderRadius: 4,
                              fontSize: 12,
                            }}
                          />
                          <button
                            onClick={() => handleAddSearchKeyword(rec.endUse, customInputValue.trim())}
                            disabled={!customInputValue.trim() || addingKeywordEndUse === `${rec.endUse}::${customInputValue.trim()}`}
                            style={{
                              background: '#7c3aed',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '5px 10px',
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: !customInputValue.trim() ? 'not-allowed' : 'pointer',
                            }}
                          >
                            + Add Keyword
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SUBTAB 3: SHOPPING & SMART FEEDS */}
          {adCampaignTypeTab === 'shopping' && (
            <div>
              {/* Shopping Architecture Guide */}
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 10,
                padding: '18px 22px',
                marginBottom: 20,
              }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#166534' }}>
                  🛍️ Merchant Center &amp; Shopping Smart Feed Taxonomy
                </h3>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: '#15803d', lineHeight: 1.5 }}>
                  The SEO Engine automatically categorizes and syncs every Shopify product to Google Merchant Center using 5 custom labels. This allows you to create high-margin, high-intent product listing groups with zero manual feed editing.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                  <div style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#166534', display: 'block' }}>custom_label_0</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}>Margin &amp; Revenue Tier (High / Medium / Value)</span>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#166534', display: 'block' }}>custom_label_1</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}>Fabric Base (Pure Georgette, Russian Silk, Organza)</span>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#166534', display: 'block' }}>custom_label_2</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}>Garment End-Use (Co-ord Set, Kurti, Saree, Bridal)</span>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#166534', display: 'block' }}>custom_label_3</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}><code>Fresh Design</code> (new arrivals; not used by the SEO engine)</span>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#166534', display: 'block' }}>custom_label_4</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}><code>SEO High Intent</code> (SEO-optimized products; wins over "Current Season")</span>
                  </div>
                </div>
              </div>

              {/* Feed Breakdown Table */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                    Catalog Feed Product Classification Matrix
                  </h4>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                      <th style={{ padding: '10px 16px' }}>Garment End-Use (`custom_label_2`)</th>
                      <th style={{ padding: '10px 16px' }}>Catalog Products</th>
                      <th style={{ padding: '10px 16px' }}>Target Feed Filter</th>
                      <th style={{ padding: '10px 16px' }}>Verified Shopify Collection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adRecommendations.map((rec) => {
                      const shopifyUrl = rec.finalUrl || rec.shopifyCollection?.url || 'https://fabvoguestudio.com/collections/all-fabrics';
                      return (
                        <tr key={rec.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a' }}>
                            {rec.endUse}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: 4, fontWeight: 600 }}>
                              {rec.productCount} in-stock SKUs
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 12, color: '#047857' }}>
                            custom_label_4="SEO High Intent" &amp; custom_label_2="{rec.targetCustomLabel2}"
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <a href={shopifyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                              {rec.shopifyCollection?.handle || 'all-fabrics'}
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SUBTAB 4: DEMAND GEN & FUTURE TYPES */}
          {adCampaignTypeTab === 'demand_gen' && (
            <div>
              {/* Demand Gen Specs */}
              <div style={{
                background: '#fdf2f8',
                border: '1px solid #fbcfe8',
                borderRadius: 10,
                padding: '18px 22px',
                marginBottom: 20,
              }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#9d174d' }}>
                  📱 Demand Gen, YouTube Shorts &amp; Discover Ad Specs
                </h3>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: '#be185d', lineHeight: 1.5 }}>
                  Demand Gen campaigns leverage visual imagery and video to engage fabric buyers across YouTube, Discover, and Gmail. Use these asset dimensions and AI-generated hooks.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                  <div style={{ background: '#ffffff', border: '1px solid #f472b6', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#9d174d', display: 'block' }}>1.91:1 Landscape</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}>1200 x 628 px (Hero Fabric Shots)</span>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #f472b6', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#9d174d', display: 'block' }}>1:1 Square</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}>1200 x 1200 px (Pattern Close-ups)</span>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #f472b6', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#9d174d', display: 'block' }}>4:5 Portrait</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}>960 x 1200 px (Discover &amp; Feed)</span>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #f472b6', borderRadius: 6, padding: '10px 12px' }}>
                    <strong style={{ fontSize: 12, color: '#9d174d', display: 'block' }}>9:16 Vertical</strong>
                    <span style={{ fontSize: 12, color: '#334155' }}>1080 x 1920 px (YouTube Shorts &amp; Reels)</span>
                  </div>
                </div>
              </div>

              {/* Demand Gen Ad Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {adRecommendations.map((rec) => {
                  const shopifyUrl = rec.finalUrl || rec.shopifyCollection?.url || 'https://fabvoguestudio.com/collections/all-fabrics';
                  return (
                    <div
                      key={rec.id}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '20px 24px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                              Demand Gen: {rec.endUse} Hook Suite
                            </h3>
                            <span style={{ background: '#fce7f3', color: '#db2777', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>
                              Visual &amp; Video Ads
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#475569' }}>
                            Destination URL: <a href={shopifyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#db2777' }}>{shopifyUrl}</a>
                          </div>
                        </div>

                        <button
                          className="btn-primary-seo"
                          style={{ background: '#db2777', padding: '8px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                          onClick={() => {
                            setModalCampaignTab('demand_gen');
                            handleGenerateAdCopy(rec);
                          }}
                        >
                          📋 View / Copy Demand Gen Copy &amp; Hooks
                        </button>
                      </div>

                      <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#831843' }}>
                        <strong>🎯 Audience Signals:</strong> Custom Intent for "{rec.sampleQueries?.[0] || rec.endUse}", In-Market for Apparel &amp; Ethnic Wear, 30d Website Visitors.
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* AD COPY PREVIEW & 1-CLICK COPY MODAL */}
      {selectedAdRecForCopy && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            maxWidth: 840,
            width: '100%',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                    {selectedAdRecForCopy.recommendedAssetGroupName}
                  </h3>
                  {selectedAdRecForCopy.isLiveInGoogleAds ? (
                    <span style={{ background: '#f0fdf4', color: '#166534', fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 700 }}>
                      🟢 Live in Google Ads
                    </span>
                  ) : (
                    <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 700 }}>
                      🟡 Draft / Ready to Create
                    </span>
                  )}
                  {generatedAdCopyMap[selectedAdRecForCopy.endUse] && (
                    <span style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 600 }}>
                      💾 Saved in Cloud
                    </span>
                  )}
                  {selectedAdRecForCopy.updateNeeded && (
                    <span style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 700 }}>
                      ⚠️ {selectedAdRecForCopy.newProductsSinceSync} New Products (Update Recommended)
                    </span>
                  )}
                </div>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>
                  Copy directly to Google Ads. All assets and prompts are persisted and only refreshed when you choose.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={() => handleGenerateAdCopy(selectedAdRecForCopy, true)}
                  disabled={loadingAdCopy}
                  title="Re-run AI generation with the latest synced products & search queries"
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#334155',
                    cursor: loadingAdCopy ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {loadingAdCopy ? '⏳ Refreshing...' : '🔄 Refresh / Regenerate Copy'}
                </button>
                <button
                  onClick={() => setSelectedAdRecForCopy(null)}
                  style={{ background: 'none', border: 'none', fontSize: 22, color: '#64748b', cursor: 'pointer', padding: 0 }}
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Modal Campaign Sub-Tabs */}
            <div style={{
              display: 'flex',
              background: '#f1f5f9',
              borderBottom: '1px solid #e2e8f0',
              padding: '0 16px',
            }}>
              {[
                { id: 'pmax', label: '🎯 Performance Max (PMax)' },
                { id: 'video_creator', label: '🎬 5-Step Video Creator' },
                { id: 'search', label: '🔍 Search (RSA & Keywords)' },
                { id: 'demand_gen', label: '📱 Demand Gen & Visuals' },
                { id: 'shopping', label: '🛍️ Feed & Filters' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setModalCampaignTab(tab.id)}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    background: 'none',
                    borderBottom: modalCampaignTab === tab.id ? '2.5px solid #7c3aed' : '2.5px solid transparent',
                    color: modalCampaignTab === tab.id ? '#7c3aed' : '#64748b',
                    fontWeight: modalCampaignTab === tab.id ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {copiedText && (
              <div style={{ background: '#10b981', color: '#ffffff', textAlign: 'center', padding: '6px', fontSize: 12, fontWeight: 700 }}>
                ✓ Copied to clipboard: "{copiedText}"
              </div>
            )}

            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {loadingAdCopy ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                  Generating high-converting multi-campaign copy with Gemini 2.5 Flash...
                </div>
              ) : generatedAdCopyMap[selectedAdRecForCopy.endUse] ? (
                <div>
                  {/* Verified Final URL */}
                  {(() => {
                    const copyData = generatedAdCopyMap[selectedAdRecForCopy.endUse];
                    const shopifyUrl = copyData?.shopifyCollection?.url || selectedAdRecForCopy.finalUrl || selectedAdRecForCopy.shopifyCollection?.url || 'https://fabvoguestudio.com/collections/all-fabrics';
                    const colTitle = copyData?.shopifyCollection?.title || selectedAdRecForCopy.shopifyCollection?.title || selectedAdRecForCopy.endUse;

                    return (
                      <div style={{ marginBottom: 18, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                            Final URL (Verified Shopify Collection)
                          </span>
                          <button
                            onClick={() => copyToClipboard(shopifyUrl, 'Final URL')}
                            style={{ background: '#e0e7ff', color: '#3730a3', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            📋 Copy URL
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{ fontSize: 13, color: '#1e40af' }}>{shopifyUrl}</code>
                          <span style={{ fontSize: 11, background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, color: '#475569' }}>
                            Collection: "{colTitle}"
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* MODAL VIEW 1: PMAX */}
                  {modalCampaignTab === 'pmax' && (
                    <div>
                      {/* Section 1: Audience Signal & Search Themes */}
                      {(() => {
                        const audSignal = generatedAdCopyMap[selectedAdRecForCopy.endUse]?.audienceSignal || generatedAdCopyMap[selectedAdRecForCopy.endUse]?.pmax?.audienceSignal || {};
                        const audName = audSignal.audienceName || `FAB VOGUE - ${selectedAdRecForCopy.endUse} Buyers`;
                        const searchThemes = audSignal.searchThemes || selectedAdRecForCopy.sampleQueries || [];
                        const customQueries = audSignal.customSegmentQueries || searchThemes;
                        const inMarket = audSignal.inMarketSegments || ["Apparel & Accessories > Women's Apparel > Ethnic Wear", "Fabrics & Textiles"];

                        return (
                          <div style={{ marginBottom: 20, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: '16px 18px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 18 }}>🎯</span>
                                <strong style={{ fontSize: 14, color: '#1e293b' }}>Audience Signal &amp; Search Themes</strong>
                              </div>
                              <span style={{ fontSize: 11, background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                                Google Ads Signals Step
                              </span>
                            </div>

                            {/* Audience Name */}
                            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px' }}>
                              <div>
                                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block' }}>Audience Name</span>
                                <strong style={{ fontSize: 13, color: '#0f172a' }}>{audName}</strong>
                              </div>
                              <button
                                onClick={() => copyToClipboard(audName, 'Audience Name')}
                                style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                              >
                                📋 Copy Name
                              </button>
                            </div>

                            {/* Search Themes */}
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                                  Search Themes (Up to 50 phrases)
                                </span>
                                <button
                                  onClick={() => copyToClipboard(searchThemes.join('\n'), 'All Search Themes')}
                                  style={{ background: '#4338ca', color: '#ffffff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                >
                                  📋 Copy All ({searchThemes.length}) Themes
                                </button>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {searchThemes.map((q, i) => (
                                  <span
                                    key={i}
                                    onClick={() => copyToClipboard(q, `Search Theme "${q}"`)}
                                    title="Click to copy single theme"
                                    style={{ background: '#ffffff', border: '1px solid #cbd5e1', padding: '3px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer', color: '#1e293b' }}
                                  >
                                    "{q}" 📋
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Custom Segment (Google Search Terms) */}
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                                  Custom Segment Queries (Searched on Google)
                                </span>
                                <button
                                  onClick={() => copyToClipboard(customQueries.join('\n'), 'All Custom Segment Queries')}
                                  style={{ background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                >
                                  📋 Copy All ({customQueries.length}) Queries
                                </button>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {customQueries.map((q, i) => (
                                  <span
                                    key={i}
                                    onClick={() => copyToClipboard(q, `Query "${q}"`)}
                                    title="Click to copy"
                                    style={{ background: '#ffffff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '3px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                                  >
                                    {q} 📋
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* In-Market & First-Party Data */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8, fontSize: 12 }}>
                              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
                                <strong style={{ color: '#475569', display: 'block', marginBottom: 2 }}>In-Market Interests:</strong>
                                <span style={{ color: '#0f172a' }}>{inMarket.join(' • ')}</span>
                              </div>
                              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
                                <strong style={{ color: '#475569', display: 'block', marginBottom: 2 }}>Your Data (First-Party):</strong>
                                <span style={{ color: '#0f172a' }}>All Website Visitors (90 Days) / Customer List</span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Section 2: Text Guidelines & Brand Controls (BETA) */}
                      <div style={{ marginBottom: 20, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '16px 18px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18 }}>🛡️</span>
                            <strong style={{ fontSize: 14, color: '#92400e' }}>Text Guidelines &amp; Brand Controls (BETA)</strong>
                          </div>
                          <span style={{ fontSize: 11, background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                            Prevents AI Mistakes
                          </span>
                        </div>

                        {/* Term Exclusions */}
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase' }}>
                              Term Exclusions (0/25) - Words AI must NEVER use
                            </span>
                            <button
                              onClick={() => {
                                const terms = 'cheap\nready made\nreadymade dress\nstitched garment\nfree pattern\ntailoring classes\nsecond hand\nused clothes';
                                copyToClipboard(terms, 'All Term Exclusions');
                              }}
                              style={{ background: '#d97706', color: '#ffffff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                            >
                              📋 Copy All Exclusions
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {['cheap', 'ready made', 'readymade dress', 'stitched garment', 'free pattern', 'tailoring classes', 'second hand', 'used clothes'].map((t, i) => (
                              <span
                                key={i}
                                onClick={() => copyToClipboard(t, `Exclusion: ${t}`)}
                                style={{ background: '#ffffff', border: '1px solid #fcd34d', color: '#b45309', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                              >
                                -{t} 📋
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Messaging Restrictions */}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase' }}>
                              Messaging Restrictions (0/40)
                            </span>
                            <button
                              onClick={() => {
                                const restr = 'We sell unstitched running fabric by the meter and 5-meter matching sets, not ready-to-wear stitched clothing.\nDo not promise 1-day or same-day delivery.';
                                copyToClipboard(restr, 'All Messaging Restrictions');
                              }}
                              style={{ background: '#d97706', color: '#ffffff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                            >
                              📋 Copy All Restrictions
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#78350f' }}>
                              <span>1. "We sell unstitched running fabric by the meter and 5-meter matching sets, not ready-to-wear stitched clothing."</span>
                              <button onClick={() => copyToClipboard('We sell unstitched running fabric by the meter and 5-meter matching sets, not ready-to-wear stitched clothing.', 'Restriction 1')} style={{ background: '#fef3c7', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}>📋</button>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#78350f' }}>
                              <span>2. "Do not promise 1-day or same-day delivery."</span>
                              <button onClick={() => copyToClipboard('Do not promise 1-day or same-day delivery.', 'Restriction 2')} style={{ background: '#fef3c7', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}>📋</button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Section 3: Ad Creative Copy */}
                      {/* Short Headlines */}
                      <div style={{ marginBottom: 18 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 13, textTransform: 'uppercase', color: '#475569', letterSpacing: '0.5px' }}>
                          Short Headlines (Max 30 chars each)
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(generatedAdCopyMap[selectedAdRecForCopy.endUse].pmax?.headlines || generatedAdCopyMap[selectedAdRecForCopy.endUse].headlines || []).map((h, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                              <strong>{h}</strong>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#64748b' }}>{h.length}/30 chars</span>
                                <button
                                  onClick={() => copyToClipboard(h, `Headline ${i + 1}`)}
                                  style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                                >
                                  📋 Copy
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Long Headlines */}
                      <div style={{ marginBottom: 18 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 13, textTransform: 'uppercase', color: '#475569', letterSpacing: '0.5px' }}>
                          Long Headlines (Max 90 chars each)
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(generatedAdCopyMap[selectedAdRecForCopy.endUse].pmax?.longHeadlines || generatedAdCopyMap[selectedAdRecForCopy.endUse].longHeadlines || []).map((lh, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                              <span>{lh}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#64748b' }}>{lh.length}/90 chars</span>
                                <button
                                  onClick={() => copyToClipboard(lh, `Long Headline ${i + 1}`)}
                                  style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                                >
                                  📋 Copy
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Descriptions */}
                      <div style={{ marginBottom: 18 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 13, textTransform: 'uppercase', color: '#475569', letterSpacing: '0.5px' }}>
                          Descriptions (Max 90 chars each)
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(generatedAdCopyMap[selectedAdRecForCopy.endUse].pmax?.descriptions || generatedAdCopyMap[selectedAdRecForCopy.endUse].descriptions || []).map((d, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                              <span>{d}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#64748b' }}>{d.length}/90 chars</span>
                                <button
                                  onClick={() => copyToClipboard(d, `Description ${i + 1}`)}
                                  style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                                >
                                  📋 Copy
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Target Custom Label Filter */}
                      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#166534' }}>
                        <strong>Listing Group Feed Filter:</strong> <code>custom_label_4 = "SEO High Intent"</code> AND <code>custom_label_2 = "{selectedAdRecForCopy.targetCustomLabel2}"</code>
                      </div>
                    </div>
                  )}

                  {/* MODAL VIEW: 5-STEP AI VIDEO CREATOR */}
                  {modalCampaignTab === 'video_creator' && (
                    <div>
                      {(() => {
                        const copyData = generatedAdCopyMap[selectedAdRecForCopy.endUse] || {};
                        const vc = copyData.videoCreator || copyData.pmax?.videoCreator || {};
                        const shopifyUrl = copyData.shopifyCollection?.url || selectedAdRecForCopy.finalUrl || selectedAdRecForCopy.shopifyCollection?.url || 'https://fabvoguestudio.com/collections/all-fabrics';
                        const colTitle = copyData.shopifyCollection?.title || selectedAdRecForCopy.shopifyCollection?.title || selectedAdRecForCopy.endUse;

                        const step1 = vc.step1 || {
                          creativeSourceUrl: shopifyUrl,
                          aspectRatios: ['Horizontal (16:9)', 'Vertical (9:16) for Shorts', 'Square (1:1)'],
                          descriptionPrompt: `Create an aesthetic showcase of FAB VOGUE Studio's premium unstitched ${selectedAdRecForCopy.endUse.toLowerCase()} fabrics. Highlight the soft drape, rich prints, and custom tailored outfit styles with fast COD delivery.`,
                        };
                        const step2 = vc.step2 || {
                          heroBenefit: `5-Meter Matching Sets & Running Fabric by the Meter with Direct Surat Mill Pricing.`,
                          targetAudience: `Fashion-conscious women, boutique owners & tailoring enthusiasts in India.`,
                          visualDirection: `Studio lighting highlighting fabric movement, pure textures, macro print details, and modern draped silhouettes.`,
                        };
                        const step3 = vc.step3 || {
                          recommendedNarrative: 'From Loom to Life (Fabric Transformation)',
                          narrativeReason: 'Starts with raw unstitched fabric to prove authentic quality, then reveals the tailored outfit to drive orders.',
                        };
                        const step4 = vc.step4 || {
                          storyboardScenes: [
                            { name: 'Introduction', imageHint: 'Main product photo (fabric draped or folded)', description: `Slow, smooth camera push-in toward the fabric with a soft light shift across its surface. Keep the fabric print, colours, pattern and every object exactly as in the uploaded image. Do not redraw, recolour, replace or add anything.` },
                            { name: 'Rising Action', imageHint: 'Close-up / texture photo of the same fabric', description: `Gentle breeze makes the fabric ripple softly in place while the camera stays steady. Keep the fabric print, colours, pattern and every object exactly as in the uploaded image. Do not redraw, recolour, replace or add anything.` },
                            { name: 'Climax', imageHint: 'Lookbook photo of a model wearing an outfit made from this fabric', description: `Subtle natural movement: the fabric of the outfit sways gently as the camera slowly pans. Keep the fabric print, colours, pattern and every object exactly as in the uploaded image. Do not redraw, recolour, replace or add anything.` },
                            { name: 'Falling Action', imageHint: 'Flat-lay or folded fabric photo', description: `Slow top-down camera drift across the fabric to reveal the print detail. Keep the fabric print, colours, pattern and every object exactly as in the uploaded image. Do not redraw, recolour, replace or add anything.` },
                            { name: 'Resolution', imageHint: 'FAB VOGUE Studio logo / end-card image', description: `Static frame with a very slow zoom-out. Keep the fabric print, colours, pattern and every object exactly as in the uploaded image. Do not redraw, recolour, replace or add anything.` },
                          ],
                          voiceoverScript: `Design your dream outfits with FAB VOGUE Studio. Shop premium unstitched fabrics by the meter online. Fast delivery and Cash on Delivery across India!`,
                        };
                        const step5 = vc.step5 || {
                          recommendedVoice: 'English (India) - Natural Warm Female or Male',
                          reviewChecklist: [
                            'Confirm aspect ratio is 16:9 or 9:16',
                            'Verify logo is FAB VOGUE Studio',
                            'Verify voiceover matches ~25 words',
                            'Click "Continue to review video" and then Save Video',
                          ],
                        };

                        return (
                          <div>
                            {/* Blueprint Intro Banner */}
                            <div style={{
                              background: 'linear-gradient(135deg, #3b0764 0%, #6b21a8 100%)',
                              color: '#ffffff',
                              borderRadius: 10,
                              padding: '16px 20px',
                              marginBottom: 20,
                            }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255, 255, 255, 0.2)', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>
                                🎬 GOOGLE ADS 5-STEP VIDEO CREATOR BLUEPRINT
                              </div>
                              <h4 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: '#ffffff' }}>
                                Multi-Scene Video Guide for {selectedAdRecForCopy.endUse}
                              </h4>
                              <p style={{ margin: 0, fontSize: 12, color: '#e9d5ff', lineHeight: 1.4 }}>
                                Follow these 5 steps in the Google Ads Video Tool. Copy the tailored prompts and voiceovers to create high-converting 16:9 &amp; 9:16 video ads.
                              </p>
                            </div>

                            {/* STEP 1 */}
                            <div style={{ marginBottom: 18, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: '16px 18px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ background: '#7c3aed', color: '#ffffff', width: 22, height: 22, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>1</span>
                                  <strong style={{ fontSize: 14, color: '#0f172a' }}>Step 1: Select Sources &amp; Describe Video</strong>
                                </div>
                                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Google Ads Step 1</span>
                              </div>

                              <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
                                <strong>Creative Sources:</strong> Select <em>"Enter a website URL"</em> ➔ <code>{shopifyUrl}</code>
                                <button
                                  onClick={() => copyToClipboard(shopifyUrl, 'Shopify Collection URL')}
                                  style={{ marginLeft: 8, background: '#e0e7ff', color: '#3730a3', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}
                                >
                                  📋 Copy URL
                                </button>
                              </div>

                              <div style={{ fontSize: 12, color: '#334155', marginBottom: 10 }}>
                                <strong>Aspect Ratio:</strong> Create <strong>1 Horizontal (16:9)</strong> + <strong>1 Vertical (9:16)</strong> for Shorts.
                              </div>

                              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                                    Describe Your Video (Paste into Prompt Box)
                                  </span>
                                  <button
                                    onClick={() => copyToClipboard(step1.descriptionPrompt, 'Video Prompt')}
                                    style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    📋 Copy Video Prompt
                                  </button>
                                </div>
                                <p style={{ margin: 0, fontSize: 13, color: '#0f172a', fontStyle: 'italic', lineHeight: 1.4 }}>
                                  "{step1.descriptionPrompt}"
                                </p>
                              </div>
                            </div>

                            {/* STEP 2 */}
                            <div style={{ marginBottom: 18, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: '16px 18px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ background: '#7c3aed', color: '#ffffff', width: 22, height: 22, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>2</span>
                                  <strong style={{ fontSize: 14, color: '#0f172a' }}>Step 2: Creative Brief &amp; Brand Guidelines</strong>
                                </div>
                                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Google Ads Step 2</span>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, fontSize: 12 }}>
                                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                    <strong style={{ color: '#475569' }}>Hero Benefit:</strong>
                                    <button onClick={() => copyToClipboard(step2.heroBenefit, 'Hero Benefit')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 3, padding: '1px 5px', fontSize: 10, cursor: 'pointer' }}>📋</button>
                                  </div>
                                  <span style={{ color: '#0f172a' }}>{step2.heroBenefit}</span>
                                </div>

                                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                    <strong style={{ color: '#475569' }}>Target Audience:</strong>
                                    <button onClick={() => copyToClipboard(step2.targetAudience, 'Target Audience')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 3, padding: '1px 5px', fontSize: 10, cursor: 'pointer' }}>📋</button>
                                  </div>
                                  <span style={{ color: '#0f172a' }}>{step2.targetAudience}</span>
                                </div>
                              </div>

                              <div style={{ marginTop: 10, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                  <strong style={{ color: '#475569' }}>Visual Direction:</strong>
                                  <button onClick={() => copyToClipboard(step2.visualDirection, 'Visual Direction')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 3, padding: '1px 5px', fontSize: 10, cursor: 'pointer' }}>📋</button>
                                </div>
                                <span style={{ color: '#0f172a' }}>{step2.visualDirection}</span>
                              </div>
                            </div>

                            {/* STEP 3 */}
                            <div style={{ marginBottom: 18, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: '16px 18px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ background: '#7c3aed', color: '#ffffff', width: 22, height: 22, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>3</span>
                                  <strong style={{ fontSize: 14, color: '#0f172a' }}>Step 3: Narrative Selection</strong>
                                </div>
                                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Google Ads Step 3</span>
                              </div>

                              <div style={{ background: '#ffffff', border: '1.5px solid #7c3aed', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                                <strong style={{ color: '#6d28d9', fontSize: 14 }}>✓ Select: "{step3.recommendedNarrative}"</strong>
                                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#475569' }}>
                                  <strong>Why:</strong> {step3.narrativeReason}
                                </p>
                              </div>
                            </div>

                            {/* STEP 4 */}
                            <div style={{ marginBottom: 18, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: '16px 18px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ background: '#7c3aed', color: '#ffffff', width: 22, height: 22, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>4</span>
                                  <strong style={{ fontSize: 14, color: '#0f172a' }}>Step 4: Storyboard Scenes &amp; Voiceover Script</strong>
                                </div>
                                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Google Ads Step 4</span>
                              </div>

                              {/* Storyboard Scenes: per-scene image source, Generate-images prompt, Scene-video-clip prompt, real photos */}
                              {(() => {
                                const scenes = step4.storyboardScenes || [];
                                const photoState = scenePhotosMap[selectedAdRecForCopy.endUse];
                                const photoProducts = photoState?.products || [];
                                const isLoadingPhotos = loadingScenePhotosEndUse === selectedAdRecForCopy.endUse;
                                const productThumbs = photoProducts.map((p) => ({ url: p.productPhotos[0], title: p.title, href: p.url })).filter((t) => t.url);
                                const lookbookThumbs = photoProducts.flatMap((p) => p.lookbookPhotos.map((url) => ({ url, title: p.title, href: p.url })));
                                const thumbUrl = (url) => `${url}${url.includes('?') ? '&' : '?'}width=180`;
                                const SOURCE_STYLE = {
                                  REAL_PRODUCT_PHOTO: { label: '📸 Real product photo', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
                                  GENERATED_FROM_PRODUCT: { label: '✨ Generate from product photo', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
                                  REAL_LOOKBOOK: { label: '👗 Real lookbook photo', bg: '#fdf4ff', color: '#a21caf', border: '#f5d0fe' },
                                  LOGO: { label: '🏷️ Logo / end card', bg: '#f8fafc', color: '#334155', border: '#cbd5e1' },
                                };
                                const promptBox = (title, tip, text, copyLabel) => (
                                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                      <strong style={{ fontSize: 11, color: '#0f172a' }}>{title}</strong>
                                      <button
                                        onClick={() => copyToClipboard(text, copyLabel)}
                                        style={{ flexShrink: 0, background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}
                                      >
                                        📋 Copy
                                      </button>
                                    </div>
                                    {tip && <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>{tip}</div>}
                                    <div style={{ fontSize: 11, color: '#334155', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word', userSelect: 'text' }}>{text}</div>
                                  </div>
                                );
                                const thumbStrip = (thumbs, emptyText) => (
                                  isLoadingPhotos ? (
                                    <div style={{ fontSize: 10, color: '#64748b' }}>Loading your product photos…</div>
                                  ) : thumbs.length === 0 ? (
                                    <div style={{ fontSize: 10, color: '#b45309' }}>{emptyText}</div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                      {thumbs.slice(0, 8).map((t, ti) => (
                                        <a key={`${t.url}-${ti}`} href={t.url} target="_blank" rel="noopener noreferrer" title={`${t.title} (opens full size to download)`}>
                                          <img src={thumbUrl(t.url)} alt={t.title} loading="lazy" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0', display: 'block' }} />
                                        </a>
                                      ))}
                                    </div>
                                  )
                                );
                                // Paste-ready text only (no how-to guidance): image description + scene (motion) description.
                                const copyAllText = scenes.map((sc, i) => [
                                  `Scene ${i + 1}: ${sc.name}`,
                                  (sc.imagePrompt || sc.imageHint) ? `Image description: ${sc.imagePrompt || sc.imageHint}` : null,
                                  `Scene description: ${sc.videoPrompt || sc.description || ''}`,
                                ].filter(Boolean).join('\n')).join('\n\n');

                                return (
                                  <>
                                    {scenes.length > 0 && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <span style={{ fontSize: 11, color: '#64748b' }}>
                                          Click a photo to open it full size and download it. In Google's video clip box, keep Google's own image description and paste our prompt after it.
                                        </span>
                                        <button
                                          onClick={() => copyToClipboard(copyAllText, 'All Storyboard Scenes')}
                                          style={{ flexShrink: 0, background: '#ffffff', color: '#7c3aed', border: '1px solid #7c3aed', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                        >
                                          📋 Copy All Scenes
                                        </button>
                                      </div>
                                    )}
                                    {photoState?.error && (
                                      <div style={{ fontSize: 11, color: '#b91c1c', marginBottom: 8 }}>Could not load product photos: {photoState.error}</div>
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                                      {scenes.map((sc, i) => {
                                        const src = SOURCE_STYLE[sc.imageSource];
                                        const videoPrompt = sc.videoPrompt || sc.description || '';
                                        return (
                                          <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                              <strong style={{ color: '#7c3aed', fontSize: 12 }}>Scene {i + 1}: {sc.name}</strong>
                                              {src && (
                                                <span style={{ alignSelf: 'flex-start', background: src.bg, color: src.color, border: `1px solid ${src.border}`, borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>
                                                  {src.label}
                                                </span>
                                              )}
                                              {sc.imageHint && <span style={{ fontSize: 11, color: '#0f766e', fontWeight: 600 }}>📷 Scene image: {sc.imageHint}</span>}
                                              {sc.imageSourceGuide && <span style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>{sc.imageSourceGuide}</span>}
                                              {sc.imageSource === 'REAL_LOOKBOOK' && thumbStrip(lookbookThumbs, 'No lookbook photos found for these products. Use the fallback prompt with a product photo attached.')}
                                              {(sc.imageSource === 'REAL_PRODUCT_PHOTO' || sc.imageSource === 'GENERATED_FROM_PRODUCT') && thumbStrip(productThumbs, 'No product photos found for this asset group yet.')}
                                              {sc.imageSource === 'REAL_LOOKBOOK' && lookbookThumbs.length === 0 && !isLoadingPhotos && productThumbs.length > 0 && (
                                                <>
                                                  <span style={{ fontSize: 10, color: '#64748b' }}>Product photos to attach for the fallback prompt:</span>
                                                  {thumbStrip(productThumbs, '')}
                                                </>
                                              )}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                              {sc.imagePrompt && promptBox(
                                                '① "Generate images" prompt',
                                                'Click "Add images", attach one product photo from the left, then paste this in "Describe the kind of image". Use the result only if the print matches exactly.',
                                                sc.imagePrompt,
                                                `Scene ${i + 1} image prompt`
                                              )}
                                              {sc.fallbackImagePrompt && promptBox(
                                                '① Fallback "Generate images" prompt (only if no lookbook photo)',
                                                'Attach a product photo first. A mannequin keeps your real fabric; avoid AI-generated people.',
                                                sc.fallbackImagePrompt,
                                                `Scene ${i + 1} fallback image prompt`
                                              )}
                                              {promptBox(
                                                `${sc.imagePrompt || sc.fallbackImagePrompt ? '②' : '①'} "Scene video clip" prompt`,
                                                'Paste after Google\'s own image description in "Tell us how to animate your scene image".',
                                                videoPrompt,
                                                `Scene ${i + 1} video prompt`
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </>
                                );
                              })()}

                              {/* Voiceover Script Box */}
                              <div style={{ background: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: 8, padding: '12px 14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 16 }}>🎙️</span>
                                    <strong style={{ fontSize: 12, color: '#6b21a8', textTransform: 'uppercase' }}>
                                      Spoken Voiceover Script ({step4.voiceoverScript.trim().split(/\s+/).length} Words)
                                    </strong>
                                  </div>
                                  <button
                                    onClick={() => copyToClipboard(step4.voiceoverScript, 'Voiceover Script')}
                                    style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    📋 Copy Voiceover Script
                                  </button>
                                </div>
                                <p style={{ margin: 0, fontSize: 13, color: '#3b0764', fontWeight: 600, fontStyle: 'italic', lineHeight: 1.4 }}>
                                  "{step4.voiceoverScript}"
                                </p>
                              </div>
                            </div>

                            {/* STEP 5 */}
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 18px', fontSize: 12, color: '#166534' }}>
                              <strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                                5️⃣ Step 5: Review &amp; Audio Voice Selection
                              </strong>
                              <div>• Recommended Voice: <strong>English (India) Natural Warm</strong> or Hindi.</div>
                              <div>• Click <strong>"Continue to review video"</strong> ➔ Preview ➔ Click <strong>"Save Video"</strong> to link to your Asset Group.</div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* MODAL VIEW 2: SEARCH (RSA & KEYWORDS) */}
                  {modalCampaignTab === 'search' && (
                    <div>
                      {/* Search Ad Status & Live Switcher */}
                      <div style={{
                        background: selectedAdRecForCopy.searchUpdateNeeded
                          ? '#fffbeb'
                          : (selectedAdRecForCopy.isSearchLive ? '#f0fdf4' : '#fffbeb'),
                        border: selectedAdRecForCopy.searchUpdateNeeded
                          ? '1.5px solid #f59e0b'
                          : (selectedAdRecForCopy.isSearchLive ? '1px solid #86efac' : '1px solid #fde68a'),
                        borderRadius: 8,
                        padding: '12px 16px',
                        marginBottom: 18,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 10,
                      }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: 13, color: selectedAdRecForCopy.searchUpdateNeeded ? '#92400e' : (selectedAdRecForCopy.isSearchLive ? '#166534' : '#92400e') }}>
                              Google Search Ad Campaign Status:
                            </strong>
                            {selectedAdRecForCopy.searchUpdateNeeded ? (
                              <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 9999 }}>
                                ⚠️ Update Pending in Google Ads ({selectedAdRecForCopy.pendingKeywordsCount} new)
                              </span>
                            ) : selectedAdRecForCopy.isSearchLive ? (
                              <span style={{ background: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 9999 }}>
                                🟢 LIVE in Google Ads (Campaign #24320551082)
                              </span>
                            ) : (
                              <span style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 9999 }}>
                                🟡 Draft / Needs Bulk Upload
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: selectedAdRecForCopy.searchUpdateNeeded ? '#b45309' : (selectedAdRecForCopy.isSearchLive ? '#15803d' : '#b45309'), marginTop: 4 }}>
                            {selectedAdRecForCopy.searchUpdateNeeded
                              ? `${selectedAdRecForCopy.pendingKeywordsCount} new keyword(s) added since last export. Sync update to Google Ads.`
                              : selectedAdRecForCopy.isSearchLive
                              ? `Marked live by ${selectedAdRecForCopy.searchLiveMarkedBy || 'admin'}${selectedAdRecForCopy.searchLiveMarkedAt ? ` on ${new Date(selectedAdRecForCopy.searchLiveMarkedAt).toLocaleDateString()}` : ''}`
                              : (selectedAdRecForCopy.searchExportedAt ? `Exported to CSV on ${new Date(selectedAdRecForCopy.searchExportedAt).toLocaleDateString()}. Mark live once uploaded.` : 'Not exported yet. Export the bulk upload CSV and mark live once uploaded.')}
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleToggleSearchAdLive(selectedAdRecForCopy.endUse, !selectedAdRecForCopy.isSearchLive)}
                            disabled={updatingSearchLiveEndUse === selectedAdRecForCopy.endUse}
                            style={{
                              background: selectedAdRecForCopy.isSearchLive ? '#f1f5f9' : '#16a34a',
                              color: selectedAdRecForCopy.isSearchLive ? '#475569' : '#ffffff',
                              border: selectedAdRecForCopy.isSearchLive ? '1px solid #cbd5e1' : 'none',
                              padding: '7px 14px',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: updatingSearchLiveEndUse === selectedAdRecForCopy.endUse ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {updatingSearchLiveEndUse === selectedAdRecForCopy.endUse
                              ? '⏳ Updating...'
                              : selectedAdRecForCopy.isSearchLive
                              ? 'Mark as Draft / Not Live'
                              : '✓ Mark Live in Google Ads'}
                          </button>
                          <button
                            onClick={() => handleDownloadAdCsv(selectedAdRecForCopy.endUse)}
                            disabled={downloadingAdCsv}
                            style={{
                              background: '#ffffff',
                              color: '#2563eb',
                              border: '1px solid #93c5fd',
                              padding: '7px 12px',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: downloadingAdCsv ? 'not-allowed' : 'pointer',
                            }}
                          >
                            📥 Export CSV
                          </button>
                        </div>
                      </div>

                      {/* Delta Update Warning Box inside Modal */}
                      {selectedAdRecForCopy.searchUpdateNeeded && (
                        <div style={{
                          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                          border: '1.5px solid #f59e0b',
                          borderRadius: 8,
                          padding: '12px 16px',
                          marginBottom: 18,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 12,
                        }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: 16 }}>⚠️</span>
                              <strong style={{ fontSize: 13, color: '#92400e' }}>
                                Google Ads Needs Update: {selectedAdRecForCopy.pendingKeywordsCount} New Keyword{selectedAdRecForCopy.pendingKeywordsCount === 1 ? '' : 's'} Not Yet Synced
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#b45309' }}>
                              New keywords waiting to be added in Google Ads: {(selectedAdRecForCopy.pendingKeywords || []).slice(0, 4).map(k => `"${k}"`).join(', ')}{selectedAdRecForCopy.pendingKeywords?.length > 4 ? ` +${selectedAdRecForCopy.pendingKeywords.length - 4} more` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              onClick={() => copyToClipboard((selectedAdRecForCopy.pendingKeywords || []).map(k => `"${k}"`).join('\n'), `${selectedAdRecForCopy.pendingKeywordsCount} New Keywords`)}
                              style={{
                                background: '#d97706',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                              }}
                            >
                              📋 Copy Only New Keywords ({selectedAdRecForCopy.pendingKeywordsCount})
                            </button>
                            <button
                              onClick={() => handleDownloadAdCsv(selectedAdRecForCopy.endUse, true)}
                              disabled={downloadingAdCsv}
                              style={{
                                background: '#ffffff',
                                color: '#b45309',
                                border: '1px solid #f59e0b',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: downloadingAdCsv ? 'not-allowed' : 'pointer',
                              }}
                            >
                              📥 Export Update CSV
                            </button>
                            <button
                              onClick={() => handleMarkSearchAdUpdated(selectedAdRecForCopy.endUse)}
                              disabled={updatingSearchLiveEndUse === selectedAdRecForCopy.endUse}
                              style={{
                                background: '#16a34a',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: updatingSearchLiveEndUse === selectedAdRecForCopy.endUse ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {updatingSearchLiveEndUse === selectedAdRecForCopy.endUse ? '⏳ Syncing...' : '✓ Mark Updated in Google Ads'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* 15 Short Headlines */}
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <h4 style={{ margin: 0, fontSize: 13, textTransform: 'uppercase', color: '#475569', letterSpacing: '0.5px' }}>
                            15 Responsive Search Ad (RSA) Headlines (Max 30 chars each)
                          </h4>
                          <button
                            onClick={() => {
                              const hdls = (generatedAdCopyMap[selectedAdRecForCopy.endUse]?.search?.headlines || generatedAdCopyMap[selectedAdRecForCopy.endUse]?.headlines || []).join('\n');
                              copyToClipboard(hdls, 'All 15 Search Headlines');
                            }}
                            style={{ background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            📋 Copy All 15 Headlines
                          </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 6 }}>
                          {(generatedAdCopyMap[selectedAdRecForCopy.endUse]?.search?.headlines || generatedAdCopyMap[selectedAdRecForCopy.endUse]?.headlines || []).map((h, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                              <span><strong>{i + 1}.</strong> {h}</span>
                              <button
                                onClick={() => copyToClipboard(h, `Headline ${i + 1}`)}
                                style={{ background: '#e2e8f0', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}
                              >
                                📋
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 4 Descriptions */}
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <h4 style={{ margin: 0, fontSize: 13, textTransform: 'uppercase', color: '#475569', letterSpacing: '0.5px' }}>
                            4 Search Descriptions (Max 90 chars each)
                          </h4>
                          <button
                            onClick={() => {
                              const descs = (generatedAdCopyMap[selectedAdRecForCopy.endUse]?.search?.descriptions || generatedAdCopyMap[selectedAdRecForCopy.endUse]?.descriptions || []).join('\n');
                              copyToClipboard(descs, 'All 4 Descriptions');
                            }}
                            style={{ background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            📋 Copy All 4 Descriptions
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(generatedAdCopyMap[selectedAdRecForCopy.endUse]?.search?.descriptions || generatedAdCopyMap[selectedAdRecForCopy.endUse]?.descriptions || []).map((d, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                              <span><strong>{i + 1}.</strong> {d}</span>
                              <button
                                onClick={() => copyToClipboard(d, `Description ${i + 1}`)}
                                style={{ background: '#e2e8f0', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}
                              >
                                📋 Copy
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Phrase & Exact Match Keywords with Removal */}
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                          <h4 style={{ margin: 0, fontSize: 13, textTransform: 'uppercase', color: '#475569', letterSpacing: '0.5px' }}>
                            Active Ad Group Keywords ({(selectedAdRecForCopy.activeSearchKeywords || []).length})
                          </h4>
                          <button
                            onClick={() => {
                              const kws = selectedAdRecForCopy.activeSearchKeywords || [];
                              copyToClipboard(kws.map(k => `"${k}"`).join('\n'), 'Active Keywords');
                            }}
                            style={{ background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            📋 Copy All ({(selectedAdRecForCopy.activeSearchKeywords || []).length}) Active
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                          {(selectedAdRecForCopy.activeSearchKeywords || []).map((kw, i) => {
                            const isCustom = (selectedAdRecForCopy.customKeywords || []).includes(kw.toLowerCase());
                            const isPending = (selectedAdRecForCopy.pendingKeywords || []).some(pk => pk.toLowerCase() === kw.toLowerCase());
                            return (
                              <span
                                key={i}
                                style={{
                                  background: isPending ? '#fef3c7' : (isCustom ? '#ecfdf5' : '#ffffff'),
                                  border: isPending ? '1.5px solid #f59e0b' : (isCustom ? '1px solid #a7f3d0' : '1px solid #cbd5e1'),
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  color: isPending ? '#92400e' : (isCustom ? '#065f46' : '#1e293b'),
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <span
                                  onClick={() => copyToClipboard(`"${kw}"`, `Phrase "${kw}"`)}
                                  style={{ cursor: 'pointer' }}
                                  title="Click to copy"
                                >
                                  "{kw}" 📋
                                </span>
                                {isPending && (
                                  <span style={{ background: '#f59e0b', color: '#ffffff', fontSize: 9, fontWeight: 800, padding: '1px 4px', borderRadius: 3, letterSpacing: '0.3px' }}>
                                    NEW
                                  </span>
                                )}
                                {isCustom && (
                                  <button
                                    onClick={() => handleRemoveSearchKeyword(selectedAdRecForCopy.endUse, kw)}
                                    title="Remove keyword"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#991b1b',
                                      cursor: 'pointer',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      padding: '0 2px',
                                    }}
                                  >
                                    &times;
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>

                        {/* Suggested New Search Terms Web Panel inside Modal */}
                        <div style={{
                          background: '#faf5ff',
                          border: '1px solid #e9d5ff',
                          borderRadius: 8,
                          padding: '12px 14px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                            <div>
                              <strong style={{ fontSize: 13, color: '#6b21a8' }}>
                                💡 Suggested New Search Terms ({(selectedAdRecForCopy.suggestedSearchTerms || []).length} Discovered)
                              </strong>
                              <div style={{ fontSize: 11, color: '#7e22ce' }}>
                                Discovered from Google Search Console &amp; shopper queries. 1-click to add to this ad group:
                              </div>
                            </div>
                            {(selectedAdRecForCopy.suggestedSearchTerms || []).length > 0 && (
                              <button
                                onClick={() => copyToClipboard((selectedAdRecForCopy.suggestedSearchTerms || []).map(k => `"${k}"`).join('\n'), 'Suggested Search Terms')}
                                style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                              >
                                📋 Copy All Suggested
                              </button>
                            )}
                          </div>

                          {(selectedAdRecForCopy.suggestedSearchTerms || []).length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                              {(selectedAdRecForCopy.suggestedSearchTerms || []).map((term, idx) => {
                                const isAdding = addingKeywordEndUse === `${selectedAdRecForCopy.endUse}::${term}`;
                                return (
                                  <span
                                    key={idx}
                                    style={{
                                      background: '#ffffff',
                                      border: '1px solid #d8b4fe',
                                      borderRadius: 6,
                                      padding: '3px 8px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      fontSize: 12,
                                      color: '#4c1d95',
                                    }}
                                  >
                                    <span
                                      onClick={() => copyToClipboard(`"${term}"`, `Term "${term}"`)}
                                      style={{ cursor: 'pointer' }}
                                      title="Click to copy"
                                    >
                                      "{term}"
                                    </span>
                                    <button
                                      onClick={() => handleAddSearchKeyword(selectedAdRecForCopy.endUse, term)}
                                      disabled={Boolean(isAdding)}
                                      style={{
                                        background: '#16a34a',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: 4,
                                        padding: '2px 6px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        cursor: isAdding ? 'not-allowed' : 'pointer',
                                      }}
                                    >
                                      {isAdding ? '...' : '+ Add'}
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: '#6b21a8', fontStyle: 'italic', marginBottom: 10 }}>
                              ✓ All high-intent discovery search queries are currently active in this ad group!
                            </div>
                          )}

                          {/* Custom input */}
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="Type custom keyword to add..."
                              value={customSearchKeywordInputs[selectedAdRecForCopy.endUse] || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setCustomSearchKeywordInputs((prev) => ({ ...prev, [selectedAdRecForCopy.endUse]: val }));
                              }}
                              onKeyDown={(e) => {
                                const val = (customSearchKeywordInputs[selectedAdRecForCopy.endUse] || '').trim();
                                if (e.key === 'Enter' && val) {
                                  handleAddSearchKeyword(selectedAdRecForCopy.endUse, val);
                                }
                              }}
                              style={{
                                flex: 1,
                                maxWidth: 380,
                                padding: '5px 10px',
                                border: '1px solid #cbd5e1',
                                borderRadius: 4,
                                fontSize: 12,
                              }}
                            />
                            <button
                              onClick={() => {
                                const val = (customSearchKeywordInputs[selectedAdRecForCopy.endUse] || '').trim();
                                if (val) handleAddSearchKeyword(selectedAdRecForCopy.endUse, val);
                              }}
                              disabled={!(customSearchKeywordInputs[selectedAdRecForCopy.endUse] || '').trim()}
                              style={{
                                background: '#7c3aed',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 4,
                                padding: '5px 10px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: !(customSearchKeywordInputs[selectedAdRecForCopy.endUse] || '').trim() ? 'not-allowed' : 'pointer',
                              }}
                            >
                              + Add Keyword
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* MODAL VIEW 3: DEMAND GEN & VISUALS */}
                  {modalCampaignTab === 'demand_gen' && (
                    <div>
                      <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                        <strong style={{ fontSize: 13, color: '#9d174d' }}>Demand Gen Specs &amp; Audience Targeting:</strong>
                        <div style={{ fontSize: 12, color: '#be185d', marginTop: 4 }}>
                          Target custom segments of users searching for {selectedAdRecForCopy.endUse} + In-Market apparel fabrics in India.
                        </div>
                      </div>

                      {/* Visual Aspect Ratios */}
                      <div style={{ marginBottom: 18 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 13, textTransform: 'uppercase', color: '#475569' }}>
                          Recommended Visual Asset Sizes
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                          {['1.91:1 Landscape (1200x628)', '1:1 Square (1200x1200)', '4:5 Portrait (960x1200)', '9:16 Shorts (1080x1920)'].map((size, i) => (
                            <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
                              🖼️ {size}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Punchy Visual Hooks */}
                      <div>
                        <h4 style={{ margin: '0 0 8px', fontSize: 13, textTransform: 'uppercase', color: '#475569' }}>
                          Discovery &amp; Video Hook Headlines
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(generatedAdCopyMap[selectedAdRecForCopy.endUse].demandGen?.longHeadlines || []).map((hook, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                              <span>{hook}</span>
                              <button
                                onClick={() => copyToClipboard(hook, `Hook ${i + 1}`)}
                                style={{ background: '#e2e8f0', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}
                              >
                                📋 Copy
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* MODAL VIEW 4: SHOPPING & FEED FILTERS */}
                  {modalCampaignTab === 'shopping' && (
                    <div>
                      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                        <strong style={{ fontSize: 13, color: '#166534' }}>Listing Group Feed Filter:</strong>
                        <div style={{ fontSize: 13, fontFamily: 'monospace', color: '#047857', marginTop: 4 }}>
                          custom_label_4 = "SEO High Intent" AND custom_label_2 = "{selectedAdRecForCopy.targetCustomLabel2}"
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px' }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 13, textTransform: 'uppercase', color: '#475569' }}>
                          Associated Shopify Collection
                        </h4>
                        <div style={{ fontSize: 13, color: '#334155', marginBottom: 6 }}>
                          <strong>Collection Title:</strong> {selectedAdRecForCopy.shopifyCollection?.title || selectedAdRecForCopy.endUse}
                        </div>
                        <div style={{ fontSize: 13, color: '#334155' }}>
                          <strong>Handle:</strong> <code>{selectedAdRecForCopy.shopifyCollection?.handle || 'all-fabrics'}</code>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <button
                onClick={() => setSelectedAdRecForCopy(null)}
                className="btn-secondary"
                style={{ padding: '8px 16px', fontSize: 13 }}
              >
                Close
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {selectedAdRecForCopy.isLiveInGoogleAds ? (
                  <>
                    {selectedAdRecForCopy.updateNeeded ? (
                      <button
                        onClick={async () => {
                          await handleToggleAssetGroupLive(selectedAdRecForCopy.endUse, true);
                          setSelectedAdRecForCopy((prev) => prev ? ({ ...prev, isLiveInGoogleAds: true, updateNeeded: false, newProductsSinceSync: 0, syncedProductCount: prev.productCount }) : null);
                        }}
                        style={{
                          background: '#d97706',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '8px 18px',
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        🔄 Confirm &amp; Save Update in Google Ads ({selectedAdRecForCopy.productCount} SKUs)
                      </button>
                    ) : (
                      <span style={{ color: '#059669', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                        ✓ Live in Google Ads ({selectedAdRecForCopy.syncedProductCount || selectedAdRecForCopy.productCount} SKUs Synced)
                      </span>
                    )}
                  </>
                ) : (
                  <button
                    onClick={async () => {
                      await handleToggleAssetGroupLive(selectedAdRecForCopy.endUse, true);
                      setSelectedAdRecForCopy((prev) => prev ? ({ ...prev, isLiveInGoogleAds: true, updateNeeded: false, newProductsSinceSync: 0, syncedProductCount: prev.productCount }) : null);
                    }}
                    style={{
                      background: '#059669',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 18px',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    ✓ Mark as Created in Google Ads ({selectedAdRecForCopy.productCount} SKUs)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMART COLLECTION CREATION MODAL */}
      {showCreateSmartModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            maxWidth: 860,
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>✨</span> Create Search-Intent Smart Collection
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>
                  Auto-matches active products using AI Product Details, verifies duplicate safety, and generates collection SEO.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowCreateSmartModal(false);
                  setSmartPreviewDraft(null);
                  setSmartError(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {smartError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                  <strong>Error:</strong> {smartError}
                </div>
              )}

              {smartSuccessMessage && (
                <div style={{ background: '#ecfdf5', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                  🎉 {smartSuccessMessage}
                </div>
              )}

              {/* Step 1: Input Form */}
              <form onSubmit={handlePreviewSmartCollection} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                      Target Search Query / Intent Keyword *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Floral Organza Fabric, Kora Silk for Kurtis, Pink Satin"
                      value={smartQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSmartQuery(val);
                        const parsed = extractAttributesFromQuery(val);
                        if (parsed.title) setSmartTitle(parsed.title);
                        if (parsed.fabric) setSmartFabric(parsed.fabric);
                        if (parsed.pattern) setSmartPattern(parsed.pattern);
                        if (parsed.color) setSmartColor(parsed.color);
                        if (parsed.garment) setSmartGarment(parsed.garment);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                        fontSize: 14,
                        boxSizing: 'border-box',
                      }}
                      required
                    />
                  </div>
                  <div style={{ width: '35%' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                      Collection Title (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Floral Organza Fabrics"
                      value={smartTitle}
                      onChange={(e) => setSmartTitle(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                        fontSize: 14,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                {/* Pre-filled / Editable Attribute Filters */}
                <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>🎯</span> Verify & Edit Criteria (AI extracts automatically — adjust anytime):
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 }}>🧵 Fabric Base</label>
                      <input
                        type="text"
                        placeholder="e.g. Organza"
                        value={smartFabric}
                        onChange={(e) => setSmartFabric(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#ffffff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 }}>🎨 Pattern / Motif</label>
                      <input
                        type="text"
                        placeholder="e.g. Floral"
                        value={smartPattern}
                        onChange={(e) => setSmartPattern(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#ffffff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 }}>🌈 Color Tone</label>
                      <input
                        type="text"
                        placeholder="e.g. Pink"
                        value={smartColor}
                        onChange={(e) => setSmartColor(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#ffffff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 }}>👗 Garment / Use</label>
                      <input
                        type="text"
                        placeholder="e.g. Lehenga"
                        value={smartGarment}
                        onChange={(e) => setSmartGarment(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#ffffff', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loadingSmartPreview || (!smartQuery && !smartTitle)}
                  className="btn-primary-seo"
                  style={{ width: '100%', padding: '10px 16px', fontSize: 14, fontWeight: 600 }}
                >
                  {loadingSmartPreview ? '🤖 Searching Catalog & Matching Products...' : '🔍 Match Products & Check Catalog'}
                </button>
              </form>

              {!smartPreviewDraft && !loadingSmartPreview && (
                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, padding: 24, textAlign: 'center', color: '#64748b' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🎯</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>
                    Verify or edit the 4 attributes above, then click <strong>"🔍 Match Products & Check Catalog"</strong>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    The engine will search active inventory using AI Product Details, verify against Shopify duplicate handles, and prepare your SEO draft.
                  </div>
                </div>
              )}

              {/* Step 2: Live AI Preview & Safety Report */}
              {smartPreviewDraft && (
                <div style={{ marginTop: 20 }}>
                  {/* Safety Checks Banner */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    {/* Check 1: Anti-Duplicate Check */}
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: 8,
                      border: smartPreviewDraft.duplicateCheck?.isDuplicate ? '1px solid #f87171' : '1px solid #bbf7d0',
                      background: smartPreviewDraft.duplicateCheck?.isDuplicate ? '#fef2f2' : '#f0fdf4',
                    }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: smartPreviewDraft.duplicateCheck?.isDuplicate ? '#b91c1c' : '#15803d', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {smartPreviewDraft.duplicateCheck?.isDuplicate ? '⚠️ Duplicate Conflict Detected' : '✓ Unique Collection (No Duplicate)'}
                      </div>
                      <div style={{ fontSize: 12, color: smartPreviewDraft.duplicateCheck?.isDuplicate ? '#991b1b' : '#166534', marginTop: 4 }}>
                        {smartPreviewDraft.duplicateCheck?.isDuplicate ? (
                          <>
                            A collection named <strong>"{smartPreviewDraft.duplicateCheck.existingCollection?.title}"</strong> already exists on {smartPreviewDraft.duplicateCheck.existingCollection?.source} ({smartPreviewDraft.duplicateCheck.existingCollection?.skuCount} products).
                          </>
                        ) : (
                          <>Slug <code>/{smartPreviewDraft.candidateHandle}</code> is available on Shopify.</>
                        )}
                      </div>
                    </div>

                    {/* Check 2: 15+ SKU Guardrail */}
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: 8,
                      border: smartPreviewDraft.productMatch?.isThinCollection ? '1px solid #fcd34d' : '1px solid #bbf7d0',
                      background: smartPreviewDraft.productMatch?.isThinCollection ? '#fffbeb' : '#f0fdf4',
                    }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: smartPreviewDraft.productMatch?.isThinCollection ? '#b45309' : '#15803d', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {smartPreviewDraft.productMatch?.isThinCollection ? '⚠️ Thin Collection Warning' : '✓ 15+ SKU Guardrail Passed'}
                      </div>
                      <div style={{ fontSize: 12, color: smartPreviewDraft.productMatch?.isThinCollection ? '#92400e' : '#166534', marginTop: 4 }}>
                        Found <strong>{smartPreviewDraft.productMatch?.totalCount} matching active products</strong> in AI catalog (Fabric: {smartPreviewDraft.productMatch?.matchedAttributes?.fabric || 'Any'}, Pattern: {smartPreviewDraft.productMatch?.matchedAttributes?.pattern || 'Any'}).
                      </div>
                    </div>
                  </div>

                  {/* Matching Products Thumbnails Carousel / Grid */}
                  {smartPreviewDraft.productMatch?.sampleProducts?.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
                        Matching Products Preview ({smartPreviewDraft.productMatch.sampleProducts.length} shown of {smartPreviewDraft.productMatch.totalCount})
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: 10,
                        overflowX: 'auto',
                        paddingBottom: 8,
                      }}>
                        {smartPreviewDraft.productMatch.sampleProducts.map((p) => (
                          <div
                            key={p.id}
                            style={{
                              flex: '0 0 130px',
                              border: '1px solid #e2e8f0',
                              borderRadius: 8,
                              padding: 8,
                              background: '#ffffff',
                              fontSize: 11,
                              textAlign: 'center',
                            }}
                          >
                            <div style={{
                              width: '100%',
                              height: 80,
                              background: '#f1f5f9',
                              borderRadius: 6,
                              overflow: 'hidden',
                              marginBottom: 6,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              {p.imageUrl ? (
                                <img src={p.imageUrl} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ color: '#94a3b8' }}>Fabric Photo</span>
                              )}
                            </div>
                            <div style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {p.title}
                            </div>
                            <div style={{ color: '#64748b', fontSize: 10, marginTop: 2 }}>
                              SKU: {p.sku || p.id}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Proposed SEO Proposal Draft */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px', color: '#0f172a' }}>
                      Proposed Collection SEO & Rich Content Draft
                    </h3>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                        Meta Title ({smartPreviewDraft.proposedSeo?.title?.length || 0}/60 characters)
                      </label>
                      <input
                        type="text"
                        value={smartPreviewDraft.proposedSeo?.title || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSmartPreviewDraft((prev) => ({
                            ...prev,
                            proposedSeo: { ...prev.proposedSeo, title: val },
                          }));
                        }}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                        Meta Description ({smartPreviewDraft.proposedSeo?.description?.length || 0}/155 characters)
                      </label>
                      <textarea
                        rows={2}
                        value={smartPreviewDraft.proposedSeo?.description || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSmartPreviewDraft((prev) => ({
                            ...prev,
                            proposedSeo: { ...prev.proposedSeo, description: val },
                          }));
                        }}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>

                    {/* Storefront Collection Description (HTML Intro) */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                          Storefront Collection Description (HTML Header Intro & Styling Guide)
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowRawModalHtml(!showRawModalHtml)}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                            color: '#475569',
                          }}
                        >
                          {showRawModalHtml ? '👁️ View Styled Preview' : '📝 Edit Raw HTML'}
                        </button>
                      </div>

                      {showRawModalHtml ? (
                        <textarea
                          rows={6}
                          value={smartPreviewDraft.proposedSeo?.htmlDescription || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSmartPreviewDraft((prev) => ({
                              ...prev,
                              proposedSeo: { ...prev.proposedSeo, htmlDescription: val },
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: 6,
                            border: '1px solid #cbd5e1',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            boxSizing: 'border-box',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 6,
                            padding: '10px 14px',
                            fontSize: 12,
                            lineHeight: 1.5,
                            color: '#334155',
                            maxHeight: 140,
                            overflowY: 'auto',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: smartPreviewDraft.proposedSeo?.htmlDescription || '<em>No description generated</em>',
                          }}
                        />
                      )}
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                        Automated Collection Rule Tag
                      </label>
                      <input
                        type="text"
                        value={smartPreviewDraft.proposedTag || ''}
                        readOnly
                        style={{ width: '100%', padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f1f5f9', fontSize: 12, color: '#64748b', boxSizing: 'border-box' }}
                      />
                    </div>


                    {smartPreviewDraft.proposedSeo?.faqs?.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                          Generated Collection FAQs ({smartPreviewDraft.proposedSeo.faqs.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {smartPreviewDraft.proposedSeo.faqs.map((faq, idx) => (
                            <div key={idx} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                              <strong style={{ color: '#0f172a' }}>Q: {faq.question}</strong>
                              <p style={{ margin: '3px 0 0', color: '#475569' }}>{faq.answer}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}>
              <button
                onClick={() => {
                  setShowCreateSmartModal(false);
                  setSmartPreviewDraft(null);
                  setSmartError(null);
                }}
                className="btn-reject"
                style={{ padding: '8px 16px', fontSize: 13 }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSmartCollection}
                disabled={
                  creatingSmartCollection ||
                  !smartPreviewDraft ||
                  smartPreviewDraft.duplicateCheck?.isDuplicate ||
                  smartPreviewDraft.productMatch?.totalCount === 0
                }
                className="btn-approve"
                style={{
                  padding: '9px 20px',
                  fontSize: 13,
                  fontWeight: 700,
                  opacity: (!smartPreviewDraft || smartPreviewDraft.duplicateCheck?.isDuplicate || smartPreviewDraft.productMatch?.totalCount === 0) ? 0.6 : 1,
                }}
              >
                {creatingSmartCollection ? '⏳ Creating Collection & Tagging Products...' : '🚀 Create & Publish Collection on Shopify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BLOG CREATION & REVIEW MODAL */}
      {showCreateBlogModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            maxWidth: 960,
            width: '100%',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              background: '#faf5ff',
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, color: '#581c87', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>📝</span> Create & Review E-E-A-T SEO Blog Post
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#7e22ce' }}>
                  Standardized 8-section HTML architecture with Author Card, Instant Definition Box, dual printed/plain coverage, and internal collection links.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowCreateBlogModal(false);
                  setBlogPreviewDraft(null);
                  setBlogError(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {/* Configuration Inputs */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                      Target Search Query / Primary Keyword
                    </label>
                    <input
                      type="text"
                      value={blogQuery}
                      onChange={(e) => setBlogQuery(e.target.value)}
                      placeholder="e.g. printed georgette saree fabric for women"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                      Blog Display Title (H1 equivalent on store)
                    </label>
                    <input
                      type="text"
                      value={blogTitle}
                      onChange={(e) => setBlogTitle(e.target.value)}
                      placeholder="e.g. Ultimate Guide to Printed Georgette Fabrics for Sarees"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                      Blog Category
                    </label>
                    <select
                      value={blogCategory}
                      onChange={(e) => setBlogCategory(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, background: '#ffffff', boxSizing: 'border-box' }}
                    >
                      {blogCategoriesList.length > 0 ? (
                        blogCategoriesList.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.label}</option>
                        ))
                      ) : (
                        <>
                          <option value="design_trends">Design Trends</option>
                          <option value="fabric_care">Fabric Care</option>
                          <option value="wholesale_guide">Wholesale Guide</option>
                          <option value="production_tips">Production Tips</option>
                          <option value="color_dye">Color & Dye</option>
                          <option value="sustainability">Sustainability</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569' }}>
                        Fabric Variants ({blogProductCategories.length} Active)
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[
                        { id: 'digital_printed', label: '🖨️ Digital Print', title: 'Include Digital Printed running fabrics' },
                        { id: 'plain_dyed', label: '🎨 Plain Dyed', title: 'Include Plain Dyed solid fabrics' },
                        { id: 'fabric_set', label: '✨ Fabric Set', title: 'Include 5-Meter Coordinated Fabric Duos (2.5m + 2.5m)' },
                      ].map((opt) => {
                        const isSelected = blogProductCategories.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => toggleBlogProductCategory(opt.id)}
                            title={opt.title}
                            style={{
                              flex: 1,
                              padding: '7px 6px',
                              borderRadius: 6,
                              border: isSelected ? '1.5px solid #4f46e5' : '1px solid #cbd5e1',
                              background: isSelected ? '#eef2ff' : '#ffffff',
                              color: isSelected ? '#4338ca' : '#64748b',
                              fontWeight: isSelected ? 600 : 500,
                              fontSize: 11,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 4,
                              whiteSpace: 'nowrap',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <span>{opt.label}</span>
                            <span style={{ fontSize: 12, color: isSelected ? '#4f46e5' : '#cbd5e1' }}>
                              {isSelected ? '✓' : '+'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                      Author (E-E-A-T Credibility)
                    </label>
                    <select
                      value={blogAuthor?.name || ''}
                      onChange={(e) => {
                        const selected = blogAuthorsList.find((a) => a.name === e.target.value);
                        if (selected) setBlogAuthor(selected);
                      }}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, background: '#ffffff', boxSizing: 'border-box' }}
                    >
                      {blogAuthorsList.map((author, idx) => (
                        <option key={idx} value={author.name}>
                          {author.name} — {author.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                      Secondary Keywords (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={blogKeywords}
                      onChange={(e) => setBlogKeywords(e.target.value)}
                      placeholder="e.g. georgette fabric online, pure georgette saree material, printed fabric for stitching"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </div>
                  <button
                    onClick={handlePreviewBlog}
                    disabled={loadingBlogPreview || !blogQuery}
                    className="btn-primary-seo"
                    style={{
                      background: '#7c3aed',
                      padding: '10px 20px',
                      fontSize: 13,
                      fontWeight: 700,
                      alignSelf: 'flex-end',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    {loadingBlogPreview ? '⚡ Generating AI Blog...' : '✨ Generate Blog Draft'}
                  </button>
                </div>
              </div>

              {/* Status & Error Alerts */}
              {loadingBlogPreview && (
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: 24, textAlign: 'center', color: '#6d28d9', margin: '20px 0' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>⚡</div>
                  <strong style={{ fontSize: 15 }}>Synthesizing 8-Section E-E-A-T HTML Blog Post...</strong>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: '#7c3aed' }}>
                    Balancing digital printed & plain dyed sections, weaving cited statistics, embedding expert quotes, and matching verified store collection URLs.
                  </p>
                </div>
              )}

              {blogError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 14, color: '#b91c1c', fontSize: 13, marginBottom: 16 }}>
                  <strong>⚠️ Generation Issue:</strong> {blogError}
                </div>
              )}

              {blogSuccessMessage && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16, color: '#15803d', fontSize: 14, marginBottom: 16 }}>
                  <strong>🎉 Success!</strong> {blogSuccessMessage}
                </div>
              )}

              {/* Generated Blog Draft Review Card */}
              {blogPreviewDraft && !loadingBlogPreview && (
                <div style={{ border: '1.5px solid #cbd5e1', borderRadius: 10, overflow: 'hidden' }}>
                  {/* Draft Summary Header */}
                  <div style={{ background: '#f8fafc', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700 }}>
                        📊 {blogPreviewDraft.word_count || 0} Words (~{Math.ceil((blogPreviewDraft.word_count || 0) / 220)} min read)
                      </span>
                      <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}>
                        🏷️ Category: {blogPreviewDraft.blog_category_label || blogPreviewDraft.blog_category}
                      </span>
                      <span style={{ background: '#fdf4ff', color: '#a21caf', border: '1px solid #f0abfc', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}>
                        ✍️ Author: {blogPreviewDraft.author?.name || 'Meera Patel'}
                      </span>
                    </div>

                    {/* View Switcher: Preview vs Raw HTML */}
                    <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden' }}>
                      <button
                        onClick={() => setBlogActiveView('preview')}
                        style={{
                          background: blogActiveView === 'preview' ? '#7c3aed' : '#ffffff',
                          color: blogActiveView === 'preview' ? '#ffffff' : '#475569',
                          border: 'none',
                          padding: '6px 14px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        👁️ Styled Preview
                      </button>
                      <button
                        onClick={() => setBlogActiveView('raw')}
                        style={{
                          background: blogActiveView === 'raw' ? '#7c3aed' : '#ffffff',
                          color: blogActiveView === 'raw' ? '#ffffff' : '#475569',
                          border: 'none',
                          padding: '6px 14px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        💻 Raw HTML
                      </button>
                    </div>
                  </div>

                  {/* SEO Meta & Internal Links Strip */}
                  <div style={{ background: '#ffffff', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                          <span>SEO Meta Title</span>
                          <span style={{ color: (blogPreviewDraft.seo_title?.length || 0) <= 60 ? '#16a34a' : '#dc2626' }}>
                            {blogPreviewDraft.seo_title?.length || 0}/60 chars
                          </span>
                        </div>
                        <input
                          type="text"
                          value={blogPreviewDraft.seo_title || ''}
                          onChange={(e) => setBlogPreviewDraft({ ...blogPreviewDraft, seo_title: e.target.value })}
                          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>

                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                          <span>SEO Meta Description</span>
                          <span style={{ color: (blogPreviewDraft.seo_description?.length || 0) >= 110 && (blogPreviewDraft.seo_description?.length || 0) <= 160 ? '#16a34a' : '#ea580c' }}>
                            {blogPreviewDraft.seo_description?.length || 0}/160 chars
                          </span>
                        </div>
                        <textarea
                          rows={2}
                          value={blogPreviewDraft.seo_description || ''}
                          onChange={(e) => setBlogPreviewDraft({ ...blogPreviewDraft, seo_description: e.target.value })}
                          style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, resize: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    {/* Matched Collections Linked */}
                    {blogPreviewDraft.matched_collections?.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>🔗 Approved Collection Links:</span>
                        {blogPreviewDraft.matched_collections.map((col, idx) => (
                          <a
                            key={idx}
                            href={col.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              background: '#f1f5f9',
                              color: '#2563eb',
                              border: '1px solid #e2e8f0',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 500,
                              textDecoration: 'none',
                            }}
                          >
                            📁 {col.title} ↗
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Blog Content Viewer */}
                  <div style={{ padding: 20 }}>
                    {blogActiveView === 'preview' ? (
                      <div
                        className="blog-preview-container"
                        dangerouslySetInnerHTML={{ __html: blogPreviewDraft.main_content }}
                      />
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(blogPreviewDraft.main_content);
                            alert('HTML copied to clipboard!');
                          }}
                          style={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            background: '#334155',
                            color: '#ffffff',
                            border: 'none',
                            padding: '4px 10px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          📋 Copy HTML
                        </button>
                        <textarea
                          rows={24}
                          value={blogPreviewDraft.main_content}
                          onChange={(e) => setBlogPreviewDraft({ ...blogPreviewDraft, main_content: e.target.value })}
                          style={{
                            width: '100%',
                            fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            fontSize: 12,
                            padding: 14,
                            borderRadius: 6,
                            border: '1px solid #cbd5e1',
                            background: '#0f172a',
                            color: '#e2e8f0',
                            lineHeight: 1.5,
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}>
              <button
                onClick={() => {
                  setShowCreateBlogModal(false);
                  setBlogPreviewDraft(null);
                  setBlogError(null);
                }}
                className="btn-reject"
                style={{ padding: '8px 16px', fontSize: 13 }}
              >
                Cancel
              </button>

              <div style={{ display: 'flex', gap: 10 }}>
                {blogPreviewDraft && (
                  <button
                    onClick={handlePreviewBlog}
                    disabled={loadingBlogPreview}
                    className="btn-primary-seo"
                    style={{ background: '#475569', padding: '9px 16px', fontSize: 13 }}
                  >
                    ⟳ Re-generate Draft
                  </button>
                )}
                <button
                  onClick={handlePublishBlog}
                  disabled={publishingBlog || !blogPreviewDraft}
                  className="btn-approve"
                  style={{
                    background: '#7c3aed',
                    padding: '9px 22px',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: (!blogPreviewDraft || publishingBlog) ? 0.6 : 1,
                  }}
                >
                  {publishingBlog ? '⏳ Publishing to Shopify...' : '🚀 1-Click Publish to Shopify Blog'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QUICK EXCLUDE KEYWORD MODAL */}
      {showExcludeModal && selectedQueryForExclude && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 540,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>🚫</span>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  Exclude Keyword / Dismiss Query
                </h3>
              </div>
              <button
                onClick={() => { setShowExcludeModal(false); setSelectedQueryForExclude(null); }}
                style={{ background: 'none', border: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                  TARGET SEARCH QUERY
                </label>
                <div style={{ background: '#f1f5f9', padding: '10px 14px', borderRadius: 6, fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                  "{selectedQueryForExclude.query}"
                </div>
              </div>

              {/* Word Chips Selector */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                  SELECT WORD OR STEM TO EXCLUDE:
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {String(selectedQueryForExclude.query || '')
                    .toLowerCase()
                    .replace(/[^\w\s]/g, ' ')
                    .split(/\s+/)
                    .filter((w) => w.length > 2)
                    .map((word) => {
                      const isSelected = (pickedExclusionStem === word && !customExclusionInput);
                      return (
                        <button
                          key={word}
                          type="button"
                          onClick={() => {
                            setPickedExclusionStem(word);
                            setCustomExclusionInput('');
                          }}
                          style={{
                            padding: '5px 12px',
                            borderRadius: 16,
                            fontSize: 12,
                            fontWeight: 600,
                            border: isSelected ? '2px solid #dc2626' : '1px solid #cbd5e1',
                            background: isSelected ? '#fee2e2' : '#ffffff',
                            color: isSelected ? '#b91c1c' : '#334155',
                            cursor: 'pointer',
                          }}
                        >
                          {word}
                        </button>
                      );
                    })}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Or type custom word (e.g. embroidery, kids)..."
                    value={customExclusionInput}
                    onChange={(e) => setCustomExclusionInput(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>

              {/* Scope Radio Options */}
              <div style={{ marginBottom: 20, background: '#fafafa', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="excludeMode"
                    value="PERMANENT"
                    checked={excludeActionType === 'PERMANENT'}
                    onChange={() => setExcludeActionType('PERMANENT')}
                    style={{ marginTop: 2 }}
                  />
                  <div>
                    <strong style={{ fontSize: 13, color: '#b91c1c' }}>🚫 Add to Permanent Exclusion List</strong>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      Permanently filters out any query containing the whole word <strong>"{customExclusionInput || pickedExclusionStem}"</strong> across all present & future trend scans.
                    </div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="excludeMode"
                    value="DISMISS_ONLY"
                    checked={excludeActionType === 'DISMISS_ONLY'}
                    onChange={() => setExcludeActionType('DISMISS_ONLY')}
                    style={{ marginTop: 2 }}
                  />
                  <div>
                    <strong style={{ fontSize: 13, color: '#475569' }}>🗑️ Dismiss This Query Only</strong>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      Hides only this exact query from the board without blocking other fabrics or similar searches.
                    </div>
                  </div>
                </label>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  className="btn-reject"
                  onClick={() => { setShowExcludeModal(false); setSelectedQueryForExclude(null); }}
                  style={{ padding: '8px 16px', fontSize: 13 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmExclude}
                  disabled={submittingExclusion || (excludeActionType === 'PERMANENT' && !customExclusionInput && !pickedExclusionStem)}
                  style={{
                    padding: '8px 18px',
                    fontSize: 13,
                    fontWeight: 700,
                    borderRadius: 6,
                    border: 'none',
                    cursor: 'pointer',
                    background: excludeActionType === 'PERMANENT' ? '#dc2626' : '#475569',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {submittingExclusion ? 'Applying...' : (excludeActionType === 'PERMANENT' ? `🚫 Exclude "${customExclusionInput || pickedExclusionStem}"` : '🗑️ Dismiss Query')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MANAGE EXCLUSIONS MODAL */}
      {showManageExclusionsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 620,
            maxHeight: '90vh',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>⚙️</span>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  Manage Negative Keywords & Exclusions
                </h3>
              </div>
              <button
                onClick={() => setShowManageExclusionsModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Add New Exclusion */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  ADD NEW EXCLUDED KEYWORD / FABRIC:
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="e.g. modal satin, embroidery, kids, replica..."
                    value={newManualExclusion}
                    onChange={(e) => setNewManualExclusion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddManualExclusion(); }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: 13,
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddManualExclusion}
                    disabled={!newManualExclusion.trim()}
                    style={{
                      padding: '8px 16px',
                      background: '#0f172a',
                      color: '#ffffff',
                      borderRadius: 6,
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    + Add
                  </button>
                </div>
              </div>

              {/* Custom Exclusions List */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase' }}>
                    Custom Blocked Terms ({exclusionsData?.customExclusions?.length || 0})
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Click ✕ to un-exclude</span>
                </div>
                {(!exclusionsData?.customExclusions || exclusionsData.customExclusions.length === 0) ? (
                  <p style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>
                    No custom blocked terms added yet.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {exclusionsData.customExclusions.map((kw) => (
                      <span
                        key={kw}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: '#fee2e2',
                          color: '#991b1b',
                          border: '1px solid #fecaca',
                          padding: '4px 10px',
                          borderRadius: 16,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {kw}
                        <button
                          type="button"
                          onClick={() => handleRemoveExclusion(kw)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#991b1b',
                            cursor: 'pointer',
                            fontSize: 12,
                            padding: 0,
                            lineHeight: 1,
                          }}
                          title={`Unblock "${kw}"`}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Built-in Negative List */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 8 }}>
                  Default System Filters (Apparel & Furniture Negatives)
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(exclusionsData?.defaultExclusions || []).map((def) => (
                    <span
                      key={def}
                      style={{
                        background: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        padding: '3px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                    >
                      {def}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowManageExclusionsModal(false)}
                className="btn-primary-seo"
                style={{ padding: '8px 18px', fontSize: 13 }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REFINE & RE-GENERATE AI MODAL */}
      {showRegenerateModal && selectedCollectionForRegenerate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 620,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>🔄</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                    Refine & Re-Generate AI SEO
                  </h3>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {selectedCollectionForRegenerate.title} ({selectedCollectionForRegenerate.skuCount} active SKUs)
                  </span>
                </div>
              </div>
              <button
                onClick={() => { setShowRegenerateModal(false); setSelectedCollectionForRegenerate(null); }}
                style={{ background: 'none', border: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {/* Previous Feedback Notice if exists */}
              {selectedCollectionForRegenerate.opportunity?.lastRegenerateReason && (
                <div style={{
                  background: '#faf5ff',
                  border: '1px solid #e9d5ff',
                  borderRadius: 6,
                  padding: '8px 12px',
                  marginBottom: 16,
                  fontSize: 12,
                  color: '#7e22ce',
                }}>
                  <strong>Current Directive:</strong> "{selectedCollectionForRegenerate.opportunity.lastRegenerateReason}"
                </div>
              )}

              {/* Quick Feedback Chips */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Quick Directives & Suggestions (Click to toggle):
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {REGENERATE_PRESET_TAGS.map((tag) => {
                    const isSelected = regenerateReasonChips.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleToggleRegenerateChip(tag)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: isSelected ? 600 : 500,
                          border: isSelected ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                          background: isSelected ? '#ede9fe' : '#f8fafc',
                          color: isSelected ? '#6d28d9' : '#334155',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {isSelected ? `✓ ${tag}` : `+ ${tag}`}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Instructions & Merchandising Rationale */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Specific Instructions / Why Re-generate?
                </label>
                <textarea
                  rows={4}
                  placeholder="E.g., Focus specifically on 60g dyeable quality for boutique salwar suits, ensure meta description is under 150 chars, and add wholesale MOQ details in FAQs..."
                  value={customRegenerateInstruction}
                  onChange={(e) => setCustomRegenerateInstruction(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                  }}
                />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  💡 Gemini 2.5 Flash will strictly incorporate these directives when recreating the meta title, description (&le;155 chars), rich HTML intro, and FAQs.
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => { setShowRegenerateModal(false); setSelectedCollectionForRegenerate(null); }}
                  style={{
                    padding: '8px 16px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  disabled={submittingRegenerate}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmRegenerate}
                  disabled={submittingRegenerate}
                  style={{
                    padding: '8px 18px',
                    background: submittingRegenerate ? '#a78bfa' : '#7c3aed',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: submittingRegenerate ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {submittingRegenerate ? '⏳ Re-generating...' : '🚀 Re-Generate AI Proposal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REFINE & RE-GENERATE HOMEPAGE AI MODAL */}
      {showHomepageRegenerateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 640,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f8fafc',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>🔄</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                    Refine & Re-Generate Homepage AI SEO
                  </h3>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    FAB VOGUE Studio (fabvoguestudio.com) • Surat Manufacturing Direct
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowHomepageRegenerateModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {/* Previous Feedback Notice if exists */}
              {homepageData?.lastRegenerateReason && (
                <div style={{
                  background: '#faf5ff',
                  border: '1px solid #e9d5ff',
                  borderRadius: 6,
                  padding: '8px 12px',
                  marginBottom: 16,
                  fontSize: 12,
                  color: '#7e22ce',
                }}>
                  <strong>Current Directive:</strong> "{homepageData.lastRegenerateReason}"
                </div>
              )}

              {/* Quick Feedback Chips */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Homepage Directives & Strategic Focus (Click to toggle):
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {HOMEPAGE_PRESET_TAGS.map((tag) => {
                    const isSelected = homepageReasonChips.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleToggleHomepageChip(tag)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: isSelected ? 600 : 500,
                          border: isSelected ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                          background: isSelected ? '#ede9fe' : '#f8fafc',
                          color: isSelected ? '#6d28d9' : '#334155',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {isSelected ? `✓ ${tag}` : `+ ${tag}`}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Instructions & Brand Rationale */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Specific Instructions / Why Re-generate?
                </label>
                <textarea
                  rows={4}
                  placeholder="E.g., Emphasize our Surat manufacturing mill pricing, pure dyeable fabrics (60g georgette, russian silk), low MOQ swatch trial packs, and international shipping to USA/UAE..."
                  value={customHomepageInstruction}
                  onChange={(e) => setCustomHomepageInstruction(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                  }}
                />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  💡 Gemini 2.5 Flash will strictly incorporate these directives when recreating the Homepage meta title, description (&le;155 chars), 200-word brand story, and FAQs.
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowHomepageRegenerateModal(false)}
                  style={{
                    padding: '8px 16px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  disabled={generatingHomepage}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleGenerateHomepage(true)}
                  disabled={generatingHomepage}
                  style={{
                    padding: '8px 18px',
                    background: generatingHomepage ? '#a78bfa' : '#7c3aed',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: generatingHomepage ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {generatingHomepage ? '⏳ Re-generating...' : '🚀 Re-Generate Homepage AI SEO'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

